/**
 * Tab 5: Reports & Calculations
 * PSP/CRE Tool v2.0
 * 
 * PROPERLY REFACTORED VERSION - Pure UI Component
 * 
 * Architecture:
 *   CONSTANTS -> casualtyCalculator.js -> reportFormatters.js -> exportUtils.js -> Tab5 (UI)
 * 
 * This component:
 * 1. IMPORTS calculation logic from casualtyCalculator.js
 * 2. IMPORTS report formatters from reportFormatters.js
 * 3. IMPORTS export functions from exportUtils.js
 * 4. CONTAINS only UI code (buttons, displays, state management)
 * 
 * Author: Dr. Tatar Radu MD
 * Date: February 16, 2026
 * Version: 4.31.0 (April 8, 2026) - V3.1-MASCAL-THRESH: Civilian patient footnote in renderMASCALTimeline().
 *   When MRoE Stream 2 is active (civilian_stream.competes_with_military && daily_patients > 0),
 *   an info Alert is rendered between the threshold chips row and the R2B section header.
 *   Shows T1/T2/T3 daily demand chips + caption: "MASCAL thresholds are military-only per
 *   AJMedP-1 §3.21 (doctrinally correct). Effective combined occupancy is higher."
 *   No threshold math changed. Data read from ma.mroe_load.civilian_stream (already in scope).
 *
 * Version: 4.32.0 (April 8, 2026) - V3.1-BLOOD-STREAM2: Civilian T1 blood demand annotation.
 *   Blood Products card in renderLogisticsImpact(): added caption below "For X T1/T2 casualties"
 *   when MRoE Stream 2 is active (civilian_stream.t1_surgical_daily > 0).
 *   Shows "+N civilian T1/day (+~X PRBC, Y FFP) — MRoE Stream 2 included" in warning color.
 *   Reads from projections.mascal_analysis.mroe_load.civilian_stream.
 *   Rates: 6 PRBC / 5 FFP per civilian T1 patient (mirrors calculator V3.1-BLOOD-STREAM2).
 *   No-op when Stream 2 absent (backward compat).
 *
 * Version: 4.31.0 (April 8, 2026) - V3.1-MASCAL-THRESH: Civilian patient footnote.
 *   In renderMASCALTimeline(): info Alert between threshold chips and R2B section header.
 *   Shows T1/T2/T3 daily demand chips + AJMedP-1 §3.21 doctrinal note.
 *   Reads ma.mroe_load.civilian_stream (already in scope). No threshold math changed.
 *
 * Version: 4.31.0 (April 9, 2026) - Category 2: ENH-046, ENH-047, ECHELON-DUAL display.
 *   ENH-046: Phase transition surge events table inside renderMASCALTimeline().
 *   ENH-047: Coalition CASEVAC overflow routing panel (renderCoalitionOverflow()).
 *   ECHELON-DUAL: System-state banner + TPRP requirements card when R3 not accessible.
 *   r3_absent_delta card updated to show ACTIVE MODE when is_active_mode=true.
 *
 * Version: 4.30.0 (April 5, 2026) - V4-TAB6-004: renderBuildupPhasePanel().
 *   Reads projections.mascal_analysis.buildup_phase (calculator v5.56.0).
 *   Shows: duration, total DNBI, day0 load, effective strength, strength reduction %.
 *   Warning Alert when strength_reduction_pct > 10%. Graceful null guard.
 *   Positioned before renderMASCALTimeline() in main render sequence.
 *
 * Version: 4.29.0 (April 5, 2026) - BUG-R2E-MARCH-001: March phase caveat note in R2E section.
 *   When any division had march phases (had_march_phases=true in r2e_divisions[]), shows
 *   an info Alert explaining movement backlog. Reads peak_movement_backlog from division.
 *   New fields present in calculator v5.55.0 output; gracefully absent for older saves.
 *
 * Version: 4.28.0 (April 5, 2026) - V4-ECH-002: Per-division R2E-absent delta cards.
 *   After R2E echelon table: maps r2e_divisions, renders one Alert card per division
 *   when r2e_absent_delta is present. 4-metric grid per card: first CRITICAL day,
 *   excess T1 deaths, R3 overflow, critical days. Only renders when any division has delta.
 * Version: 4.27.0 (April 5, 2026) - V4-ECH-001: R3-absent delta card in renderMASCALTimeline.
 *   Destructures r3_absent_delta from mascal_analysis.
 *   Inserts "What-if: R3 lost" Alert below R3 echelon table (only when r3_absent_delta != null).
 *   4-metric grid: first CRITICAL day, excess T1 deaths estimate, unmet patient overflow,
 *   critical days without R3. Disclaimer note on modeling assumptions.
 * Version: 4.26.0 (April 5, 2026) - V4-CAL-001: renderMROEPanel two-stream display.
 *   EPW stream card: chain visualization (daily patients → collection → EPW section beds).
 *   EPW stream: ADDITIVE chip, does-not-compete label, MP escort badge.
 *   Civilian stream card: triage profile breakdown (T1/T2/T3 chips with patient counts).
 *   Civilian stream: COMPETES-at-R2B/R2E chip, T3 HNS transfer count, competition label.
 *   Both cards: border-colored (warning=EPW, info=civilian) for visual separation.
 *   Backward compat: falls back gracefully when epw_stream/civilian_stream absent (V3.x saves).
 * Version: 4.25.1 (March 26, 2026) - ESLint: remove 6 unused MUI imports (FormControl,
 *   InputLabel, Select, MenuItem, ButtonGroup, Checkbox) left over from ENH-030 cleanup.
 * Version: 4.25.0 (March 26, 2026) - ENH-030: Report generation moved to Tab 6.
 *   Tab 5 is now pure Medical Estimate (analysis). Formal report generation (12 NATO
 *   report types) moved to Tab 6 Section 6. Removed: renderReportsSection(), Preview
 *   Dialog, REPORT_TYPES, EXPORT_FORMATS, formatter imports, export utility imports,
 *   report state variables (selectedReports, selectedFormat, previewDialogOpen,
 *   previewContent, previewReportId, isExporting), report handler functions.
 *   Title updated: "Calculate & Reports" -> "Medical Estimate".
 *   Description updated to point to Tab 6 for formal reports.
 *
 * Version: 4.24.3 (March 26, 2026) - BUG-202 diagnostic probes removed.
 *   Root cause confirmed in Tab4_Logistics.jsx v8.20.0. No Tab5 changes required.
 *
 * Version: 4.24.2 (March 26, 2026) - BUG-202 diagnostic: console.log probes inserted
 *   immediately after calculateCasualties() returns. Logs:
 *   supply_chain.daily_consumption.prbc, medical_requirements.blood.prbc,
 *   supply_chain keys, medical_requirements.blood. Remove after root cause confirmed.
 *
 * Version: 4.24.1 (March 25, 2026) - ESLint: remove unused _firstBrigadeStd.
 *   _firstBrigadeStd was declared in BUG-205 fix but not used after the chain
 *   was reordered. Declaration removed, comment updated.
 *
 * Version: 4.24.0 (March 25, 2026) - BUG-205 + BUG-209:
 *   BUG-205: "Standard: ROMANIAN" shown for MULTINATIONAL force. Root cause:
 *     getScenarioMetadata() standard chain fell through to _firstBrigadeStd before
 *     MULTINATIONAL guard. Fix: _forceIsMultinational check moved to position 2.
 *   BUG-209: ENH-002 Chip removed from Evacuation Asset Attachments header.
 *     ARCH-010 tracking tag removed from CASEVAC tooltip text.
 *
 * Version: 4.23.0 (March 25, 2026) - BUG-203 + BUG-204:
 *   BUG-204 ROOT CAUSE: projections initialized as null. useEffect at line ~1401
 *   loaded scenario.casualty_projections asynchronously. MC panel gated on
 *   projections.monte_carlo — null on first render → MC panel never visible on load.
 *   Required RECALCULATE to trigger the effect and populate projections.
 *   BUG-203 ROOT CAUSE: calculationStale = isDirty() on mount. If Tab4 Save & Continue
 *   was called after the last Tab5 calculation (even without real changes), dirty flag
 *   was true in localStorage. useEffect set calculationComplete=true immediately after
 *   loading persisted projections → both conditions met → "Scenario Changed" on every load.
 *   Fix: lazy useState initializers.
 *     projections: initialized from scenario.casualty_projections directly.
 *     calculationComplete: initialized from !!scenario.casualty_projections.
 *     calculationStale: initialized as isDirty() && !!scenario.casualty_projections
 *       (stale only if BOTH dirty AND results already exist — can't be stale with no baseline).
 *
 * Version: 4.22.0 (March 20, 2026) - ENH-031: Monte Carlo auto-run on Tab5 mount.
 *   When user has enabled MC in Tab2 (operation_parameters.monte_carlo_config.enabled)
 *   and Tab5 receives casualty_projections without .monte_carlo (Tab3 Save & Continue
 *   runs {final:false}, no MC), Tab5 now auto-triggers runCasualtyCalculator() which
 *   calls calculateCasualties(scenario, {final:true}) — same path as manual RECALCULATE.
 *   Implementation:
 *   - autoMCFiredRef (useRef): gate prevents StrictMode double-fire and repeated triggers
 *     on Tab3 re-saves. Resets on component unmount/remount (ref is component-local).
 *   - useEffect deps: [scenario?.casualty_projections, monte_carlo_config?.enabled].
 *     Fires once on mount after load guard, and again if MC toggle changes in Tab2.
 *   - Loop guard: after auto-run, projections.monte_carlo is populated → condition false
 *     → effect does nothing on subsequent fires.
 *   - UI: info Alert "Monte Carlo auto-calculating..." visible while isCalculating +
 *     mcEnabled + no current monte_carlo data. Disappears automatically on completion.
 *   - eslint-disable-line react-hooks/exhaustive-deps: runCasualtyCalculator is read via
 *     closure (stable enough — recreates on render but deps cover all meaningful changes).
 *
 * Version: 4.21.0 (March 19, 2026) - Tab6 baseline auto-store on mount.
 *   storeBaseline() was only called in the manual RECALCULATE path. Tab6 therefore
 *   showed "No Tab 5 Baseline Found" unless user manually clicked RECALCULATE.
 *   Architecture: Tab4 Save & Continue runs calculateLogisticsProjection (not full
 *   casualty calc). Tab5 receives scenario.casualty_projections via prop on mount
 *   (produced by Tab3 Save & Continue). Fix: add storeBaseline(scenarioRef.current, incoming)
 *   to all accepting branches of the useEffect load guard. Tab6 now has a baseline
 *   immediately when Tab5 first renders, without requiring RECALCULATE.
 * Version: 4.20.0 - BUG-160 + BUG-161 fixes (March 18, 2026):
 *   BUG-160: useEffect guard (BUG-143) accepted ANY versioned result including
 *     non-MC full results from Tab3 Save & Continue ({final:false} run, has .version,
 *     no .monte_carlo). After a Tab5 MC calculation, a subsequent Tab3 save fired
 *     the useEffect with a non-MC result → guard accepted it → MC wiped from state →
 *     MC panel absent until second RECALCULATE.
 *     Fix: guard now checks incomingHasMC separately. MC-bearing results always
 *     accepted. Non-MC full results only accepted when current state has no MC.
 *     Current MC state is never replaced by a non-MC result from any external path.
 *   BUG-161: storeBaseline() return value now checked. If false (localStorage quota
 *     exceeded), a warning snackbar fires so the user knows Tab 6 is unavailable.
 *     Root cause fix is in calculationService.js v1.1.0.
 * Version: 4.19.0 - BUG-158 + BUG-159 UI fixes (March 18, 2026):
 *   BUG-158: Personnel composition "HQ & admin support" row was reading
 *     support_casualties.hq_support_personnel (small internal staff, ~20-40 people)
 *     instead of hq_casualties.total_hq_personnel (actual command HQ, e.g. 580).
 *     Fix: row now reads projections.hq_casualties?.total_hq_personnel. Row label
 *     updated to "HQ & command staff". "Combat formations" count now subtracts both
 *     hq_casualties.total_hq_personnel AND support_casualties.personnel so the four
 *     rows sum correctly to Total Initial Strength.
 *   BUG-159: R3 planning gap warning added to MASCAL timeline section. When
 *     mascal_analysis.r3_configured === false (no Role 3 MTF in Tab 3), an MUI
 *     Alert warning fires instead of silently showing phantom 200 R3 beds
 *     (the fallback was removed in casualtyCalculator.js v5.43.0).
 * Version: 4.18.0 - ESLint fix (March 18, 2026):
 *   react-hooks/exhaustive-deps warning on load useEffect (line ~1329).
 *   Effect reads projections?.monte_carlo but projections was not in dep array.
 *   Adding projections would cause infinite re-renders (effect sets projections →
 *   projections changes → effect re-fires). Fix: use projectionsRef.current instead —
 *   already kept in sync on every render (line ~1241). ESLint does not require
 *   refs in dependency arrays. BUG-143 guard logic unchanged.
 *
 * Version: 4.17.0 - BUG-143 (March 17, 2026):
 *   BUG-143 FIX: Monte Carlo results no longer wiped by Tab3 Save & Continue.
 *     Root cause: Tab3 handleSaveAndComplete calls calculateCasualties({final:false}),
 *     returning a result WITHOUT monte_carlo, then writes it to scenario.casualty_projections
 *     via onUpdate. Tab5's load useEffect fired unconditionally on every
 *     scenario.casualty_projections change, calling setProjections with the non-MC
 *     Tab3 result and wiping the MC-containing projections state.
 *     Fix: useEffect guards setProjections — only accepts incoming value when it has
 *     .version (full calculator result with complete data) or when no projections exist yet.
 *     If current state has monte_carlo and incoming lacks .version, state is preserved.
 *     Manual RECALCULATE (line 1596) always overwrites regardless — that path unchanged.
 *
 * Version: 4.16.0 - BUG-124 + BUG-138 (March 17, 2026):
 *   BUG-124 FIX: commandedMDOS clamped to Math.max(1, ...) in renderLogisticsTimeline.
 *     Root cause: when user sets target MDOS to 0, commandedMDOS = 0.0. getDOSBgColor(0)
 *     evaluates 0 >= 0 = true → 'success.light' (GREEN). Any DOS value including 0.0
 *     satisfied the condition. Fix: clamp commandedMDOS to minimum 1 so the threshold
 *     is always meaningful. DOS = 0.0 now correctly shows red (0 >= 1 = false).
 *   BUG-138 FIX: getScenarioMetadata() standard resolution for MULTINATIONAL force.
 *     Root cause: with no corpsLeadNation set, firstDefined() fell through to
 *     scenario.force.national_standard = 'MULTINATIONAL' which is truthy — so
 *     the chip showed "Standard: MULTINATIONAL" (or further fallback to 'NATO'
 *     when the field is absent). Fix: skip 'MULTINATIONAL' in the firstDefined
 *     chain; when no single standard applies, derive from first brigade's
 *     nationalStandard (mirrors Tab4 _tab4InitNatStd pattern) and display
 *     as "MULTINATIONAL" label only when no nation-specific standard can be resolved.
 *
 * Version: 4.24.0 (March 25, 2026) - BUG-209: Removed internal tracking tags (ENH/ARCH/Phase) from rendered UI strings. No logic change.
 *
 * Version: 4.23.0 - BUG-197 (March 24, 2026): DOS 999 sentinel displayed literally.
 *   Root cause: casualtyCalculator writes 999 as "infinite DOS" when dailyPRBCConsumption=0
 *   (CSS/support force with no T1/T2 casualties). Tab5 displayed "999.0" raw.
 *   Fix: formatDOS() helper — values ≥100 render as "N/A" (no blood consumption).
 *   Also guards the minimum_dos<5 warning panel against firing on sentinel values.
 *   Zero calculator change — display only.
 *
 * Version: 4.15.0 - Phase 6: MASCAL threshold reference row in renderMASCALTimeline (March 15, 2026)
 *   Small info row below the top summary alert showing the scenario's configured
 *   MTF warning/critical thresholds (from scenario.medical_configuration.mascal_config.thresholds)
 *   or constant defaults when absent. Reads scenario prop directly — display only.
 *
 * Version: 4.14.0 - ENH-002: Attachment/Detachment Timeline panel (March 15, 2026):
 *   renderAttachmentTimeline(): NEW function. Reads
 *   scenario.medical_configuration.per_facility_evacuation_assets[*].attached_assets /
 *   detached_assets. Only renders when at least one facility has attachment data.
 *   Two sub-tables per facility: Attached (model, qty, day range, source unit, MOU ref)
 *   and Detached (type, qty, day range, destination unit).
 *   Positioned after renderMROEPanel() in main return.
 *   Zero calculator change — display only from data already saved by Tab3 v4.22.0.
 *
 * Version: 4.13.0 - RESEARCH-REPORTS-002 formal implementation (March 15, 2026):
 *   renderMedicalEstimateHeader() replaced with renderMedicalEstimateSteps() —
 *   full AJMedP-1 6-step coverage panel. Each step shows: step number, name,
 *   tool coverage status (FULL/PARTIAL/PLANNER), brief description, and which
 *   tab/module provides the data. Cross-reference to Tab 6 for Step 5 (COA
 *   Analysis) and formal MSP (OPORD Annex Q). Educational framing for students.
 *
 * Version: 4.12.0 - ENH-022b (March 15, 2026):
 *   Bed census timeline column headers (D1, D2...) now show calendar date alongside
 *   when scenario.operation_parameters.operation_start_date is set in Tab 2.
 *   e.g. D3 -> "D3 (12 Jun)". fmtDay() local utility inside renderMASCALTimeline.
 *   Zero impact when date not set. Backward compatible.
 *
 * Version: 4.11.0 - RESEARCH-REPORTS-002 + BUG-112 (March 14, 2026):
 *   Non-ASCII chars replaced (emoji, arrows, box-drawing, superscripts, section sign).
 *   "Medical Estimate" terminology enforced throughout (removed "Initial Medical
 *   Planning Estimate"  -  not a NATO term per AJMedP-1). Educational disclaimer
 *   header added to results section (AJMedP-1 Steps 4-6 citation).
 *   renderStratevacCapacity, renderStratevacScheduleAnalysis, renderEvacuationRoutes,
 *   renderTacticalEvacCapacity, renderDailyProjectionChart removed  -  moved to Tab 6.
 *   renderInjuryMechanismAnalysis, renderResupplyAnalysis, renderG1Summary
 *   retained in Tab 5 AND duplicated in Tab 6 (session 30 panel migration decisions).
 * Version: 4.10.0 - BUG-108: Summary chip "Standard" shows correct lead nation for MULTINATIONAL corps (March 13, 2026).
 *   getScenarioMetadata() firstDefined chain now checks scenario.force.corpsLeadNation first (ENH-018F field).
 *   Fixes "Standard: NATO" appearing when Romanian is lead nation of a MULTINATIONAL corps.
 *
 * Version: 4.9.0 - ENH-019: renderMROEPanel()  -  EPW & civilian casualty planning panel.
 *   Reads scenario.operation_parameters.mroe_config + projections.mascal_analysis.mroe_load
 *   (populated by casualtyCalculator.js v5.38.0). Displays EPW daily patients, bed demand
 *   (x1.4 no-RTD multiplier per GC III Art.12), civilian impact, MP wing flag, CMO flag,
 *   humanitarian crisis flag. Planning gap warning when MROE enabled but not yet calculated.
 *   Import: EPW_THREAT_LEVELS, CIVILIAN_CASUALTY_LEVELS from mroeConfig.js (March 13, 2026).
 * Version: 4.8.0 - BUG-086: Renamed "Convoy Planning (Average Daily Resupply)" to
 *   "Convoy Planning  -  Consumption Reference" (March 12, 2026). Tab5 has no schedule
 *   access  -  label was misleading when no convoys configured. Companion to BUG-083 (Tab4).
 *
 * Version: 4.7.0 - ENH-018-E: Echelon support personnel breakdown in Force Sustainability (March 11, 2026).
 * Version: 4.6.0 - HNS-001: renderHNSPanel() + IMP-008: cohesion chip in strength tab (March 10, 2026).
 * Version: 4.6.0 - BUG-110: Daily Casualty table truncated at D14 for longer operations (March 13, 2026).
 *   Root cause: hardcoded .slice(0, 14) on dailyProjectionData  -  D15+ silently discarded.
 *   Fix: showAllDays state (default true = all rows shown). Toggle button appears only when
 *   operation > 14 days, allowing user to collapse to D1-D14 compact view if desired.
 *
 * Version: 4.5.0 - ACTION-2: Remove Surgical (DOS) column from Logistics Supply Timeline (March 9, 2026).
 *   Surgical DOS = FST team capacity (staffing metric), not a consumable stock.
 *   G4/J4 cannot action a surgical DOS deficit  -  fix is Tab 3 (add FST teams), not Tab 4.
 *   Removed: getSurgicalRemaining(), surgicalStockoutDay, surgicalDOSTotal, surgical key in
 *   effectiveStockoutDays, Surgical (DOS) table header, surgical cell in each row, surgicalSummary,
 *   surgical entry in Supply Adequacy Summary, surgical from STOCKOUT DETECTED chip condition.
 *   FST Surgical Throughput card in Logistics Impact Analysis section is UNCHANGED  -  correct location.
 *   Root cause: color='default' is not a valid MUI LinearProgress prop  -  bar rendered transparent.
 *   Fix: color changed to 'inherit' + sx override forces grey.200/grey.500 fill for that region.
 *   BUG-RESUPPLY-004 fix is in supplyChainModule.js v5.0.1  -  materiel packages now apply
 *   to materiel DOS correctly (SURGICAL_RESUPPLY affects Medical Materiel column).
 *   Display: truck count on resupply rows now uses primary.contrastText (white) instead
 *   of success.main (green)  -  was invisible against primary.light (blue) row background.
 *   Build: March 9, 2026
 * Version: 4.3.0 - ENH-003 Phase 4: renderStratevacScheduleAnalysis()  -  CSU/PHA queue timeline (March 6, 2026)
 *   - renderStratevacScheduleAnalysis(): NEW function. Reads mascal_analysis.stratevac_schedule_analysis.
 *     Per-airfield day-by-day table: CSU fill %, queue count, departed, backflow -> R3, wait hours.
 *     Doctrinal note: airfield has no beds  -  CSU deployed to APOD (AJMedP-2/RTO-MP-HFM-157).
 *     MASCAL purple tint distinct from R3 red. Departure days marked with [flight] blue header.
 *     Positioned after renderStratevacCapacity() in main render.
 *   - Import: STRATEVAC_AIRCRAFT from stratevacScheduling.js for aircraft label/spec footer.
 *   - Header chip: COLD CHAIN COMPROMISED when cold_chain_viability.any_non_viable is true.
 *   - renderLogisticsTimeline: cold chain section after Supply Adequacy Summary.
 *     Alert (error) when any_non_viable; success box when all viable.
 *     Loss table: Day / Product / Ordered / Arrived Viable / Lost / Reason.
 *     Per-product viability chips (PRBC/FFP/PLT/CRYO).
 *     Binary model note: NATO AMedP-7.1  -  product exceeding transit limit = 0 viable units.
 *     Reads: logisticsProjection.cold_chain_losses[], logisticsProjection.cold_chain_viability{}.
 *     Null-safe: section absent when cold_chain_viability not present (backward compatible).
 *
 * Version: 4.1.0 - ARCH-002: MSR convoy_plan display in logistics timeline panel (March 6, 2026)
 *   - renderLogisticsTimeline: Convoy Planning panel now shows convoy_plan.lead_time_days,
 *     travel time, and warning from logisticsProjection.convoy_plan (supplyChainModule v4.8.0).
 *     Null-safe: panel hidden when no convoy_plan (backward compatible).
 *
 * Version: 4.0.0 - ENH-010: Weight/Volume columns + Convoy Planning panel in logistics timeline (March 6, 2026)
 *   - renderLogisticsTimeline: two new columns  -  Blood (kg/day) and Materiel (kg/day).
 *     Reads blood_weight_kg / materiel_weight_kg from daily_simulation rows (supplyChainModule v4.7.0).
 *     Cells null-safe (older projections without weight data show ' - ').
 *   - renderLogisticsTimeline: Convoy Planning panel below Supply Adequacy Summary.
 *     Shows peak weight/volume day, average daily truck requirement, cold-chain note.
 *     Reads daily_weight_volume from logisticsProjection (ENH-010-D).
 *   - IMPORT: CONVOY_CAPACITY from classVIII.js for truck spec display.
 *
 * Version: 3.9.0 - Phase 5.2 complete: Tactical Evacuation Capacity vs Demand table (March 6, 2026)
 *   - renderTacticalEvacCapacity(): NEW function  -  per-day capacity vs demand table.
 *     Reads projections.by_brigade[*].by_day[*].tactical_evac_analysis (v5.12.0).
 *     Aggregates across brigades per day: sums ground_capacity_per_day, air_capacity_per_day,
 *     total_capacity_per_day, and total demand (hourly x 24).
 *     Table rows: day number, ground capacity, air capacity, total capacity, demand, surplus/deficit,
 *     sufficiency ratio, status chip (GREEN/YELLOW/ORANGE/RED).
 *     Color-coded status column: success/warning/error.
 *     Fleet utilisation row below table: vehicles available / total from getTacticalEvacSummary.
 *     Only renders when at least one day has tactical_evac_analysis data.
 *     Inserted before renderSustainabilityIndicators() in main render.
 *   - ARCH-010 note displayed in card: arch010_dow_risk_informational is display-only.
 * Version: 4.5.0 - calculationService integration (March 8, 2026):
 *   - IMPORT: storeBaseline, isDirty, clearDirty from calculationService.js (new service layer).
 *   - After every successful calculateCasualties() run: storeBaseline(scenario, results) preserves
 *     the baseline snapshot for Tab 6 COA planning. clearDirty() + setCalculationStale(false).
 *   - STATE: calculationStale  -  initialized from isDirty() on mount.
 *   - ALERT: "Scenario Changed" warning rendered above Calculate button when
 *     calculationStale === true && calculationComplete === true. Prompts user to Recalculate.
 *     Dismissed automatically on next successful calculation.
 *   - No change to calculation logic, calculator imports, or result display.
 *   - calculationService.js path: src/services/calculationService.js
 *
 * Version: 3.8.0 - Phase 6.2: MTP indicator, shelf life warning, surgeon fatigue chip, STRATEVAC capacity card (March 5, 2026)
 *   - renderLogisticsImpact(): Blood Products card extended with MTP protocol indicator and
 *     PLT shelf life wastage warning. Both read from projections.by_brigade[last].resources.
 *     MTP indicator: shows configured ratio (1:1:1 / 2:1:1) when mtp_protocol_applied is true.
 *     Shelf life: PLT daily wastage estimate chip when shelf_life.shelf_life_warning is true.
 *   - renderResultsDashboard(): Surgeon fatigue chip added to summary row.
 *     Shows surgeon_performance_pct from last day of first brigade (representative value).
 *     Green >=90%, warning 75-89%, error <75%.
 *   - renderStratevacCapacity(): NEW function  -  STRATEVAC capacity card.
 *     Reads projections.medical_requirements.stratevac_capacity (populated by casualtyCalculator.js v5.11.0).
 *     Shows class, aircraft types, litter capacity, effective hourly throughput, FARP status.
 *     Inserted between renderMASCALTimeline() and renderLogisticsTimeline() in main render.
 *     Only renders when stratevac_capacity is present (air evac enabled in Tab 3).
 * Version: 3.7.0 - Phase 5.2: Tactical MEDEVAC Evacuation Capacity card (March 5, 2026)
 *   - IMPORT: getTacticalEvacSummary from evacuationCapacityCalculator.js v1.4.
 *   - NEW CARD: Evacuation capacity display before Monte Carlo section.
 *     Shows AD threat modifier, weather modifier, effective sorties/day,
 *     ground fleet utilization, air pool label, and degradation warnings.
 *     Card only renders when scenario.medical_configuration.tactical_evac_config exists.
 * v3.6.1 - CONTRAST-001: Logistics timeline text contrast fix (March 5, 2026)
 *   getDOSColor: warning/error states now return '#FFFFFF' (white) instead of warning.main/error.main.
 *   White text on yellow/orange/red backgrounds passes WCAG AA contrast ratio.
 *   STOCKOUT cells: color changed from error.dark to '#FFFFFF' for same reason.
 *   Green (success) cells retain dark text (success.dark)  -  white also readable but dark is fine on light green.
 * Version: 3.6.0 - MASCAL-R2E-R3: Per-division R2E + corps R3 bed timelines added to renderMASCALTimeline() (March 5, 2026)
 *   Reads mascal_analysis.r2e_divisions[], mascal_analysis.r3_corps, r2e_system_summary, r3_system_summary.
 *   Summary alert updated to show first MASCAL event across all three echelons.
 *   R2E section: per-division rows with division name, beds, first MASCAL, peak, dayxday grid.
 *   R3 section: single corps row. Planning chips show correct echelon fractions (30%/10%).
 * Version: 3.5.0 - See changelog above for v3.2.0 / v3.3.0 / v3.4.0 / v3.5.0 changes
 *
 * v3.5.0 (March 4, 2026)  -  MASCAL-WHEN-WHERE: renderMASCALTimeline()
 *   Reads projections.mascal_analysis (populated by casualtyCalculator.js v5.8.0).
 *   Shows: summary alert (first CRITICAL brigade + day), per-brigade R2B occupancy
 *   grid (day x brigade table), color-coded by status GREEN/ELEVATED/HIGH/CRITICAL.
 *   Inserted after renderLogisticsImpact() in the main render.
 *
 * v3.3.0 (March 3, 2026)  -  BED-OCC: Bed occupancy chip in Results Dashboard
 *   Reads scenario.logistics.bed_occupancy (written by Tab4 onUpdate  -  NOT in psp_logistics_projection).
 *   Shows system-wide occupancy rate with status colour. Per-role detail in Tab4 Section 4D.
 *
 * v3.2.0 (March 3, 2026)  -  WTD-01: Anatomical wound distribution panel
 * - Anatomical breakdown section added after DOS cards in logisticsImpact render.
 * - Reads brigadeResult.wound_distribution.t1_by_wound / t2_by_wound from projections
 *   (populated by casualtyCalculator.js v5.7.0 via woundTypeDistribution.js).
 * - 6 body regions (extremity, thorax, abdomen, head_neck, burns, multi) with
 *   LinearProgress bars, casualty counts, percentages, and doctrinal notes.
 * - Gracefully absent when wound_distribution not present in projections.
 *
 * v3.1.1 (March 2, 2026)  -  ISSUE-04 + ISSUE-05:
 * ISSUE-04: Supply timeline "Day" column header now shows "(end of day)" sub-label.
 *   Clarifies that Day 1 = 6.x DOS is correct (commanded 7 DOS minus Day 1 consumption),
 *   not an off-by-one error. No calculation changes.
 * ISSUE-05: DOW chip HelpOutlineIcon tooltip updated to explicitly state "Click (?) for
 *   DOW methodology". Prevents users from interpreting the help button as a warning flag.
 *   The icon and behaviour (opens DOW education dialog) are intentional and unchanged.
 *
 * v3.1.0 (March 1, 2026)  -  PSP_CRE_MASTER_FIX_PLAN_v2_2 Phase G G-07 (ARCH-010 / C-012):
 * Aligns Tab5 CASEVAC display with casualtyCalculator.js v5.6.0 (ARCH-010) and adds
 * four-tier asset status helper per C-012.
 *
 * G-07a: Import ASSET_STATUS_THRESHOLDS from resupplyThresholds.js.
 *   New constant added in resupplyThresholds.js v1.1.0 (G-05).
 *   No other imports changed.
 *
 * G-07b: Add getAssetStatusColor(percent) helper (line ~682).
 *   Implements C-012 four-tier coloring: GREEN >=60%, YELLOW 40-59%,
 *   ORANGE 20-39%, RED <20%. Reads thresholds from ASSET_STATUS_THRESHOLDS
 *   (no hardcoded values). Used for MEDEVAC capacity utilization display.
 *
 * G-07c: DOW_EXPLANATION.factors  -  CASEVAC entry updated (line ~639).
 *   Old text: "CASEVAC increases DOW by ~35% due to no en-route care".
 *   New text: CASEVAC is unplanned/opportunistic per AJMedP-2 and is NOT
 *   a factor in the medical estimate. All overflow modelled as unevacuated.
 *   Educational value preserved  -  context changed from "DOW driver" to
 *   "why MEDEVAC planning is critical".
 *
 * G-07d: CASEVAC chip tooltip (line ~2034)  -  remove "+35% DOW risk".
 *   Old: "CASEVAC used when MEDEVAC capacity exceeded. +35% DOW risk."
 *   New: Informational tracking note per AJMedP-2 / ARCH-010.
 *
 * G-07e: CASEVAC Used card color (line ~3917)  -  change from warning to info.
 *   Old: warning.main if casevacPercent > 0, success.main if 0.
 *   ARCH-010: CASEVAC tracking is informational  -  color implies DOW impact.
 *   New: always info.main; caption adds "(Informational  -  AJMedP-2)".
 *
 * v3.0.0 - Phase E + Phase A: Logistics timeline graph + doctrinal corrections (February 26, 2026)
 *
 * v3.0.4 (February 28, 2026)  -  HIGH-1 + HIGH-2 + HIGH-3 + CRIT-2
 *
 * v3.0.4: CRIT-2: Eliminated duplicate day-by-day DOS table
 *   - renderResupplyAnalysis "DOS Projection Summary" table removed
 *   - renderLogisticsTimeline is now the single authoritative depletion view
 *   - Summary cards, Monte Carlo bands, warnings, per-event table retained
 *
 * v3.0.3: HIGH-3: Surgical column in Logistics Supply Timeline
 *   - surgical_remaining always null (supplyChainModule has no team data)
 *   - getSurgicalRemaining(day) computed from logisticsImpact.surgicalDOS
 *
 * v3.0.2: HIGH-2: "over 0 days" in HQ/Support casualty display
 *   - getScenarioMetadata() called without args -> duration=0
 *   - Fix: pass (projections, scenario) to all three prop calls
 *
 * v3.0.1: HIGH-1: logistics reads immediately on Tab4 Save
 *   - useEffect dep: added scenario?.logistics?.logistics_saved_at
 *
 * v3.0.0 (February 26, 2026)  -  PSP_CRE_MASTER_FIX_PLAN_v2_0 Phase E + Phase A:
 *
 * PHASE E  -  Logistics DOS Timeline Graph (ARCH-005):
 * - E-01: logisticsProjection already loaded from localStorage (L5-01 useEffect  -  no change needed).
 *   Comment corrected: Tab4 (not Tab3) writes 'psp_logistics_projection' per ARCH-009.
 * - E-02: renderLogisticsTimeline() replaces static DOS cards. Added immediately after the
 *   existing Logistics Impact Analysis paper. DOS cards retained  -  they show the user's
 *   Commanded MDOS target. Timeline graph shows actual day-by-day stock depletion.
 * - E-03: Three category lines: Blood, NATO Medical Materiel, Surgical Capacity.
 * - E-04: X axis: Day 1 to op_duration (from logisticsProjection.op_duration_days).
 * - E-05: Y axis: days of supply remaining (from daily_simulation[].{blood,materiel,surgical}_remaining).
 * - E-06: Commanded MDOS horizontal reference (logisticsProjection.commanded_mdos_days ?? target_mdos_days).
 * - E-07: Resupply days annotated in table (from logisticsProjection.resupply_events[]).
 * - E-08: Stockout markers: RED cells + "STOCKOUT" label at first day when DOS reaches 0.
 *   Reads logisticsProjection.stockout_days.{blood, nato_medical_materiel, surgical}.
 * - E-09: Color coding per cell: GREEN (>= commandedMDOS), AMBER (>= minimum), RED (< minimum).
 * - E-10: Text summary below table: per-category adequacy statement.
 *   "Blood: Adequate for full operation" or "Blood: STOCKOUT Day X".
 * - E-11: Null-safe: renders "Run Tab 3 and Tab 4 first" message if no projection.
 *   Placed inside {projections && ...} block  -  no crash if projections also absent.
 * - E-12: ESLint compliance  -  zero new warnings.
 *
 * PHASE A  -  Label and doctrinal corrections:
 * - A-01 (C-001): "Class VIII Supplies" -> "Medical Materiel (AMedP-1.12)" with doctrinal tooltip.
 *   Add tooltip: "NATO medical materiel is classified under AMedP-1.12 (STANAG 2128). US forces
 *   use 'Class VIII' under ATP 4-02." Internal constant names unchanged.
 * - A-03 (C-003): REPORT_TYPES.BLUE_LIGHT.name/fullName  -  no change needed (internal ID).
 *   Add doctrinal note to WBB card subtitle (A-07 / C-005).
 * - A-04 (C-004): REPORT_TYPES.BLUE_LIGHT display name: "Blue Light Protocol" ->
 *   "MASCAL Escalation Protocol". Report internal ID (blue_light) unchanged.
 * - A-07 (C-005): WBB Status Card: add doctrinal note below "Walking Blood Bank" title.
 *   "WBB is not a standalone NATO STANAG. Referenced in AMedP-1.1 operational appendices."
 * - Stale comment on L5-01 useEffect corrected: Tab4 (not Tab3) writes psp_logistics_projection.
 *
 * v2.20.1 (February 26, 2026)  -  Post-L5 Corrections [preserved for history]:
 * - FIX: bloodDOS and supplyDOS now read projection.target_mdos_days (= user's buffer target, e.g. 7).
 *        Was reading by_category.*.days_remaining = opDuration + targetMDOS = 20 days for a 10-day
 *        op with 10-day buffer. Planner sees their configured buffer, not total stock duration.
 * - FIX: surgicalDOS computation restored (v2.19.4 FST/SURG_CO formula). supplyChainModule
 *        explicitly sets surgical.days_remaining = null (no team data available in the engine).
 *        L5-07 removal was incorrect. The Tab5 computation is the only correct source.
 *
 * v2.20.0 (February 26, 2026)  -  FIX_PLAN_LOGISTICS_DOS_v1_0 Phase L-5:
 * - L5-01: useEffect added  -  reads 'psp_logistics_projection' from localStorage on mount.
 *          Re-fires when scenario.calculated_at changes (Tab3 recalculated). Sets logisticsProjection state.
 * - L5-02: Blood DOS card: bloodDOS reads projection.by_category.blood.days_remaining.
 *          Replaces v2.18.3 supply_chain.days_remaining_enhanced block (65 lines deleted).
 * - L5-03: Class VIII DOS card: supplyDOS reads projection.by_category.class_viii.days_remaining.
 *          Replaces v2.19.4 consumableKeys min-calculation block (8 lines deleted).
 * - L5-04: Surgical DOS card: surgicalDOS reads projection.by_category.surgical.days_remaining.
 * - L5-05: All three DOS values null-safe: null -> 'N/A  -  Complete Tab 3 calculation'.
 * - L5-06: getMDOSColor/getMDOSLabel now accept optional thresholds param {minimum, adequate, optimal}.
 *          renderLogisticsImpact passes getNationalLogistics(projection.national_standard) thresholds.
 *          NATO remains default (5/10/15) when projection absent.
 * - L5-07: Interim surgical capacity computation (v2.19.4 FST/SURG_CO inline formulas) removed.
 *          logisticsProjection added to logisticsImpact dependency array.
 * - Added getNationalLogistics import from nationalLogistics.js.
 * - Removed diagnostic console.error blocks that referenced supply_chain (now obsolete).
 *
 * v2.19.5 (February 26, 2026) - Null-safe display: N/A cards, getMDOSColor/Label null guards, remove || 14 fallbacks
 *
 * ==============================================================================
 * v2.18.5 (February 22, 2026) - APP_RULES 6.1 COMPLIANCE
 * - FIX P1-06: t1/t2/t3RtdRate used `?? 0.05/0.20/0.70` after RTD_RATES constants lookup.
 *   APP_RULES 6.1 prohibits fallbacks that mask constants validation errors.
 * - PATTERN: React render function cannot throw (would crash component). Instead:
 *   read all three rates, check for undefined, return visible error UI if missing.
 *   This makes constants failure visible to the user rather than silently wrong.
 * - OLD: RTD_RATES?.BY_TRIAGE_CATEGORY?.T3_MINIMAL?.rtd_rates?.[rtdTimeframe] ?? 0.70
 * - NEW: Read without fallback -> guard check -> return <Alert error> if any undefined.
 * ==============================================================================
 *
 * - FIX P1-05: renderRTDTimeline() used hardcoded RTD rates: T3=0.60, T2=0.15, T1=0.05.
 * - ROOT CAUSE: Rates were literals; they ignored the Theatre Holding Policy configured by user.
 * - WRONG EXAMPLE (THP_7_DAY): T3 rate was 0.60; constants say 0.70 for within_7d.
 * - WRONG EXAMPLE (THP_3_DAY): T3 rate was 0.60; constants say 0.40 for within_72h.
 * - FIX: Added import RTD_RATES from theatreHoldingPolicy.js.
 *   Derive rtdTimeframe from scenario?.logistics?.theatre_holding_policy.
 *   Read T1/T2/T3 RTD rates from RTD_RATES.BY_TRIAGE_CATEGORY.[T].rtd_rates[rtdTimeframe].
 * - NOTE: Triage proportions (T3=30%, T2=25%, T1=15% of WIA) remain as NATO planning estimates
 *   because by_corps does not carry per-triage WIA counts.
 * ==============================================================================
 *
 * v2.18.3 (February 19, 2026) - BLOOD DOS DISPLAY FIX: Read from days_remaining_enhanced
 * - CRITICAL FIX: Updated bloodDOS calculation to read from correct calculator output
 * - PROBLEM: Tab5 showed "N/A" for blood products despite calculator computing correct values
 * - ROOT CAUSE: Tab5 read from days_remaining with uppercase keys (PRBC, FFP, Platelets, Cryoprecipitate)
 * - REALITY: Calculator v5.4.5+ returns days_remaining_enhanced with lowercase _units keys
 *   - prbc_units, ffp_units, platelets_units, cryoprecipitate_units
 * - SYMPTOM: Console showed "supply_chain.days_remaining exists but contains no blood product data"
 * - DIAGNOSTIC: localStorage had correct data ({prbc_units: {days: 6, status: "NOMINAL"}}) but Tab5 couldn't read it
 * - FIX: Lines 945-973 - Changed to read from days_remaining_enhanced with correct keys
 * - ADDED: Handle both numeric days and {days, status} object format
 * - ADDED: Filter Infinity values (unused items with no consumption)
 * - RESULT: Tab5 now displays blood DOS correctly (6 days instead of N/A)
 * - RELATED: Works with Calculator v5.4.5 key mapping and Tab4 v6.5.3 validation fix
 * ==============================================================================
 * v2.18.2 (February 16, 2026) - TAB 2 VALIDATION FIX: Proper Operational Config Check
 * - BUG FIX: Tab 2 prerequisite check was too simple - only checked if brigades array exists
 * - PROBLEM: User completes Tab 1 (brigades exist) but hasn't configured Tab 2 operations yet
 * - OLD CHECK: `if (!scenario.force.forceBuilderResult.brigades)` - passes even without Tab 2!
 * - NEW CHECK: Verifies brigades have operational configuration (operational_state, position, operation, environment)
 * - IMPACT: Now correctly shows error if Tab 2 not completed
 * - ERROR MESSAGE: "Brigade configurations not found -> Fix: Complete Tab 2: Configure brigade operations (click Continue to save)"
 * - FIXED LOCATIONS:
 *   - Line 848-869: validatePrerequisites() function
 *   - Line 1374-1399: renderPrerequisitesCheck() panel
 * - VALIDATION LOGIC: Checks brigades.some(b => b.operational_state || b.position || b.operation || b.environment)
 * ==============================================================================
 * v2.18.1 (February 16, 2026) - NAVIGATION FIX: Stay on Tab 5 After Calculate
 * - BUG FIX: Removed auto-navigation to Tab 6 after calculation completes
 * - PROBLEM: User clicks "Calculate" -> calculator runs -> auto-advances to Tab 6
 * - IMPACT: Users couldn't review results before being moved away
 * - ROOT CAUSE: Line 1186 called onComplete() after calculation (not just after "Proceed" button)
 * - FIX: Removed onComplete() call from calculation handler (lines 1185-1188 deleted)
 * - RESULT: User stays on Tab 5 to review results, only advances when clicking "Proceed to Tab 6"
 * - Tab completion now ONLY triggered by explicit "Proceed to Tab 6" button (line 3370)
 * ==============================================================================
 * v2.18.0 (February 16, 2026) - MONTE CARLO PHASE 2: UNCERTAINTY DISPLAY
 * - BUG FIX: bloodDOS now checks ALL blood products (PRBC, FFP, Platelets, Cryo), uses minimum
 * - ROOT CAUSE: Previous version used single field (blood_dos or days_of_supply), showed misleading value
 * - ISSUE: User saw "Blood Products: 10 days" when platelets only had 3 days (critical shortage!)
 * - EXAMPLE: PRBC 7d + FFP 7d + Platelets 3d + Cryo 10d -> OLD showed "10 days" (WRONG!) -> NEW shows "3 days" (CORRECT!)
 * - FIX: Now reads from projections.supply_chain.days_remaining for each blood product
 * - LOGIC: Uses minimum days across all products (platelets often limiting factor due to shelf life)
 * - DATA SOURCE: projections.supply_chain.days_remaining (from calculator v5.2.0)
 * - FALLBACK: If no supply_chain data, uses legacy logistics.blood_dos field
 * - RESULT: Logistics Impact section now accurately reflects worst-case blood product shortage
 * - IMPACT: Now matches Tab4 badge status and Stockout Predictions section (all show 3 days for this scenario)
 * - Lines changed: 885-915 (bloodDOS calculation rewritten with supply_chain check)
 *
 * v2.16.0 (February 11, 2026) - CRITICAL BUG FIX: Personnel Replacement Calculation:
 * - FIXED: g1Summary totalReplacement calculation (lines 976-1007)
 * - ROOT CAUSE: Used fixed percentages (40% WIA, 15% DNBI) instead of actual RTD values
 * - OLD FORMULA: WIA_permanent = total_wia * 0.40 (WRONG!)
 * - NEW FORMULA: WIA_permanent = total_wia - wia_rtd (CORRECT!)
 * - RTD breakdown estimated proportionally: wia_rtd = total_rtd * (wia / (wia + dnbi))
 * - RESULT: totalReplacement now equals netLoss (mathematically correct)
 * - IMPACT: Personnel replacement numbers now accurate in G1 Summary section
 * - Verification: totalReplacement = KIA + DOW + (WIA - wia_rtd) + (DNBI - dnbi_rtd) = netLoss [ok]
 *
 * v2.15.0 (February 11, 2026) - ARCHITECTURE VERIFICATION & CONSOLE CLEANUP:
 * - VERIFIED: Tab5 correctly auto-loads calculator results from scenario.casualty_projections
 * - VERIFIED: useEffect (line 828) automatically loads results when Tab3/Tab4 save
 * - VERIFIED: Calculate button correctly shows "Recalculate" when results exist
 * - VERIFIED: Architecture complete: Tab3/Tab4 auto-run calculator -> Tab5 auto-loads results
 * - CLEANUP: Removed 4 debug console.log/warn statements (kept console.error for error handling)
 * - DATA FLOW: Tab3 Save -> Calculator -> Tab4 Save -> Calculator -> Tab5 Display
 * - Button logic: "Calculate Casualties" (no results) / "Recalculate" (results exist)
 * - Navigation buttons already implemented: Back to Tab 4, Proceed to Tab 6
 *
 * v2.14.0 (February 10, 2026):
 * - ARCH: Added forwardRef + useImperativeHandle for toolbar navigation compatibility
 *   - Tab5 is Pattern C (read-only consumer): no form state to flush
 *   - flushState is a guarded re-emit: if projections exist, re-emits them; otherwise no-op
 *   - projectionsRef synced from projections state for access inside flushState closure
 * - CLEANUP: Removed debug console.log block (lines 707-717, ran on every render)
 * - ENCODING FIX: Removed all non-ASCII characters from code lines (36 lines cleaned)
 *
 * Version: 2.13.0 - v7.0 Architectural Integration
 * 
 * CHANGELOG:
 * v2.13.0 (February 7, 2026 - v7.0 ARCHITECTURAL INTEGRATION):
 * - REFACTOR: Updated validation to check force.forceBuilderResult.brigades
 *   - OLD: Checked operation_parameters.brigade_configs (doesn't exist after Tab2 v7.0)
 *   - NEW: Checks force.forceBuilderResult.brigades (single source of truth)
 * - FIX: Tab 2 prerequisite chip now checks correct brigade location (line 1256-1262)
 * - CLEANUP: Removed unnecessary fallback to brigade_configs in getScenarioMetadata() (line 563)
 * - INTEGRATION: Compatible with Tab2 v7.0 and Calculator v5.1.0
 * - NO CHANGES to calculator result loading (already correct - line 788-793)
 * - DEPLOY: Must deploy with Tab2 v7.0 and Calculator v5.1.0 simultaneously
 * 
 * v2.12.1 (January 30, 2026):
 * - ENHANCED: Delivery risk tooltip now shows separate time and probability factors
 *   - Time Factors section: Route, Weather, AD Threat modifiers
 *   - Shows AD threat impact on delivery time (air methods take longer routes)
 *   - Clear separation between time impact and success probability
 *   - Example: AD Threat: MEDIUM affects both time (1.3x longer) and success (80%)
 * 
 * v2.12.0 (January 29, 2026 - PHASE 4.7.1):
 * - CROSS-TAB NAVIGATION:
 *   - Added onNavigate prop to Tab5_Reports component
 *   - "Fix in Tab 4" button in stockout alert (CRITICAL)
 *   - "Add Resupply" button in low DOS warning
 *   - Navigation with scroll targeting: scrolls to #resupply-section
 *   - 100ms delay for scroll to allow tab switch to complete
 *   - Guidance text: "-> Click 'Fix in Tab 4' to add resupply events or use Auto-Schedule"
 * - USER GUIDANCE:
 *   - Clear next steps when problems detected
 *   - One-click navigation to solution location
 *   - Eliminates "what do I do now?" confusion
 *   - Completes the Tab 4 <-> Tab 5 workflow loop
 * 
 * v2.11.0 (January 29, 2026 - PHASE 4.6.5):
 * - MONTE CARLO RISK VISUALIZATION:
 *   - Added probability bands display: Worst case (5th), Expected (50th), Best case (95th)
 *   - Shows final DOS and minimum DOS for each percentile
 *   - Color-coded: Red (worst), Yellow (expected), Green (best)
 *   - Stockout probability percentage with average stockout day
 *   - Success rate badge with color coding (<80% = error, 80-95% = warning, >95% = success)
 *   - Environmental conditions display: Weather and AD threat used in simulation
 * - DELIVERY RISK INDICATORS:
 *   - New "Delivery Risk" column in resupply events timeline
 *   - Shows success probability % for each event (85%, 98%, etc.)
 *   - Color-coded chips: Green (LOW), Yellow (MEDIUM), Red (HIGH/CRITICAL)
 *   - Tooltip with detailed factors:
 *     - Route status and impact
 *     - Weather impact
 *     - AD threat impact (air only)
 *     - Total delivery time
 * - CONDITIONAL DISPLAY:
 *   - Monte Carlo section only shows if routes configured AND enabled
 *   - Delivery risk column only shows when Monte Carlo active
 *   - Maintains backward compatibility when routes not configured
 * 
 * v2.10.0 (January 29, 2026 - PHASE 4.5.5):
 * - INJURY MECHANISM ANALYSIS:
 *   - Added aggregateInjuryMechanismData() to aggregate mechanism breakdown from all brigades/days
 *   - Added renderInjuryMechanismAnalysis() displaying actual injury patterns
 *   - Shows operational environment (URBAN, OPEN, DEFENSIVE, OFFENSIVE, MIXED)
 *   - Displays average blood consumption vs NATO standard (2.0 PRBC/casualty)
 *   - Percentage difference indicator: +20% warning if higher, success if lower
 *   - Breakdown table by mechanism: GSW, Blast/Frag, Mine/IED, Burns, Crush
 *   - Shows T1 casualties, PRBC/FFP units, MTP patients per mechanism
 *   - Surge detection: Alert when MSK surge threshold exceeded
 *   - Educational note explaining why consumption differs from NATO standard
 *   - Uses calculator exports (BLOOD_BY_MECHANISM_CONFIG) for consistency
 *   - Placed after Logistics Impact Analysis, before Evacuation Routes
 *
 * v2.9.0 (January 28, 2026 - PHASE 4.2):
 * - RESUPPLY OPERATIONS DISPLAY:
 *   - Import PUSH_PACKAGES from classVIII.js for package labels
 *   - Added renderResupplyAnalysis() function showing resupply timeline
 *   - Resupply summary cards: Total events, PRBC received, Minimum DOS, Final DOS
 *   - Resupply events timeline table with package names, delivery method, units, DOS impact
 *   - DOS projection table showing day-by-day blood supply status
 *   - Stockout warnings (critical alert if blood supply exhausted)
 *   - Low DOS warnings (warning if minimum DOS < 5 days)
 *   - Package labels looked up from PUSH_PACKAGES (Emergency Blood Package, etc.)
 *   - Delivery method color coding: BLUE_LIGHT (red), AIR (blue), GROUND (default)
 *   - DOS status color coding: CRITICAL (red, <3d), LOW (yellow, 3-5d), ADEQUATE (green, >5d)
 *   - Resupply received rows highlighted in green
 *   - Tooltips show package descriptions on hover
 *
 * v2.8.0 (January 26, 2026 - PHASE 2.2):
 * - EVACUATION ROUTES DISPLAY:
 *   - Added renderEvacuationRoutes() function showing route status
 *   - Route segments display: R1->R2B, R2B->R2E, R2E->R3, R3->R4
 *   - Golden Hour status card with achievable/at risk indicator
 *   - Total travel time and DOW impact display
 *   - Route warnings display (closed routes, contested routes, alternate usage)
 *   - Critical route (R1->R2B) highlighted if >45 min (Golden Hour risk)
 *   - Alternate route usage indicator
 *   - Uses AltRouteIcon from MUI (no UTF-8 encoding issues)
 *
 * v2.7.0 (January 26, 2026 - PHASE 1.2):
 * - WBB (Walking Blood Bank) Display Integration:
 *   - Added WBB Status Card showing activations, units contributed, universal units
 *   - Card displays only when WBB was activated during operation
 *   - Shows days activated and total contributions
 *   - Uses BloodtypeIcon from MUI (no UTF-8 encoding issues)
 *
 * v2.6.5 (January 24, 2026):
 * - BUG-064 FIX: All snackbar/console message encoding issues
 *   - Fixed 11 lines with corrupted UTF-8 emojis (???"????????'????? ?????????"??????"??")
 *   - Snackbar success, error, warning messages all display correctly
 *
 * v2.6.4 (January 24, 2026):
 * - BUG-062 FIX: DOW formula encoding (???'?+??(TM)???????????" ???? ??(TM) ????")
 * - BUG-063 FIX: Warning display handles both string and object formats
 *   - Calculator returns strings like "CRITICAL: message"
 *   - UI now correctly displays both formats
 *
 * v2.6.3 (January 24, 2026):
 * - BUG-059 FIX: Warning bullet point encoding (???'???????????????????????????? ???? ??(TM) ???????)
 * - Added fallback for missing warning type and message
 *
 * v2.6.2 (January 23, 2026):
 * - BUG-058 FIX: Surgical hours displaying 0h
 *   - UI was reading surgical.total_hours but calculator returns surgical.hours
 *   - Fixed in 3 locations: lines 714, 729, 1348
 *   - Surgical chip now shows correct hours (e.g., 1262h)
 *
 * v2.6.1 (January 23, 2026):
 * - ESLint FIX: Removed TrendingDownIcon MUI import, using DATA_ICONS.TRENDING_DOWN from icons.js
 * - ESLint FIX: Using getStatusColor() function instead of hardcoded ternaries (3 locations)
 * - ESLint FIX: Implemented activeChartTab/setActiveChartTab for T5-003 chart tab feature
 * - Added chart view tabs: Daily Casualties, Cumulative, Force Strength
 *
 * v2.6.0 (January 22, 2026):
 * - BUG-051 FIX: Removed hardcoded "v4.13" - now reads from projections.version
 * - BUG-052 FIX: Duration shows correctly via getScenarioMetadata()
 * - BUG-053 FIX: Brigades shows correctly via getScenarioMetadata()
 * - BUG-054 FIX: National Standard shows correctly via getScenarioMetadata()
 * - BUG-055 FIX: Timestamp shows correctly via formatCalculatedAt()
 * - BUG-056 FIX: Brigade breakdown uses proper field paths
 * - BUG-057 FIX: UTF-8 encoding fixed for bullet points
 * - Added getScenarioMetadata() accessor function that looks in:
 *   1. projections.scenario_summary (calculator output)
 *   2. projections root level
 *   3. scenario prop (Tab 1-2 data) as fallback
 * - Added getCalculatorVersion() for dynamic version display
 * - Added formatCalculatedAt() for proper date handling
 * - Added getBrigadeTotal() for correct brigade totals
 *
 * v2.5.0 (January 20, 2026):
 * - BUG-030 FIX: Infinite re-render loop on Tab 5 navigation
 *   - Added useRef to track previous validation errors
 *   - Only calls onValidationChange when errors actually change
 *   - Changed first useEffect to depend on scenario?.casualty_projections instead of entire scenario
 *   - Root cause: useEffect was calling onValidationChange on every render because
 *     validatePrerequisites created new function reference each time scenario changed
 *
 * v2.4.0 (January 19, 2026):
 * - CRIT-007 FIX: Changed onUpdate() to only pass calculation results, not entire scenario
 */

import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
// V4-005: i18n
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  AlertTitle,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Snackbar,
  Tooltip
} from '@mui/material';

// Icons
import CalculateIcon from '@mui/icons-material/Calculate';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import InventoryIcon from '@mui/icons-material/Inventory';
import HealingIcon from '@mui/icons-material/Healing';
import TimelineIcon from '@mui/icons-material/Timeline';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveAltIcon from '@mui/icons-material/SaveAlt';

// ============================================================================
// IMPORTS FROM CALCULATION ENGINE
// ============================================================================
import { calculateCasualties } from '../calculationEngine/casualtyCalculator.js';
import {
  storeBaseline,
  isDirty,
  clearDirty,
} from '../services/calculationService.js';

// Phase 5.2: Tactical evacuation summary for evacuation capacity card
import { getTacticalEvacSummary } from '../calculationEngine/evacuationCapacityCalculator.js';

// PHASE 4.2: Import PUSH_PACKAGES from classVIII.js for resupply display
// ENH-010: CONVOY_CAPACITY for truck count display
import { PUSH_PACKAGES, CONVOY_CAPACITY } from '../constants/classVIII.js';

// P1-05 FIX: Import RTD_RATES to replace hardcoded RTD rates in renderRTDTimeline.
// v3.5.0: Add HOSPITALIZATION_RATE + ECHELON_ADMISSION_DISTRIBUTION for MASCAL planning parameter chip display.
import { RTD_RATES, HOSPITALIZATION_RATE, ECHELON_ADMISSION_DISTRIBUTION } from '../constants/theatreHoldingPolicy.js';

// L5-06: Import getNationalLogistics for national DOS thresholds in color coding.
import { getNationalLogistics, AVAILABLE_NATIONAL_STANDARDS } from '../constants/nationalLogistics.js';

// G-07a / C-012: Import ASSET_STATUS_THRESHOLDS for four-tier asset readiness coloring.
// Implements GREEN >=60%, YELLOW 40-59%, ORANGE 20-39%, RED <20% per C-012.
// ARCH-010: CASEVAC is informational per AJMedP-2  -  these tiers apply to MEDEVAC
// capacity utilization, not to CASEVAC as a DOW-reducing factor.
import { ASSET_STATUS_THRESHOLDS } from '../constants/resupplyThresholds.js';

// ENH-003 Phase 4: STRATEVAC schedule analysis display.

// HNS-001: HNS level definitions for panel display (label, icon, nominal bed/ambulance values).
import { HNS_SUPPORT_LEVELS } from '../constants/hnsConfig.js';
// ENH-019: MROE panel  -  EPW level labels, civilian level labels, and V3.1 matrix constants
import {
  EPW_THREAT_LEVELS,
  CIVILIAN_CASUALTY_LEVELS,
  MROE_STATES,
  PATIENT_CATEGORIES,
  MROE_DEFAULT_MATRICES,
} from '../constants/mroeConfig.js';

// GROUP-1-B: USCENTCOM "6 and 2" blood floor constants (Military Medicine 2024).
import {
  BLOOD_FLOOR_PER_OR_BED,
  BLOOD_FLOOR_PER_POI_TEAM,
  BLOOD_FLOOR_FST_OR_BEDS,
  BLOOD_FLOOR_SURGCO_OR_BEDS,
  BLOOD_FLOOR_AMBER_THRESHOLD,
  BLOOD_FLOOR_RED_THRESHOLD
} from '../constants/bloodProducts.js';

// GROUP-1-C: WIA:KIA ratio validation thresholds (Military Medicine 2022).
import {
  WIA_KIA_RATIO_PEER_MIN,
  WIA_KIA_RATIO_PEER_MAX,
  WIA_KIA_RATIO_HIGH_WARNING,
  WIA_KIA_RATIO_LOW_WARNING,
  WIA_KIA_RATIO_COIN,
  WIA_KIA_RATIO_PEACEKEEPING
} from '../constants/casualtyCalculatorConstants.js';

// Centralized icons - PSP/CRE architecture standard
import { DATA_ICONS } from '../utils/icons.js';

// (ENH-030: ReportFormattersService and exportUtils imports moved to Tab 6)

// ============================================================================
// IMPORTS FROM EXPORT COMPONENTS
// ============================================================================
import NonCombatPersonnelSection from '../components/NonCombatPersonnelSection';
import MedicalPersonnelSection from '../components/MedicalPersonnelSection';
import AssumptionsPanel from '../components/AssumptionsPanel';  // CALC-010: inputs assumed by the calculation
import { lsRead, lsWrite } from '../utils/lsStore.js'; // BUG-FF-012; lsWrite added V4-TAB6-001
import { instantOfDay, formatZulu, operationPeriodLabel, dayLabelZulu, DEFAULT_START_TIME_Z } from '../constants/exerciseDates';   // R2-09: operation dates in Zulu

// (ENH-030: REPORT_TYPES and EXPORT_FORMATS moved to Tab 6)

// Human-readable lookup for national standard codes (e.g. 'MULTINATIONAL' → 'Multinational Force')
// Placed after all imports to avoid ESLint import/first violation.
const _NS_LABEL = Object.fromEntries(
  AVAILABLE_NATIONAL_STANDARDS.map(s => [s.code, s.name])
);
const humanizeNatStd = code => _NS_LABEL[code] || code;

// ============================================================================
// DOW EXPLANATION (T5-011) - Educational content
// ============================================================================

const DOW_EXPLANATION = {
  definition: 'Died of Wounds (DOW) refers to military personnel who were wounded in action (WIA) but subsequently died before reaching definitive surgical care. This is distinct from Killed in Action (KIA), who die immediately or within minutes of injury.',
  factors: [
    { factor: 'Evacuation Time', impact: 'Primary determinant - each 10 min delay beyond Golden Hour increases DOW by ~8%' },
    { factor: 'Blood Availability', impact: 'Shortage can increase DOW by 30-50%' },
    { factor: 'Forward Surgical Teams', impact: 'Can reduce DOW by 15-20% via damage control surgery' },
    { factor: 'MEDEVAC vs CASEVAC', impact: 'CASEVAC is unplanned/opportunistic and is NOT a medical planning factor (AJMedP-2). All MEDEVAC overflow is modelled as unevacuated (+60% DOW penalty). This is why maximising MEDEVAC capacity is critical.' },
    { factor: 'Terrain', impact: 'Urban/Mountain terrain increases DOW due to difficult evacuation' },
    { factor: 'Night Operations', impact: 'Limited MEDEVAC increases DOW by ~20%' }
  ],
  // BUG-062 FIX: Use proper multiplication symbol
  formula: 'DOW Rate = Base Rate (5-8%) " Evacuation Modifier " Blood Modifier " FST Modifier',
  references: [
    'NATO AJMedP-1: Allied Joint Medical Doctrine',
    'TCCC Guidelines 2023',
    'Eastridge et al. (2012): Death on the battlefield 2001-2011',
    'Kotwal et al. (2016): Effect of Golden Hour policy on combat casualties'
  ]
};

// ============================================================================
// HELPER FUNCTIONS - v2.6.0 Accessor Functions
// ============================================================================

/**
 * Format number with locale-aware separators
 */
const formatNumber = (num) => {
  if (num === undefined || num === null || isNaN(num)) return '0';
  return Number(num).toLocaleString('en-US');
};

/**
 * Format percentage with rounding
 */
const formatPercent = (value) => {
  if (value === undefined || value === null || isNaN(value)) return '0%';
  return `${Math.round(value)}%`;
};

/**
 * Get status color based on percentage
 */
const getStatusColor = (value, thresholds = { green: 80, yellow: 60 }) => {
  if (value >= thresholds.green) return 'success';
  if (value >= thresholds.yellow) return 'warning';
  return 'error';
};

/**
 * G-07b / C-012: Get four-tier MUI color for asset operational readiness percentage.
 *
 * Implements C-012 thresholds from ASSET_STATUS_THRESHOLDS (resupplyThresholds.js v1.1.0).
 * No hardcoded values  -  all thresholds sourced from constants per APP_RULES 2.1.
 *
 * ARCH-010: Applied to MEDEVAC capacity utilization display.
 * CASEVAC tracking is informational only per AJMedP-2 and does not influence DOW.
 *
 * @param {number} percent - Asset operational readiness (0-100)
 * @returns {string} MUI color string: 'success' | 'warning' | 'error' | 'default'
 */
const getAssetStatusColor = (percent) => {
  if (percent === null || percent === undefined || isNaN(percent)) return 'default';
  if (percent >= ASSET_STATUS_THRESHOLDS.GREEN.min_percent)  return ASSET_STATUS_THRESHOLDS.GREEN.mui_color;
  if (percent >= ASSET_STATUS_THRESHOLDS.YELLOW.min_percent) return ASSET_STATUS_THRESHOLDS.YELLOW.mui_color;
  if (percent >= ASSET_STATUS_THRESHOLDS.ORANGE.min_percent) return ASSET_STATUS_THRESHOLDS.ORANGE.mui_color;
  return ASSET_STATUS_THRESHOLDS.RED.mui_color;
};

/**
 * Get MDOS status color.
 * L5-06: Optional thresholds param accepts {minimum, adequate, optimal} from getNationalLogistics().
 * Defaults to NATO thresholds (minimum=5, adequate=10, optimal=15) when absent.
 */
const getMDOSColor = (days, thresholds = {}) => {
  if (days === null || days === undefined) return 'default';
  const optimal  = thresholds.optimal  ?? 15;
  const adequate = thresholds.adequate ?? 10;
  const minimum  = thresholds.minimum  ?? 5;
  if (days >= optimal)  return 'success';
  if (days >= adequate) return 'info';
  if (days >= minimum)  return 'warning';
  return 'error';
};

/**
 * Get MDOS status label.
 * L5-06: Optional thresholds param for national-standard-aware labels.
 */
const getMDOSLabel = (days, thresholds = {}) => {
  if (days === null || days === undefined) return 'N/A - Data Missing';
  const optimal  = thresholds.optimal  ?? 15;
  const adequate = thresholds.adequate ?? 10;
  const minimum  = thresholds.minimum  ?? 5;
  if (days >= optimal)  return 'GREEN - Adequate';
  if (days >= adequate) return 'BLUE - Monitor';
  if (days >= minimum)  return 'YELLOW - Plan Resupply';
  return 'RED - Critical';
};

/**
 * BUG-051 FIX: Get calculator version from projections
 * Looks in multiple places for version string
 */
// BUG-T23 fix: version may be absent from old projections (calculator v4.x did not write it).
// Add CURRENT_CALCULATOR_VERSION as last-resort fallback so chip always shows a meaningful value.
const CURRENT_CALCULATOR_VERSION = '5.56.0'; // sync with casualtyCalculator.js this.version

const getCalculatorVersion = (projections) => {
  if (!projections) return CURRENT_CALCULATOR_VERSION;

  // Try direct version field first (set by calculator v5.x+)
  if (projections.version && projections.version !== 'N/A') return projections.version;

  // Try scenario_summary.version
  if (projections.scenario_summary?.calculator_version) {
    return projections.scenario_summary.calculator_version;
  }

  // Try calculator_version at root
  if (projections.calculator_version) return projections.calculator_version;

  // Fallback: current known version (old projections that pre-date version stamping)
  return CURRENT_CALCULATOR_VERSION;
};

/**
 * BUG-052/053/054 FIX: Get scenario metadata from multiple sources
 * Priority: projections.scenario_summary > projections root > scenario prop
 * 
 * @param {Object} projections - Calculator output
 * @param {Object} scenario - Tab 1-4 scenario data
 * @returns {Object} { duration, brigades, standard, timestamp }
 */
const getScenarioMetadata = (projections, scenario) => {
  // Default values
  const defaults = {
    duration: 0,
    brigades: 0,
    standard: 'Unknown',
    timestamp: null
  };
  
  if (!projections && !scenario) return defaults;
  
  // Helper to find first defined value
  const firstDefined = (...values) => values.find(v => v !== undefined && v !== null);
  
  // Duration: Look in multiple places
  const duration = firstDefined(
    projections?.scenario_summary?.duration_days,
    projections?.duration_days,
    scenario?.operation_parameters?.duration_days,
    scenario?.operation_parameters?.total_duration_days,
    14 // NATO standard default
  );
  
  // Brigades: Count from projections or scenario
  const brigades = firstDefined(
    projections?.scenario_summary?.total_brigades,
    projections?.total_brigades,
    projections?.by_brigade?.length,
    scenario?.force?.forceBuilderResult?.brigades?.length,
    // v2.13.0: Removed brigade_configs fallback (doesn't exist after Tab2 v7.0)
    0
  );
  
  // National Standard: Look in multiple places
  // BUG-108 FIX: corpsLeadNation (ENH-018F) checked first  -  MULTINATIONAL corps with a lead
  // nation must display that nation's standard, not fall through to 'NATO'.
  // BUG-138 FIX: Skip 'MULTINATIONAL' in the chain — it is a planning mode, not a
  // consumable standard. When no corpsLeadNation is set and the force is MULTINATIONAL,
  // return 'MULTINATIONAL' directly (position 2, after corpsLeadNation).
  // BUG-205 FIX (March 25, 2026): moved _forceIsMultinational to position 2 so the
  // chain no longer falls through to _firstBrigadeStd (removed — was unused after fix).
  const _forceIsMultinational =
    scenario?.force?.nationalStandard === 'MULTINATIONAL' ||
    scenario?.force?.national_standard === 'MULTINATIONAL';
  // BUG-205 FIX (March 25, 2026): For MULTINATIONAL forces without a corpsLeadNation,
  // the previous chain fell through to _firstBrigadeStd (e.g. ROMANIAN for RO+FR+DE).
  // The chip showed "Standard: ROMANIAN" instead of "Standard: MULTINATIONAL".
  // Fix: moved _forceIsMultinational check to position 2 (after corpsLeadNation).
  const standard = firstDefined(
    scenario?.force?.corpsLeadNation,
    _forceIsMultinational ? 'MULTINATIONAL' : undefined,
    projections?.scenario_summary?.national_standard !== 'MULTINATIONAL'
      ? projections?.scenario_summary?.national_standard : undefined,
    projections?.national_standard !== 'MULTINATIONAL'
      ? projections?.national_standard : undefined,
    scenario?.force?.forceBuilderResult?.national_standard !== 'MULTINATIONAL'
      ? scenario?.force?.forceBuilderResult?.national_standard : undefined,
    scenario?.force?.national_standard !== 'MULTINATIONAL'
      ? scenario?.force?.national_standard : undefined,
    'NATO' // Default for single-nation forces where standard is genuinely unknown
  );
  
  // Timestamp: Look for calculation time
  const timestamp = firstDefined(
    projections?.scenario_summary?.calculated_at,
    projections?.calculated_at,
    scenario?.calculated_at,
    null
  );
  
  return { duration, brigades, standard, timestamp };
};

/**
 * BUG-055 FIX: Format calculated timestamp safely
 */
const formatCalculatedAt = (timestamp) => {
  if (!timestamp) return 'Not calculated';
  
  try {
    const date = new Date(timestamp);
    // Check for invalid date
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return 'Invalid Date';
  }
};

/**
 * BUG-056 FIX: Get brigade totals safely
 * Handles different data structures from calculator versions
 */
const getBrigadeTotal = (brigade, field) => {
  if (!brigade) return 0;
  
  // Try totals object first (v4.22+ format)
  if (brigade.totals?.[field] !== undefined) {
    const val = brigade.totals[field];
    return isNaN(val) ? 0 : val;
  }
  
  // Try direct field (older format)
  if (brigade[field] !== undefined) {
    const val = brigade[field];
    return isNaN(val) ? 0 : val;
  }
  
  // Try prefixed field (e.g., total_kia)
  if (brigade[`total_${field}`] !== undefined) {
    const val = brigade[`total_${field}`];
    return isNaN(val) ? 0 : val;
  }
  
  return 0;
};

/**
 * Get total casualties for a brigade safely
 */
const getBrigadeTotalCasualties = (brigade) => {
  if (!brigade) return 0;
  
  // Try total_casualties first
  if (brigade.total_casualties !== undefined && !isNaN(brigade.total_casualties)) {
    return brigade.total_casualties;
  }
  
  // Try totals sum
  if (brigade.totals) {
    const sum = (brigade.totals.kia || 0) + 
                (brigade.totals.dow || 0) + 
                (brigade.totals.wia || 0) + 
                (brigade.totals.dnbi || 0);
    if (!isNaN(sum)) return sum;
  }
  
  // Calculate from individual fields
  const sum = getBrigadeTotal(brigade, 'kia') +
              getBrigadeTotal(brigade, 'dow') +
              getBrigadeTotal(brigade, 'wia') +
              getBrigadeTotal(brigade, 'dnbi');
  
  return isNaN(sum) ? 0 : sum;
};

/**
 * Get brigade casualty rate safely
 */
const getBrigadeCasualtyRate = (brigade) => {
  if (!brigade) return 0;
  
  // Try direct casualty_rate
  if (brigade.casualty_rate !== undefined && !isNaN(brigade.casualty_rate)) {
    return brigade.casualty_rate;
  }
  
  // Calculate from strength and casualties
  const strength = brigade.initial_strength || brigade.strength || 0;
  const casualties = getBrigadeTotalCasualties(brigade);
  
  if (strength > 0) {
    return (casualties / strength) * 100;
  }
  
  return 0;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const Tab5_Reports = forwardRef(function Tab5_Reports({ scenario, mode: _mode, onUpdate, onComplete, onValidationChange, onNavigate }, ref) {
  // R2-09: label of an operation day: 'D+3 (25 Oct 2026 06:00Z)' with a start date (Zulu), 'D+3' without
  const dayLabel = (n) => {
    const op = scenario?.operation_parameters;
    try { return dayLabelZulu(op?.operation_start_date || '', op?.operation_start_time_z || DEFAULT_START_TIME_Z, Number(n)); } catch (_e) { return `D+${n}`; }
  };
  // V4-005: i18n
  const { t, i18n } = useTranslation();

  // ============================================================================
  // TOOLBAR NAVIGATION ARCHITECTURE
  // ============================================================================

  // Tab5 is Pattern C (read-only consumer): no form state to flush.
  // flushState re-emits projections if a calculation has been completed,
  // allowing App.jsx to capture results when user navigates away mid-session.
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);

  // Snapshot of projections for access inside flushState closure (avoids stale ref)
  const projectionsRef = useRef(null);

  useImperativeHandle(ref, () => ({
    flushState: () => {
      if (projectionsRef.current && onUpdateRef.current) {
        onUpdateRef.current({
          casualty_projections: projectionsRef.current,
          calculation_results: projectionsRef.current
        });
      }
      // No-op if no calculation has been run yet
    }
  }));
  
  // Note: _mode prefixed with underscore - available for future use but not currently needed in Tab 5
  // ============================================================================
  // STATE
  // ============================================================================
  
  const [isCalculating, setIsCalculating] = useState(false);

  const [calculationError, setCalculationError] = useState(null);
  // BUG-203/204 FIX (March 25, 2026): Lazy-initialize from persisted scenario data.
  // BUG-204: projections=null on first render → MC panel hidden until RECALCULATE pressed.
  //   Fix: initialize directly from scenario.casualty_projections — MC panel renders immediately.
  // BUG-203: calculationComplete=false on first render, then useEffect sets it true immediately
  //   when persisted projections exist → calculationStale && calculationComplete → "Scenario Changed".
  //   Fix: initialize calculationComplete from existing projections; gate calculationStale on both
  //   isDirty() AND existing projections (can't be stale if nothing calculated yet).
  const [projections, setProjections] = useState(() => scenario?.casualty_projections || null);
  // calculationService dirty flag  -  true when Tab 1-4 changed after last calculation
  const [calculationComplete, setCalculationComplete] = useState(() => !!scenario?.casualty_projections);
  // PRELIM-001: True only after a successful Tab 5 RECALCULATE (handleCalculate success path).
  // Used to show/hide the "Preliminary data" banner in the Results Dashboard.
  // Initialized from existing projections: if they already carry .monte_carlo data from a
  // previous session, the banner is suppressed on mount (no need to recalculate just to
  // see results the user already produced). Clears to false only on component remount
  // (app RESET) — not on navigation (CSS-mounted, never unmounts mid-session).
  const [calculatedInTab5, setCalculatedInTab5] = useState(
    () => !!(scenario?.casualty_projections?.monte_carlo)
  );
  // BUG-203 FIX: Only stale if BOTH dirty flag set AND results already exist.
  // Previous: isDirty() alone → warning fired on every reopen if Tab4 was saved after last
  // calculation, even with no actual user changes in the current session.
  const [calculationStale, setCalculationStale] = useState(() =>
    isDirty() && !!scenario?.casualty_projections
  );
  // ENH-WORKFLOW-001 FIX: useState initializer only runs at mount — if the user goes to
  // Tab 1-4, changes something, then comes back, calculationStale never re-evaluated.
  // This effect re-checks the dirty flag whenever the scenario prop changes identity
  // (i.e. whenever the parent writes a new scenario object after any tab modification).
  useEffect(() => {
    if (isDirty() && !!scenario?.casualty_projections) {
      setCalculationStale(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario]);

  // L5-01: Logistics projection from localStorage (written by Tab3 Save & Continue via L-3)
  const [logisticsProjection, setLogisticsProjection] = useState(null);

  // Keep projectionsRef current on every render so flushState always reads latest value
  projectionsRef.current = projections;
  // scenarioRef: stable ref for scenario — used inside useEffect that depends only on
  // scenario.casualty_projections. Adding scenario directly would cause infinite re-renders.
  const scenarioRef = useRef(scenario);
  scenarioRef.current = scenario;
  // ENH-031: gate ref — prevents StrictMode double-fire and repeated auto-MC on Tab3 re-saves
  const autoMCFiredRef = useRef(false);
  
  const [expandedBrigade, setExpandedBrigade] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // ENH-COA-GUARD-001 + ENH-COA-LOAD-001: CoA slot state — read from localStorage on mount
  // and refresh after each save. Holds { saved_at, label, name, cas } or null per slot.
  const [coaSlots, setCoaSlots] = useState(() => {
    const readSlot = (key) => {
      try {
        const d = lsRead(key);
        if (!d) return null;
        return {
          saved_at: d.saved_at,
          label: d.label,
          name: d.name,
          cas: d.results?.by_corps?.total_casualties ?? null
        };
      } catch (_e) { return null; }
    };
    return {
      1: readSlot('psp_cre_coa_1'),
      2: readSlot('psp_cre_coa_2')
    };
  });
  // Overwrite confirmation dialog state: { slot, label } | null
  const [coaOverwriteDialog, setCoaOverwriteDialog] = useState(null);

  // T5-011: DOW tooltip dialog
  const [dowDialogOpen, setDowDialogOpen] = useState(false);
  
  // T5-003: Active chart tab
  // BUG-110 FIX: Show all days  -  default true (no truncation). Toggle available for
  // very long operations where the user wants a compact view.

  // ============================================================================
  // DERIVED METADATA - v2.6.0 Uses accessor functions
  // ============================================================================
  
  const scenarioMetadata = useMemo(() => 
    getScenarioMetadata(projections, scenario),
    [projections, scenario]
  );
  
  const calculatorVersion = useMemo(() => 
    getCalculatorVersion(projections),
    [projections]
  );

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const validatePrerequisites = useCallback(() => {
    const errors = [];
    
    if (!scenario?.force?.forceBuilderResult) {
      errors.push('Tab 1 not complete: No force structure defined');
    } else {
      const fbr = scenario.force.forceBuilderResult;
      if (!fbr.brigades || fbr.brigades.length === 0) {
        errors.push('Tab 1 incomplete: No brigades in force structure');
      }
    }
    
    // BUG-PREREQ-002 FIX: Tab 2 operational config is saved to operation_parameters,
    // not into forceBuilderResult.brigades entries. Check operation_parameters directly.
    if (!scenario?.operation_parameters) {
      errors.push('Tab 2 not complete: No operation parameters defined');
    } else {
      const op = scenario.operation_parameters;
      const duration = op.duration_days || op.corps_config?.total_duration_days;
      if (!duration || duration <= 0) {
        errors.push('Tab 2 incomplete: Operation duration not set');
      } else if (!op.configured) {
        errors.push('Tab 2 not complete: Operation configuration not saved — use Save & Continue in Tab 2');
      } else if (!op.operational_posture && !op.operation_type) {
        errors.push('Tab 2 incomplete: Operational posture not configured');
      }
    }
    
    if (!scenario?.medical_configuration) {
      errors.push('Tab 3 recommended: Medical configuration improves accuracy');
    }
    
    return errors;
  }, [scenario]);

  // Load existing projections if available
  useEffect(() => {
    if (scenario?.casualty_projections) {
      // BUG-143 FIX: Tab3 Save & Continue writes casualty_projections WITHOUT monte_carlo
      // ({final:false} run). Previously setProjections fired unconditionally, wiping the
      // monte_carlo field from state every time Tab3 was saved after a Tab5 MC calculation.
      // Guard: accept incoming value only when it is a full result (has .version, set by
      // Tab5 or complete runs) OR when we have no current projections (initial load).
      // If current state has monte_carlo and incoming lacks .version, preserve current state.
      // Manual RECALCULATE (line 1596 below) always overwrites — that path is unchanged.
      //
      // ESLint exhaustive-deps fix (v4.18.0): read projections via projectionsRef.current
      // instead of the projections state variable. Adding projections to the dep array
      // would cause an infinite loop (effect sets projections → projections changes →
      // effect re-fires). projectionsRef.current is always in sync (line ~1241) and is
      // a stable ref — ESLint does not require refs in dependency arrays.
      const incoming = scenario.casualty_projections;
      const isFullResult    = !!incoming?.version;
      const incomingHasMC   = !!incoming?.monte_carlo;
      const currentHasMC    = !!projectionsRef.current?.monte_carlo;

      // BUG-160 FIX: Guard now explicitly protects MC state from being overwritten
      // by a non-MC full result (has .version but no .monte_carlo).
      // Root cause: `if (isFullResult || ...)` accepted ANY versioned result, including
      // {final:false} runs from Tab3 Save & Continue (has .version, no .monte_carlo).
      // If one of those fires after a Tab5 MC calculation the MC data was wiped,
      // causing MC panel to be absent until the second RECALCULATE press.
      //
      // New logic:
      //   1. No current state  → accept anything (initial load)
      //   2. Incoming has MC   → always accept (freshest complete result, e.g. RECALCULATE)
      //   3. Full result, no MC, no current MC → accept (first calculation, MC disabled)
      //   4. Partial result (no version), no current MC → accept (Tab3 preview)
      //   else: current has MC, incoming lacks MC → PRESERVE (never downgrade)
      if (!projectionsRef.current) {
        setProjections(incoming);
        setCalculationComplete(true);
        // Store baseline so Tab6 has data immediately (no RECALCULATE required)
        storeBaseline(scenarioRef.current, incoming);
      } else if (incomingHasMC) {
        // Always accept MC results — best possible data
        setProjections(incoming);
        setCalculationComplete(true);
        storeBaseline(scenarioRef.current, incoming);
      } else if (isFullResult && !currentHasMC) {
        // Non-MC full result, no MC to protect — accept
        setProjections(incoming);
        setCalculationComplete(true);
        storeBaseline(scenarioRef.current, incoming);
      } else if (!isFullResult && !currentHasMC) {
        // Partial result (no version), no MC yet — accept
        setProjections(incoming);
        setCalculationComplete(true);
        storeBaseline(scenarioRef.current, incoming);
      }
      // else: current has MC and incoming lacks MC — preserve current MC state
    }
  }, [scenario?.casualty_projections]); // BUG-030 FIX: Only depend on scenario projection, not projections state

  // ENH-031: Auto-run Monte Carlo on Tab5 mount when user has enabled MC in Tab2 and
  // casualty_projections exists but lacks .monte_carlo (Tab3 Save & Continue runs {final:false}).
  // autoMCFiredRef prevents StrictMode double-fire and repeated triggers on Tab3 re-saves.
  // When auto-run completes, projections.monte_carlo is populated → condition false → no loop.
  useEffect(() => {
    const mcEnabled = scenarioRef.current?.operation_parameters?.monte_carlo_config?.enabled;
    if (!mcEnabled) return;                                // MC not enabled in Tab2 — do nothing
    if (!projectionsRef.current) return;                  // no projections yet
    if (projectionsRef.current.monte_carlo) return;       // already has MC — do nothing
    if (autoMCFiredRef.current) return;                   // already triggered this mount cycle
    autoMCFiredRef.current = true;
    runCasualtyCalculator();
  }, [scenario?.casualty_projections, scenario?.operation_parameters?.monte_carlo_config?.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // E-01 / L5-01: Load logistics projection from localStorage.
  // Tab4 Save & Continue writes 'psp_logistics_projection' (ARCH-009  -  Tab4 is sole owner).
  // Re-fires when Tab3 recalculates (scenario.calculated_at changes) so Tab5 picks up
  // fresh projection whenever either Tab3 (casualties) or Tab4 (logistics) is re-saved.
  useEffect(() => {
    const proj = lsRead('psp_logistics_projection');
    setLogisticsProjection(proj);
  }, [scenario?.calculated_at, scenario?.logistics?.logistics_saved_at]); // HIGH-1: Re-fires on Tab3 Recalculate (calculated_at) AND Tab4 Save (logistics_saved_at)

  // BUG-030 FIX: Track previous validation errors to prevent infinite loop
  const prevValidationRef = useRef(null);
  
  // Report validation to parent
  // IMP-001: Clear CoA A chip when user unlocks ORBAT from Tab 1
  useEffect(() => {
    const handler = () => setCoaSlots(prev => ({ ...prev, 1: null }));
    window.addEventListener('psp:orbatUnlocked', handler);
    return () => window.removeEventListener('psp:orbatUnlocked', handler);
  }, []);

  useEffect(() => {
    const errors = validatePrerequisites();
    const blockingErrors = errors.filter(e =>
      e.includes('Tab 1 not complete') || e.includes('Tab 2 not complete')
    );
    
    // BUG-030 FIX: Only call onValidationChange if errors actually changed
    const currentErrorsStr = JSON.stringify(blockingErrors);
    if (prevValidationRef.current !== currentErrorsStr) {
      prevValidationRef.current = currentErrorsStr;
      if (onValidationChange) {
        onValidationChange(blockingErrors);
      }
    }
  }, [validatePrerequisites, onValidationChange]);

  // ============================================================================
  // DERIVED DATA (T5-002, T5-007, T5-009, T5-010)
  // ============================================================================
  
  /**
   * T5-002: Calculate logistics impact summary
   */
  const logisticsImpact = useMemo(() => {
    if (!projections || !scenario) return null;
    
    const medReq = projections.medical_requirements || {};
    
    // STEP 1 FIX: Restore corps variable for casualty context
    const corps = projections.by_corps || {};
    
    // v2.20.1 FIX: DOS cards show the BUFFER (target_mdos_days = what remains after operation ends).
    // The previous by_category.*.days_remaining = opDuration + targetMDOS = total stock duration.
    // A planner who sets "7 days buffer" expects to see "7 days" in the DOS cards, not "17 days".
    // target_mdos_days is the single correct figure for all supply categories (all stocked to same buffer).

    // L5-02: Blood DOS  -  buffer remaining after op = target_mdos_days
    const bloodDOS = logisticsProjection?.target_mdos_days ?? null;

    // L5-03: Class VIII DOS  -  same buffer target applies to all non-blood consumables
    const supplyDOS = logisticsProjection?.target_mdos_days ?? null;

    // L5-04/L5-07 RESTORED: Surgical DOS  -  computed from surgical team counts in scenario.
    // supplyChainModule sets surgical.days_remaining = null (no team data available there).
    // The v2.19.4 inline computation is the correct and only source for this value.
    // FST/SURG_CO constants match SURGICAL_TEAM_COMPOSITION in Tab3.
    const surgicalHoursReq = medReq.surgical?.hours || 0;
    const opDuration = projections?.scenario_summary?.duration_days
      || scenario?.operation_parameters?.duration_days || 0;
    const surgTeams = scenario?.medical_configuration?.surgical_config?.teams;
    const FST_PROC_PER_DAY   = 10;   // SURGICAL_TEAM_COMPOSITION.FORWARD_SURGICAL_TEAM.capability.procedures_per_day
    const SURGCO_PROC_PER_DAY = 24;  // SURGICAL_TEAM_COMPOSITION.SURGICAL_COMPANY.capability.procedures_per_day
    const HOURS_PER_PROCEDURE = 2;   // NATO DCS standard
    const surgCapPerDay = surgTeams != null
      ? ((surgTeams.FST?.count || 0) * FST_PROC_PER_DAY +
         (surgTeams.SURG_CO?.count || 0) * SURGCO_PROC_PER_DAY) * HOURS_PER_PROCEDURE
      : null;
    const dailySurgDemand = opDuration > 0 ? surgicalHoursReq / opDuration : 0;
    // BUG-FST-001 FIX: distinguish null (Tab 3 not saved) from 0 (Tab 3 saved, 0 teams).
    // Old: surgCapPerDay=0 → surgicalDOS=null → "N/A — Complete Tab 3" (wrong: Tab 3 IS done)
    // New: surgCapPerDay=0 (teams configured but count=0) → surgicalDOS=0 → chip shows
    //       "RED - Add FST teams (Tab 3)" (correct action message).
    // null preserved for surgTeams===null (Tab 3 genuinely not saved yet).
    const surgicalDOS = surgCapPerDay === null
      ? null                         // Tab 3 not saved
      : surgCapPerDay === 0
        ? 0                          // Tab 3 saved, no surgical teams configured
        : dailySurgDemand === 0
          ? opDuration               // no surgical demand → full coverage
          : surgCapPerDay >= dailySurgDemand
            ? opDuration
            : Math.floor((surgCapPerDay / dailySurgDemand) * opDuration);
    
    // CASEVAC usage from projections
    const casevacData = projections.evacuation_analysis?.casevac || {};
    const casevacUsed = casevacData.patients_evacuated || 0;
    const medevacUsed = projections.evacuation_analysis?.medevac?.patients_evacuated || 0;
    
    // STEP 1 FIX: Calculate casualty context for display
    const t1Casualties = corps.total_wia_t1 || Math.round((corps.total_wia || 0) * 0.15);
    const t2Casualties = corps.total_wia_t2 || Math.round((corps.total_wia || 0) * 0.25);
    const t1t2Total = t1Casualties + t2Casualties;
    const bloodUnitsRequired = medReq.blood?.total_units || 0;
    const unitsPerCasualty = t1t2Total > 0 ? (bloodUnitsRequired / t1t2Total).toFixed(1) : 0;
    
    // Surgical context
    const surgicalCases = Math.round((corps.total_wia || 0) * 0.70); // ~70% need surgery
    // BUG-058 FIX: Calculator returns .hours not .total_hours
    const surgicalHoursRequired = medReq.surgical?.hours || 0;
    const hoursPerCase = surgicalCases > 0 ? (surgicalHoursRequired / surgicalCases).toFixed(1) : 0;
    
    return {
      bloodDOS,
      supplyDOS,
      surgicalDOS,
      minDOS: Math.min(...[bloodDOS, supplyDOS, surgicalDOS].filter(d => d !== null)),
      casevacUsed,
      medevacUsed,
      totalEvac: casevacUsed + medevacUsed,
      casevacPercent: (casevacUsed + medevacUsed) > 0 
        ? Math.round((casevacUsed / (casevacUsed + medevacUsed)) * 100) 
        : 0,
      bloodRequired: medReq.blood?.total_units || 0,
      // BUG-058 FIX: Calculator returns .hours not .total_hours
      surgicalHours: medReq.surgical?.hours || 0,
      // STEP 1 FIX: Casualty context for UI display
      casualtyContext: {
        t1t2Total,
        unitsPerCasualty,
        surgicalCases,
        hoursPerCase
      }
    };
  }, [projections, scenario, logisticsProjection]);

  /**
   * T5-007: Calculate force sustainability indicators
   */
  const sustainabilityIndicators = useMemo(() => {
    if (!projections || !scenario) return null;
    
    const corps = projections.by_corps || {};
    const totalStrength = corps.total_strength || scenario.force?.forceBuilderResult?.total_strength || 0;
    const totalCasualties = corps.total_all_sources || corps.total_casualties || 0;
    const rtd = corps.total_rtd || 0;
    const netLoss = totalCasualties - rtd;
    
    // Calculate combat effectiveness
    const remainingStrength = totalStrength - netLoss;
    const effectivenessPercent = totalStrength > 0 
      ? Math.round((remainingStrength / totalStrength) * 100)
      : 100;
    
    // Medical capacity utilization
    const medAnalysis = projections.medical_system_analysis || {};
    const goldenHour = medAnalysis.overall_golden_hour_percent || 0;
    const bedUtilization = medAnalysis.bed_utilization_percent || 0;
    
    // Supply sustainability - use scenarioMetadata for duration
    // v2.19.5: No fallback  -  if minDOS is null (Tab4 not saved), supplyAdequate = false (correct)
    const minDOS = logisticsImpact?.minDOS ?? null;
    const durationDays = scenarioMetadata.duration ?? null;
    const supplyAdequate = minDOS !== null && durationDays !== null && minDOS >= durationDays;
    
    return {
      totalStrength,
      totalCasualties,
      rtd,
      netLoss,
      remainingStrength,
      effectivenessPercent,
      goldenHour,
      bedUtilization,
      minDOS,
      durationDays,
      supplyAdequate,
      overallStatus: effectivenessPercent >= 70 && goldenHour >= 60 && supplyAdequate 
        ? 'SUSTAINABLE' 
        : effectivenessPercent >= 50 
          ? 'MARGINAL' 
          : 'CRITICAL'
    };
  }, [projections, scenario, logisticsImpact, scenarioMetadata.duration]);

  /**
   * T5-009: G1 Personnel Request Summary
   */
  const g1Summary = useMemo(() => {
    if (!projections) return null;
    
    const corps = projections.by_corps || {};
    const totalRTD = corps.total_rtd || 0;
    const totalCasualties = corps.total_casualties || 0;
    const totalWIA = corps.total_wia || 0;
    const totalDNBI = corps.total_dnbi || 0;
    
    // Net loss = casualties - RTD (mathematically correct)
    const netLoss = totalCasualties - totalRTD;
    
    // Personnel replacement needs by category
    const kiaReplace = corps.total_kia || 0;
    const dowReplace = corps.total_dow || 0;
    
    // BUG FIX: Calculate actual permanent losses using RTD
    // Calculator provides total_rtd but not breakdown by WIA/DNBI
    // Estimate RTD breakdown proportionally to casualties
    const totalWoundedAndIll = totalWIA + totalDNBI;
    const wiaRTD = totalWoundedAndIll > 0 
      ? Math.round(totalRTD * (totalWIA / totalWoundedAndIll))
      : 0;
    const dnbiRTD = totalWoundedAndIll > 0
      ? Math.round(totalRTD * (totalDNBI / totalWoundedAndIll))
      : 0;
    
    // Permanent losses = those who don't RTD
    const wiaLongTerm = totalWIA - wiaRTD;
    const dnbiLongTerm = totalDNBI - dnbiRTD;
    
    // Verification: totalReplacement should equal netLoss
    const totalReplacement = kiaReplace + dowReplace + wiaLongTerm + dnbiLongTerm;
    
    return {
      totalReplacement,
      breakdown: {
        kia: kiaReplace,
        dow: dowReplace,
        wia_permanent: wiaLongTerm,
        dnbi_permanent: dnbiLongTerm
      },
      priority: {
        immediate: kiaReplace + dowReplace, // Dead - immediate replacement
        shortTerm: wiaLongTerm, // 30-90 day recovery, plan now
        longTerm: dnbiLongTerm // Variable timeline
      },
      rtd: totalRTD,
      netLoss
    };
  }, [projections]);


  // ============================================================================
  // CALCULATOR EXECUTION
  // ============================================================================

  const runCasualtyCalculator = async () => {
    setIsCalculating(true);
    setCalculationError(null);
    
    try {
      
      // Validate prerequisites
      const errors = validatePrerequisites();
      const blockingErrors = errors.filter(e => 
        e.includes('Tab 1 not complete') || e.includes('Tab 2 not complete')
      );
      
      if (blockingErrors.length > 0) {
        throw new Error(blockingErrors.join('\n'));
      }
      
      // *** CALL THE IMPORTED CALCULATOR ***
      // PHASE 2: Pass { final: true } to trigger Monte Carlo if enabled in Tab2
      const calculatedProjections = calculateCasualties(scenario, { final: true });

      // T45-001 FIX: Check for calculator validation failure BEFORE setting projections
      if (calculatedProjections.success === false) {
        const errorMessages = calculatedProjections.errors?.join('\n') || 'Calculator validation failed';
        console.error('Calculator validation failed:', calculatedProjections.errors);
        throw new Error(`Calculator validation failed:\n${errorMessages}`);
      }
      
      // T45-001 FIX: Also check if we got actual data
      if (!calculatedProjections.by_corps || 
          (calculatedProjections.by_corps.total_kia === 0 && 
           calculatedProjections.by_corps.total_wia === 0 &&
           calculatedProjections.by_corps.total_dow === 0)) {
        // Don't throw error, but log warning - could be legitimate for small/safe operations
      }
      
      setProjections(calculatedProjections);
      setCalculationComplete(true);
      setCalculatedInTab5(true); // PRELIM-001: dismiss "Preliminary data" banner

      // calculationService: preserve baseline for Tab 6 COA planning
      // BUG-FF-012 FIX: storeBaseline can fail (localStorage quota exceeded).
      // Previously the warning snackbar was immediately overwritten by the success
      // snackbar in the same render cycle — user never saw the failure.
      // Now a single conditional snackbar is set after onUpdate so it is never clobbered.
      // BUG-FF-013 FIX: Generate a single shared timestamp so storeBaseline and
      // onUpdate both write the same calculated_at value. Previously storeBaseline
      // received scenario.calculated_at from the PREVIOUS run, then onUpdate wrote a
      // NEW timestamp into App state. Tab 6's staleness detector fired immediately on
      // every load (new !== old) → permanent "Baseline Out of Date" warning.
      const newCalculatedAt = new Date().toISOString();
      const scenarioForBaseline = { ...scenario, calculated_at: newCalculatedAt };
      const baselineStored = storeBaseline(scenarioForBaseline, calculatedProjections);
      clearDirty();
      setCalculationStale(false);

      // CRIT-007 FIX: Only pass what Tab 5 owns, NOT entire scenario
      if (onUpdate) {
        onUpdate({
          casualty_projections: calculatedProjections,
          calculation_results: calculatedProjections, // For reportFormatters compatibility
          calculated_at: newCalculatedAt  // same timestamp as stored in baseline
        });
      }

      // Single snackbar — success if baseline saved, persistent warning if not.
      setSnackbar({
        open: true,
        message: baselineStored
          ? 'Calculations complete! Select reports to export.'
          : '⚠️ Calculations complete, but baseline could not be saved — scenario data (~5 MB) exceeds browser storage limit. Use "Load Current Configuration" in Tab 6 to build variants. Full Tab 6 comparison requires V3.1 storage fix.',
        severity: baselineStored ? 'success' : 'warning'
      });
      
      // BUG FIX: Do NOT auto-navigate to Tab 6 after calculation
      // Let user review results first. Tab completion only triggered by explicit "Proceed" button.
      
    } catch (error) {
      console.error('Calculation error:', error);
      setCalculationError(error.message);
      setSnackbar({
        open: true,
        message: ` Calculation failed: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsCalculating(false);
    }
  };

  // (ENH-030: Report generation functions moved to Tab 6)

  // ============================================================================
  // BUG-RPT-005: Download Situation Snapshot — plain-text Medical Estimate export
  // ============================================================================

  const handleDownloadSnapshot = useCallback(() => {
    if (!projections) return;

    const meta    = getScenarioMetadata(projections, scenario);
    const corps   = projections.by_corps || {};
    const dur     = meta.duration || scenario?.operation_parameters?.duration_days || '?';
    const ts      = new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
    const fName   = scenario?.force?.corps?.name
                 || scenario?.force?.division?.name
                 || scenario?.force?.brigades?.[0]?.name
                 || [scenario?.force?.nationalStandard, scenario?.force?.echelon].filter(Boolean).join(' ')
                 || 'Unknown Unit';
    const echelon = scenario?.force?.echelon || '?';
    const strength = (scenario?.force?.totalStrength || 0).toLocaleString();

    const pad = (label, value, width = 34) => `${label.padEnd(width)}${value}`;

    const lines = [
      'PSP/CRE TOOL — MEDICAL ESTIMATE SNAPSHOT',
      `Generated: ${ts}`,
      '='.repeat(54),
      '',
      pad('Scenario:', fName),
      pad('Echelon:', echelon),
      pad('Standard:', humanizeNatStd(meta.standard) || '?'),
      pad('Duration:', `${dur} days`),
      pad('Total Strength:', `${strength} personnel`),
      pad('Brigades:', String(meta.brigades || '?')),
      '',
      'CASUALTY SUMMARY (cumulative, all sources)',
      '-'.repeat(54),
      pad('KIA  (Killed in Action):', String(corps.total_kia  || 0)),
      pad('DOW  (Died of Wounds):', String(corps.total_dow  || 0)),
      pad('WIA  (Wounded in Action):', String(corps.total_wia  || 0)),
      pad('DNBI (Disease & Non-Battle Inj):', String(corps.total_dnbi || 0)),
      pad('RTD  (Return to Duty):', String(corps.total_rtd  || 0)),
      pad('Evacuated (total):', String(corps.total_evacuated || 0)),
      pad('All sources (total):', String(corps.total_all_sources || 0)),
    ];

    // Medical system analysis (if present)
    const msa = projections.medical_system_analysis || {};
    const r2bMascal = msa.r2b_analysis?.mascal_events_count ?? msa.r2b_system_summary?.mascal_events_count ?? null;
    const r3Mascal  = msa.r3_analysis?.mascal_events_count  ?? msa.r3_system_summary?.mascal_events_count  ?? null;
    if (r2bMascal !== null || r3Mascal !== null) {
      lines.push('');
      lines.push('MEDICAL SYSTEM');
      lines.push('-'.repeat(54));
      if (r2bMascal !== null) lines.push(pad('R2B MASCAL events:', String(r2bMascal)));
      if (r3Mascal  !== null) lines.push(pad('R3  MASCAL events:', String(r3Mascal)));
    }

    // Logistics snapshot (if present)
    const lp = projections.logistics_projection;
    const sas = lp?.supply_adequacy_summary;
    if (sas) {
      lines.push('');
      lines.push('LOGISTICS (Day 1)');
      lines.push('-'.repeat(54));
      if (sas.materiel_dos_day1 !== undefined)
        lines.push(pad('Medical Materiel DOS:', `${Number(sas.materiel_dos_day1).toFixed(1)} days`));
      if (sas.blood_dos_day1 !== undefined)
        lines.push(pad('Blood DOS:', `${Number(sas.blood_dos_day1).toFixed(1)} days`));
    }

    lines.push('');
    lines.push('='.repeat(54));
    lines.push('PSP/CRE Tool v3  |  AJMedP-1 Medical Estimate');
    lines.push('PLANNING TOOL OUTPUT — NOT A FORMAL NATO DOCUMENT');

    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `MedEstimate_Snapshot_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setSnackbar({ open: true, message: 'Casualty line (CASREP) downloaded.', severity: 'success' });
  }, [projections, scenario]);

  // ============================================================================
  // BUG-T35: Full Medical Estimate export — G1 replacement, G4 DOS/convoys,
  // facility status, sustainability verdict. Companion to CASREP button above.
  // ============================================================================

  const handleDownloadFullEstimate = useCallback(() => {
    if (!projections) return;

    const meta     = getScenarioMetadata(projections, scenario);
    const dur      = meta.duration || scenario?.operation_parameters?.duration_days || '?';
    const ts       = new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
    const fName    = scenario?.force?.corps?.name
                  || scenario?.force?.division?.name
                  || scenario?.force?.brigades?.[0]?.name
                  || [scenario?.force?.nationalStandard, scenario?.force?.echelon].filter(Boolean).join(' ')
                  || 'Unknown Unit';
    const echelon  = scenario?.force?.echelon || '?';
    const strength = (scenario?.force?.totalStrength || 0).toLocaleString();

    const pad  = (label, value, width = 36) => `${label.padEnd(width)}${value}`;
    const sep  = (char = '-', n = 60) => char.repeat(n);
    const nl   = '';

    const verdictColor = (v) => v === 'SUSTAINABLE' ? 'GREEN' : v === 'MARGINAL' ? 'AMBER' : 'RED';

    // ── Header ──────────────────────────────────────────────────────────────
    const lines = [
      'PSP/CRE TOOL — FULL MEDICAL ESTIMATE',
      `Generated: ${ts}`,
      sep('=', 60),
      nl,
      pad('Formation:', fName),
      pad('Echelon:', echelon),
      pad('Standard:', humanizeNatStd(meta.standard) || '?'),
      pad('Duration:', `${dur} days`),
      pad('Total Strength:', `${strength} personnel`),
      pad('Brigades:', String(meta.brigades || '?')),
      nl,
    ];

    // ── SECTION 1 — G1 Replacement Timeline ─────────────────────────────────
    lines.push('SECTION 1 — G1 PERSONNEL REPLACEMENT ESTIMATE');
    lines.push(sep());
    if (g1Summary) {
      lines.push(pad('Net personnel loss:', String(g1Summary.netLoss)));
      lines.push(pad('  KIA (immediate replacement):', String(g1Summary.breakdown.kia)));
      lines.push(pad('  DOW (immediate replacement):', String(g1Summary.breakdown.dow)));
      lines.push(pad('  WIA non-RTD (short-term 30-90d):', String(g1Summary.breakdown.wia_permanent)));
      lines.push(pad('  DNBI non-RTD (variable timeline):', String(g1Summary.breakdown.dnbi_permanent)));
      lines.push(pad('  RTD (return to unit):', String(g1Summary.rtd)));
      lines.push(pad('Total replacement requirement:', String(g1Summary.totalReplacement)));
    } else {
      lines.push('  No G1 data available — run calculator first.');
    }
    lines.push(nl);

    // Per-brigade breakdown
    const brigades = projections.by_brigade || [];
    if (brigades.length > 0) {
      lines.push('  Per-Brigade Breakdown:');
      // BUG-S102-023 FIX: column widened from 24→32 so full brigade names are not truncated.
      lines.push(`  ${'Brigade'.padEnd(30)} ${'KIA'.padStart(5)} ${'DOW'.padStart(5)} ${'WIA'.padStart(6)} ${'DNBI'.padStart(6)} ${'RTD'.padStart(6)} ${'NetLoss'.padStart(8)}`);
      lines.push(`  ${sep('-', 68)}`);
      for (const bde of brigades) {
        // BUG-S81-001 FIX: calculator outputs bde.totals.kia (not bde.totals.total_kia)
        const bc    = bde.totals || bde;
        const bKia  = bc.kia  || bc.total_kia  || 0;
        const bDow  = bc.dow  || bc.total_dow  || 0;
        const bWia  = bc.wia  || bc.total_wia  || 0;
        const bDnbi = bc.dnbi || bc.total_dnbi || 0;
        const bRtd  = bc.rtd  || bc.total_rtd  || 0;
        const bNet  = (bKia + bDow + bWia + bDnbi) - bRtd;
        // BUG-S102-023 FIX: truncate at 28 chars (was 22) to match wider column.
        const bName = (bde.brigade_name || bde.name || `Bde ${bde.brigade_id || '?'}`).substring(0, 28);
        lines.push(`  ${bName.padEnd(30)} ${String(bKia).padStart(5)} ${String(bDow).padStart(5)} ${String(bWia).padStart(6)} ${String(bDnbi).padStart(6)} ${String(bRtd).padStart(6)} ${String(bNet).padStart(8)}`);
      }
    }
    lines.push(nl);

    // ── SECTION 2 — G4 Supply & Logistics ───────────────────────────────────
    lines.push('SECTION 2 — G4 SUPPLY & LOGISTICS');
    lines.push(sep());
    if (logisticsImpact) {
      const bDOS = logisticsImpact.bloodDOS;
      const sDOS = logisticsImpact.supplyDOS;
      const xDOS = logisticsImpact.surgicalDOS;
      const minD = logisticsImpact.minDOS;
      lines.push(pad('Blood DOS (buffer):', bDOS !== null ? `${Number(bDOS).toFixed(1)} days` : 'N/A — complete Tab 4'));
      lines.push(pad('Medical Materiel DOS (buffer):', sDOS !== null ? `${Number(sDOS).toFixed(1)} days` : 'N/A — complete Tab 4'));
      lines.push(pad('Surgical Capacity DOS:', xDOS !== null ? `${Number(xDOS).toFixed(0)} days` : 'N/A — complete Tab 3'));
      lines.push(pad('Limiting DOS (minimum):', minD !== null && minD !== Infinity ? `${Number(minD).toFixed(1)} days` : 'N/A'));
      lines.push(pad('Blood units required:', String(logisticsImpact.bloodRequired)));
      lines.push(pad('Surgical hours required:', String(logisticsImpact.surgicalHours)));
    } else {
      lines.push('  No logistics data — run calculator first.');
    }

    // Convoys (from transport projection if available)
    const transport = projections.transport_requirements || projections.logistics_projection?.transport || null;
    if (transport?.summary) {
      lines.push(nl);
      lines.push('  Resupply Convoys:');
      lines.push(pad('  Total ground convoys:', String(transport.summary.total_ground_convoys || 0)));
      lines.push(pad('  Total vehicle-hours:', String(Math.round(transport.summary.total_vehicle_hours || 0))));
    }
    lines.push(nl);

    // ── SECTION 3 — Facility Status ──────────────────────────────────────────
    lines.push('SECTION 3 — MEDICAL FACILITY STATUS');
    lines.push(sep());
    const msa = projections.medical_system_analysis || {};
    const facilities = scenario?.medical_configuration?.facilities || [];

    // BUG-S102-022 FIX: msa.bed_utilization_percent does not exist in calculator output.
    // Derive peak utilization per echelon from mascal_analysis (the data IS computed but
    // lives in mascal_analysis.brigades[].peak_pct, r2e_divisions[].peak_pct, r3_corps.peak_pct).
    // Role 1 facilities are treatment-point evacuate-forward — no meaningful "bed util".
    const _ma = projections.mascal_analysis || {};
    const _r2bUtil = (() => {
      const b = _ma.brigades || [];
      if (!b.length) return null;
      return Math.round(b.reduce((s, x) => s + (x.peak_pct || 0), 0) / b.length);
    })();
    const _r2eUtil = (() => {
      const d = _ma.r2e_divisions || [];
      if (!d.length) return null;
      return Math.round(d.reduce((s, x) => s + (x.peak_pct || 0), 0) / d.length);
    })();
    const _r3Util  = _ma.r3_corps?.peak_pct != null ? Math.round(_ma.r3_corps.peak_pct) : null;
    const _echelonUtil = (type) => {
      if (!type || type === 'ROLE1')          return 'N/A';
      if (type === 'ROLE2_BASIC')             return _r2bUtil != null ? `${_r2bUtil}%` : '?';
      if (type === 'ROLE2_ENHANCED')          return _r2eUtil != null ? `${_r2eUtil}%` : '?';
      if (type === 'ROLE3')                   return _r3Util  != null ? `${_r3Util}%`  : '?';
      return '?';
    };

    if (facilities.length > 0) {
      lines.push(`  ${'Facility'.padEnd(28)} ${'Echelon'.padStart(14)} ${'Beds'.padStart(6)} ${'Peak%'.padStart(6)}`);
      lines.push(`  ${sep('-', 58)}`);
      for (const f of facilities) {
        const ech   = f.echelon || f.type || '?';
        const beds  = f.beds || f.total_beds || 0;
        const util  = _echelonUtil(f.type || f.echelon);
        const fname = (f.name || f.facility_name || ech).substring(0, 26);
        lines.push(`  ${fname.padEnd(28)} ${ech.padStart(14)} ${String(beds).padStart(6)} ${util.padStart(6)}`);
      }
    } else {
      lines.push('  No facility data — complete Tab 3.');
    }
    if (msa.overall_golden_hour_percent != null || _r2bUtil != null) {
      lines.push(nl);
      if (msa.overall_golden_hour_percent != null) lines.push(pad('  Golden Hour success:', `${Math.round(msa.overall_golden_hour_percent)}%`));
      if (_r2bUtil  != null) lines.push(pad('  R2B peak bed occupancy (avg):', `${_r2bUtil}%`));
      if (_r2eUtil  != null) lines.push(pad('  R2E peak bed occupancy (avg):', `${_r2eUtil}%`));
      if (_r3Util   != null) lines.push(pad('  R3  peak bed occupancy:', `${_r3Util}%`));
    }
    lines.push(nl);

    // ── SECTION 4 — Sustainability Verdict ──────────────────────────────────
    lines.push('SECTION 4 — SUSTAINABILITY VERDICT');
    lines.push(sep());
    if (sustainabilityIndicators) {
      const si      = sustainabilityIndicators;
      const verdict = verdictColor(si.overallStatus);
      lines.push(`  OVERALL STATUS: ${verdict} — ${si.overallStatus}`);
      lines.push(nl);
      lines.push(pad('  Combat effectiveness:', `${si.effectivenessPercent}%`));
      lines.push(pad('  Golden Hour success:', si.goldenHour ? `${Math.round(si.goldenHour)}%` : 'N/A'));
      lines.push(pad('  Supply adequate for duration:', si.supplyAdequate ? 'YES' : 'NO'));
      lines.push(pad('  Min DOS vs operation duration:', `${si.minDOS !== null ? si.minDOS.toFixed(1) : '?'} / ${si.durationDays ?? '?'} days`));
      lines.push(pad('  Remaining strength:', `${(si.remainingStrength || 0).toLocaleString()} of ${(si.totalStrength || 0).toLocaleString()}`));
      lines.push(nl);
      if (verdict === 'RED') {
        lines.push('  >> CRITICAL: Force below 50% effectiveness. Immediate reinforcement required.');
      } else if (verdict === 'AMBER') {
        lines.push('  >> MARGINAL: Monitor closely. Plan replacement and resupply actions.');
      } else {
        lines.push('  >> Force sustainable within planned parameters.');
      }
    } else {
      lines.push('  No data — run calculator first.');
    }
    lines.push(nl);

    // ── Footer ───────────────────────────────────────────────────────────────
    lines.push(sep('=', 60));
    lines.push('PSP/CRE Tool v3  |  AJMedP-1 Medical Estimate');
    lines.push('PLANNING TOOL OUTPUT — NOT A FORMAL NATO DOCUMENT');

    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `MedEstimate_Full_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setSnackbar({ open: true, message: 'Full Medical Estimate downloaded.', severity: 'success' });
  }, [projections, scenario, g1Summary, logisticsImpact, sustainabilityIndicators]);

  // ============================================================================
  // V4-TAB6-001: Save current scenario+results as CoA A or B for Tab 6 comparison
  // Writes to psp_cre_coa_1 / psp_cre_coa_2 (read by Tab 6 renderCoAComparison)
  // ============================================================================

  // ENH-COA-GUARD-001 FIX: doSaveAsCoa performs the actual write; handleSaveAsCoa checks
  // for existing slot first and prompts overwrite confirmation if needed.
  const doSaveAsCoa = useCallback((slot) => {
    if (!projections || !scenario) return;
    const key   = slot === 1 ? 'psp_cre_coa_1' : 'psp_cre_coa_2';
    const label = slot === 1 ? 'CoA A' : 'CoA B';
    const cas   = projections?.by_corps?.total_casualties ?? null;
    const payload = {
      snapshot: scenario,
      results:  projections,
      saved_at: new Date().toISOString(),
      label,
      name: scenario?.operation_parameters?.operation_name || label
    };
    try {
      lsWrite(key, payload);
      // BUG-COA-001 FIX: notify Tab6 (always-mounted) via custom DOM event so it can
      // refresh its coaSlot1/coaSlot2 state without waiting for a remount.
      window.dispatchEvent(new CustomEvent('psp:coaSaved', { detail: { slot, payload } }));
      // Refresh slot display state
      setCoaSlots(prev => ({
        ...prev,
        [slot]: { saved_at: payload.saved_at, label, name: payload.name, cas }
      }));
      setSnackbar({ open: true, message: `${label} saved — open Tab 6 \u203a Phase 3 to compare`, severity: 'success' });
    } catch (_e) {
      setSnackbar({ open: true, message: `Could not save ${label} — browser storage limit reached. Try clearing Tab 6 variants first.`, severity: 'error' });
    }
  }, [projections, scenario]);

  const handleSaveAsCoa = useCallback((slot) => {
    if (!projections || !scenario) return;
    // ENH-COA-GUARD-001: if slot already has data, ask for confirmation before overwriting
    if (coaSlots[slot]) {
      setCoaOverwriteDialog({ slot, label: slot === 1 ? 'CoA A' : 'CoA B' });
      return;
    }
    doSaveAsCoa(slot);
  }, [projections, scenario, coaSlots, doSaveAsCoa]);

  // ============================================================================
  // RENDER: Prerequisites Check
  // ============================================================================

  const renderPrerequisitesCheck = () => {
    const errors = validatePrerequisites();
    const hasBlockingErrors = errors.some(e => 
      e.includes('Tab 1 not complete') || e.includes('Tab 2 not complete')
    );
    
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color={hasBlockingErrors ? 'error' : 'success'} />
          {t('tab5.prereq.title')}
        </Typography>
        
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            {/* BUG-PREREQ-001 FIX: chip condition now matches validatePrerequisites() */}
            <Alert
              severity={(() => {
                const fbr = scenario?.force?.forceBuilderResult;
                if (!fbr || !fbr.brigades || fbr.brigades.length === 0) return 'error';
                return 'success';
              })()}
              icon={(() => {
                const fbr = scenario?.force?.forceBuilderResult;
                if (!fbr || !fbr.brigades || fbr.brigades.length === 0) return <ErrorIcon />;
                return <CheckCircleIcon />;
              })()}
              sx={{ py: 0.5 }}
            >
              {t('tab5.prereq.tab1_force')}
            </Alert>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            {/* BUG-PREREQ-002 FIX: check operation_parameters directly, not brigade fields */}
            <Alert
              severity={(() => {
                const op = scenario?.operation_parameters;
                if (!op) return 'error';
                const duration = op.duration_days || op.corps_config?.total_duration_days;
                if (!duration || duration <= 0) return 'error';
                if (!op.configured) return 'error';
                return 'success';
              })()}
              icon={(() => {
                const op = scenario?.operation_parameters;
                if (!op) return <ErrorIcon />;
                const duration = op.duration_days || op.corps_config?.total_duration_days;
                if (!duration || duration <= 0) return <ErrorIcon />;
                if (!op.configured) return <ErrorIcon />;
                return <CheckCircleIcon />;
              })()}
              sx={{ py: 0.5 }}
            >
              {t('tab5.prereq.tab2_ops')}
            </Alert>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            {/* BUG-84-015: check for at least one Role 2B configured, not just that
                medical_configuration exists. Without ≥1 R2B all medical outputs are zero.
                BUG-S97-003 FIX: also check per_facility_evacuation_assets (modern path) —
                old mtf_structure.role2.count is zero when facility builder is used. */}
            {(() => {
              const hasMedConfig = !!scenario?.medical_configuration;
              const hasR2BLegacy = (scenario?.medical_configuration?.mtf_structure?.role2?.count ?? 0) > 0;
              const hasR2BFacility = (scenario?.medical_configuration?.per_facility_evacuation_assets || [])
                .some(f => f.facility_type === 'ROLE2_BASIC' || f.facility_type === 'ROLE_2_BASIC');
              const hasR2B = hasR2BLegacy || hasR2BFacility;
              const tab3Ok = hasMedConfig && hasR2B;
              return (
                <Alert
                  severity={tab3Ok ? 'success' : 'warning'}
                  icon={tab3Ok ? <CheckCircleIcon /> : <WarningAmberIcon />}
                  sx={{ py: 0.5 }}
                >
                  {hasMedConfig && !hasR2B ? t('tab5.prereq.tab3_medical_no_r2b') : t('tab5.prereq.tab3_medical')}
                </Alert>
              );
            })()}
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Alert 
              severity={!scenario?.logistics ? 'warning' : 'success'}
              icon={!scenario?.logistics ? <WarningAmberIcon /> : <CheckCircleIcon />}
              sx={{ py: 0.5 }}
            >
              {t('tab5.prereq.tab4_logistics')}
            </Alert>
          </Grid>
        </Grid>
        
        {errors.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {errors.map((err, idx) => (
              <Alert 
                key={idx} 
                severity={err.includes('not complete') ? 'error' : 'warning'}
                sx={{ mb: 1 }}
              >
                {err}
              </Alert>
            ))}
          </Box>
        )}
      </Paper>
    );
  };

  // ============================================================================
  // RENDER: Calculate Section
  // ============================================================================

  const renderCalculateSection = () => {
    const errors = validatePrerequisites();
    const hasBlockingErrors = errors.some(e => 
      e.includes('Tab 1 not complete') || e.includes('Tab 2 not complete')
    );
    
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalculateIcon color="primary" />
          {t('tab5.calc.title')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('tab5.calc.description')}
        </Typography>

        {/* ENH-WORKFLOW-001: Stale guard — prominent error when results are outdated */}
        {calculationStale && calculationComplete && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>{t('tab5.stale_warning')}</AlertTitle>
            {t('tab5.calc.stale_body')}
          </Alert>
        )}

        {/* ENH-031: Auto-MC progress banner — visible when MC is enabled, calculating, and no MC data yet */}
        {isCalculating && scenario?.operation_parameters?.monte_carlo_config?.enabled && !projections?.monte_carlo && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Monte Carlo auto-calculating</AlertTitle>
            Monte Carlo is enabled in Tab 2. Running {scenario?.operation_parameters?.monte_carlo_config?.iterations || 1000} iterations
            automatically — this takes approximately 5 seconds. Results will appear when complete.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={isCalculating ? <CircularProgress size={20} color="inherit" /> : <CalculateIcon />}
            onClick={runCasualtyCalculator}
            disabled={hasBlockingErrors || isCalculating}
            sx={{ minWidth: 200 }}
          >
            {isCalculating ? t('common.calculating') : calculationComplete ? t('common.recalculate') : t('common.calculate')}
          </Button>
          
          {calculationComplete && (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                setProjections(null);
                setCalculationComplete(false);
              }}
            >
              {t('tab5.calc.clear_results')}
            </Button>
          )}
          {/* BUG-RPT-005 / BUG-T35: Casualty line CASREP + Full Medical Estimate exports */}
          {calculationComplete && (
            <Tooltip title={t('tab5.calc.casrep_tooltip')}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<SaveAltIcon />}
                onClick={handleDownloadSnapshot}
              >
                {t('tab5.calc.casrep_btn')}
              </Button>
            </Tooltip>
          )}
          {calculationComplete && (
            <Tooltip title={t('tab5.calc.full_estimate_tooltip')}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<SaveAltIcon />}
                onClick={handleDownloadFullEstimate}
              >
                {t('tab5.calc.full_estimate_btn')}
              </Button>
            </Tooltip>
          )}
          {/* V4-TAB6-001 + ENH-WORKFLOW-001 + ENH-COA-GUARD-001: Save as CoA */}
          {calculationComplete && (
            <Tooltip title={calculationStale
              ? 'Recalculate first — stale results cannot be saved as CoA'
              : coaSlots[1]
                ? `Overwrite CoA A (saved ${new Date(coaSlots[1].saved_at).toLocaleTimeString()})`
                : 'Save this scenario as Course of Action A — compare in Tab 6 \u203a Phase 3'
            }>
              <span>
                <Button
                  variant={coaSlots[1] ? 'outlined' : 'contained'}
                  color={coaSlots[1] ? 'success' : 'primary'}
                  startIcon={<SaveAltIcon />}
                  onClick={() => handleSaveAsCoa(1)}
                  disabled={calculationStale}
                >
                  {coaSlots[1] ? `\u2713 CoA A saved` : t('tab5.coa_save_a')}
                </Button>
              </span>
            </Tooltip>
          )}
          {calculationComplete && (
            <Tooltip title={calculationStale
              ? 'Recalculate first — stale results cannot be saved as CoA'
              : !coaSlots[1]
                ? 'Save as CoA A first — CoA B requires a baseline to compare against'
                : coaSlots[2]
                  ? `Overwrite CoA B (saved ${new Date(coaSlots[2].saved_at).toLocaleTimeString()})`
                  : 'Save this scenario as Course of Action B — compare in Tab 6 \u203a Phase 3'
            }>
              <span>
                <Button
                  variant={coaSlots[2] ? 'outlined' : 'contained'}
                  color={coaSlots[2] ? 'success' : 'primary'}
                  startIcon={<SaveAltIcon />}
                  onClick={() => handleSaveAsCoa(2)}
                  disabled={calculationStale || !coaSlots[1]}
                >
                  {coaSlots[2] ? `\u2713 CoA B saved` : t('tab5.coa_save_b')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        {/* OBS-S102-019: CoA B workflow guide — visible only after CoA A saved but before CoA B saved */}
        {coaSlots[1] && !coaSlots[2] && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.light', borderRadius: 1 }}>
            <Typography variant="caption" color="info.dark" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              CoA A saved — next steps to create a comparable CoA B:
            </Typography>
            <Typography variant="caption" color="text.secondary" component="ol" sx={{ m: 0, pl: 2 }}>
              <li>Return to <strong>Tab 2 (Operations)</strong> — change phases, posture, or sub-phase mission type</li>
              <li>If you changed corps-level phases: click <strong>Force Cascade</strong> (amber button in Corps Configuration) to push the new phases to all divisions and brigades — otherwise subordinate units keep CoA A overrides</li>
              <li>Or adjust medical / logistics in Tabs 3–4</li>
              <li>Come back here and <strong>Recalculate</strong> (button above)</li>
              <li>Click <strong>Save as CoA B</strong> — the comparison will then reflect real differences</li>
            </Typography>
            <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.5 }}>
              ⚠ Saving CoA B without Force Cascade after phase changes will produce identical or near-identical results in Tab 6.
            </Typography>
          </Box>
        )}

        {/* ENH-COA-LOAD-001: CoA Manager panel — shows slot contents, with clear action */}
        {(coaSlots[1] || coaSlots[2]) && (
          <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              CoA Manager — saved snapshots (open Tab 6 &rsaquo; Phase 3 to compare)
            </Typography>
            <Grid container spacing={2}>
              {[1, 2].map(slot => {
                const s = coaSlots[slot];
                const slotLabel = slot === 1 ? 'CoA A' : 'CoA B';
                return (
                  <Grid key={slot} size={{ xs: 12, sm: 6 }}>
                    {s ? (
                      <Box sx={{ p: 1.5, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.main', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {slotLabel}: {s.name || slotLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Saved: {new Date(s.saved_at).toLocaleString()}
                          {s.cas != null ? ` \u2022 ${s.cas} total casualties` : ''}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => {
                              try {
                                localStorage.removeItem(slot === 1 ? 'psp_cre_coa_1' : 'psp_cre_coa_2');
                              } catch (_e) { /* noop */ }
                              setCoaSlots(prev => ({ ...prev, [slot]: null }));
                              setSnackbar({ open: true, message: `${slotLabel} cleared`, severity: 'info' });
                            }}
                          >
                            Clear {slotLabel}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', border: '1px dashed', borderColor: 'grey.400', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">{slotLabel}: empty</Typography>
                      </Box>
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* ENH-WORKFLOW-002: CoA B Delta panel — parameters changed since CoA A was saved */}
        {coaSlots[1] && !coaSlots[2] && (() => {
          // Read full CoA A snapshot from localStorage (not abbreviated coaSlots[1])
          let coaAScenario = null;
          try { coaAScenario = lsRead('psp_cre_coa_1')?.snapshot; } catch (_e) { /* noop */ }
          if (!coaAScenario || !scenario) return null;

          const opA = coaAScenario.operation_parameters || {};
          const opB = scenario.operation_parameters || {};

          // Parameters to diff: [label, pathA, pathB, formatter]
          const diffs = [];
          const chk = (label, a, b, fmt = String) => {
            const va = a ?? null;
            const vb = b ?? null;
            if (JSON.stringify(va) !== JSON.stringify(vb)) {
              diffs.push({ label, from: va != null ? fmt(va) : 'not set', to: vb != null ? fmt(vb) : 'not set' });
            }
          };

          chk('Operation name',      opA.operation_name,    opB.operation_name);
          chk('Duration',            opA.duration_days,     opB.duration_days,     v => `${v} days`);
          chk('Posture',             opA.posture,           opB.posture);
          chk('Primary operation',   opA.primary_operation, opB.primary_operation);
          chk('Environment',         opA.environment,       opB.environment);
          chk('Threat level',        opA.threat_level,      opB.threat_level);
          chk('Build-up days',       opA.buildup_config?.duration_days, opB.buildup_config?.duration_days, v => `${v} days`);
          chk('Build-up enabled',    opA.buildup_config?.enabled, opB.buildup_config?.enabled, v => v ? 'yes' : 'no');
          chk('Force size (total)', coaAScenario.force?.forceBuilderResult?.total_strength, scenario.force?.forceBuilderResult?.total_strength, v => `${v} personnel`);
          // Medical config summary
          const medA = coaAScenario.medical_configuration || {};
          const medB = scenario.medical_configuration || {};
          chk('R3 accessible',       medA.r3_accessible,   medB.r3_accessible,   v => v ? 'yes' : 'no');
          chk('MRoE enabled',        medA.mroe_config?.enabled, medB.mroe_config?.enabled, v => v ? 'yes' : 'no');

          if (diffs.length === 0) return null;
          return (
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'warning.main', borderRadius: 1, bgcolor: 'warning.50' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                CoA B Delta — {diffs.length} parameter{diffs.length !== 1 ? 's' : ''} changed since CoA A was saved
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                These changes define what is being compared. Save as CoA B when ready.
              </Typography>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <thead>
                  <tr>
                    <Box component="th" sx={{ textAlign: 'left', pb: 0.5, fontWeight: 600, width: '30%', borderBottom: '1px solid', borderColor: 'divider' }}>Parameter</Box>
                    <Box component="th" sx={{ textAlign: 'left', pb: 0.5, fontWeight: 600, width: '35%', borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>CoA A</Box>
                    <Box component="th" sx={{ textAlign: 'left', pb: 0.5, fontWeight: 600, width: '35%', borderBottom: '1px solid', borderColor: 'divider' }}>Current (CoA B)</Box>
                  </tr>
                </thead>
                <tbody>
                  {diffs.map(d => (
                    <tr key={d.label}>
                      <Box component="td" sx={{ py: 0.5, pr: 1 }}>{d.label}</Box>
                      <Box component="td" sx={{ py: 0.5, pr: 1, color: 'text.secondary', fontStyle: 'italic' }}>{d.from}</Box>
                      <Box component="td" sx={{ py: 0.5, fontWeight: 600 }}>{d.to}</Box>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          );
        })()}

        {calculationError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>Calculation Error</AlertTitle>
            {calculationError}
          </Alert>
        )}
        
        {isCalculating && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('tab5.calc.running_progress')}
            </Typography>
          </Box>
        )}
      </Paper>
    );
  };

  // ============================================================================
  // RENDER: Results Dashboard (v2.6.0 - Using accessor functions)
  // ============================================================================

  // RESEARCH-REPORTS-002: AJMedP-1 Medical Estimate — 6-step coverage panel
  // Replaces the single-line caption with a structured step-coverage table.
  // Educational: students see which steps the tool covers and what they must provide.
  const renderMedicalEstimateSteps = () => {
    const STEPS = [
      {
        num: 1,
        name: 'Mission Analysis',
        coverage: 'PARTIAL',
        desc: 'Operational task, duration, posture, national standard.',
        source: 'Tab 2 — mission type, operational posture, phases, duration'
      },
      {
        num: 2,
        name: 'Medical Threat Assessment',
        coverage: 'FULL',
        desc: 'DNBI rates, climate, environment, endemic disease burden.',
        source: 'dnbiModule.js — terrain, weather, climate zone, HNS absorption'
      },
      {
        num: 3,
        name: 'Opposing Forces',
        coverage: 'PARTIAL',
        desc: 'Enemy quality, threat level, weapon systems (wound pattern distribution).',
        source: 'Tab 2 — enemy assessment. Full G2 product is planner responsibility.'
      },
      {
        num: 4,
        name: 'Friendly Medical Capabilities',
        coverage: 'FULL',
        desc: 'MTF hierarchy, bed census, surgical capacity, MEDEVAC assets, evacuation chain.',
        source: 'Tab 3 — MTF config. casualtyCalculator.js — bed timeline, Golden Hour.'
      },
      {
        num: 5,
        name: 'Analysis of COAs',
        coverage: 'TAB6',
        desc: 'Side-by-side comparison of medical support options and their outcomes.',
        source: 'Tab 6 — Medical Plan Variant Analysis (sensitivity analysis, variant comparison)'
      },
      {
        num: 6,
        name: 'Recommendations',
        coverage: 'PARTIAL',
        desc: 'Recommended COA with rationale; medical shortfalls and risk statement.',
        source: 'Tab 6 — MEDSITREP download + Annex Q. Final judgement: planner.'
      },
    ];

    const coverageColor = (c) =>
      c === 'FULL' ? 'success' : c === 'TAB6' ? 'primary' : 'warning';
    const coverageLabel = (c) =>
      c === 'FULL' ? 'Tool — Full' : c === 'TAB6' ? 'Tab 6' : 'Partial / Planner';

    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'primary.light' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
            Medical Estimate — AJMedP-1 Six-Step Process
          </Typography>
          <Chip label="AJMedP-1" size="small" color="primary" variant="outlined" />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Formal MSP (OPORD Annex Q) produced in Tab 6
          </Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.75rem' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ textAlign: 'center', padding: '4px 8px', borderBottom: '2px solid #e0e0e0', width: 32 }}>#</th>
                <th style={{ textAlign: 'left',   padding: '4px 8px', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' }}>Step</th>
                <th style={{ textAlign: 'center', padding: '4px 8px', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' }}>Coverage</th>
                <th style={{ textAlign: 'left',   padding: '4px 8px', borderBottom: '2px solid #e0e0e0' }}>What the tool provides</th>
                <th style={{ textAlign: 'left',   padding: '4px 8px', borderBottom: '2px solid #e0e0e0', color: '#666' }}>Source</th>
              </tr>
            </thead>
            <tbody>
              {STEPS.map(s => (
                <tr key={s.num} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ textAlign: 'center', padding: '4px 8px', fontWeight: 'bold', color: '#1565c0' }}>{s.num}</td>
                  <td style={{ padding: '4px 8px', whiteSpace: 'nowrap', fontWeight: 500 }}>{s.name}</td>
                  <td style={{ textAlign: 'center', padding: '4px 6px' }}>
                    <Chip
                      label={coverageLabel(s.coverage)}
                      size="small"
                      color={coverageColor(s.coverage)}
                      variant={s.coverage === 'FULL' ? 'filled' : 'outlined'}
                      sx={{ fontSize: '0.65rem', height: 18 }}
                    />
                  </td>
                  <td style={{ padding: '4px 8px', color: '#333' }}>{s.desc}</td>
                  <td style={{ padding: '4px 8px', color: '#777', fontStyle: 'italic' }}>{s.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Steps 1-4 produce the Medical Estimate (this tab). Step 5 is in Tab 6 Medical Plan Variant Analysis.
          Step 6 output: MEDSITREP + Annex Q download (Tab 6). Planner judgement required throughout.
        </Typography>
      </Paper>
    );
  };

  const renderResultsDashboard = () => {
    if (!projections) return null;
    
    const corps = projections.by_corps || {};
    const medReq = projections.medical_requirements || {};
    const medAnalysis = projections.medical_system_analysis || {};
    
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3, position: 'relative' }}>
        {/* ENH-WORKFLOW-001: Semi-transparent stale overlay — blocks results from being read as current */}
        {calculationStale && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 10,
            bgcolor: 'rgba(255,255,255,0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 1
          }}>
            <Alert severity="error" sx={{ maxWidth: 480, boxShadow: 3 }}>
              <AlertTitle>Stale Results</AlertTitle>
              Parameters changed since last calculation. Recalculate before reading these values.
            </Alert>
          </Box>
        )}
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon color={calculationStale ? 'disabled' : 'primary'} />
          {t('tab5.results.title')}
          {calculationStale && (
            <Chip label="STALE" size="small" color="error" sx={{ ml: 1 }} />
          )}
          {/* v2.6.0: Show calculator version dynamically */}
          <Chip
            label={`Calculator v${calculatorVersion}`}
            size="small"
            variant="outlined"
            sx={{ ml: 'auto' }}
          />
        </Typography>
        
        {/* PRELIM-001: Preliminary data banner — shown when projections exist but came from a
            {final:false} preview run (Tab 3 / Tab 4 Save & Continue), not from the full
            Tab 5 RECALCULATE. Detection: full results always carry a .version field;
            preview results do not. Banner auto-dismisses when RECALCULATE completes. */}
        {projections && !calculatedInTab5 && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ mb: 2, borderRadius: 1 }}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>
              Preliminary data — Full calculation &amp; Monte Carlo (if enabled in Tab 2)not run yet
            </AlertTitle>
            These figures are a rapid preview generated during scenario configuration.
            Click <strong>RECALCULATE</strong> above to run the full 18-step SABReS algorithm
            with Monte Carlo uncertainty analysis and establish the Tab 6 baseline.
          </Alert>
        )}

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5">{formatNumber(corps.total_all_sources || corps.total_casualties)}</Typography>
                <Typography variant="caption">{t('tab5.results.total_casualties')}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ textAlign: 'center', bgcolor: 'error.main', color: 'white' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5">{formatNumber(corps.total_kia)}</Typography>
                <Typography variant="caption">{t('tab5.results.kia')}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            {/* QM-WIA-DOW-DISPLAY: total_wia = WIA Survivors (originalWIA − DOW).
                When medical care improves → DOW ↓ → WIA Survivors ↑ = good outcome.
                Sub-note shows DOW so planner sees: Total Wounded = WIA Survivors + DOW. */}
            <Card sx={{ textAlign: 'center', bgcolor: 'warning.main', color: 'white' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5">{formatNumber(corps.total_wia)}</Typography>
                <Typography variant="caption">{t('tab5.results.wia_survivors')}</Typography>
                {corps.total_dow > 0 && (
                  <Typography variant="caption" display="block"
                    sx={{ fontSize: '0.6rem', opacity: 0.85, mt: 0.25 }}>
                    DOW: {formatNumber(corps.total_dow)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ textAlign: 'center', bgcolor: 'info.main', color: 'white' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5">{formatNumber(corps.total_dnbi)}</Typography>
                <Typography variant="caption">{t('tab5.results.dnbi')}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ textAlign: 'center', bgcolor: `${getStatusColor(medAnalysis.overall_golden_hour_percent || 0)}.main`, color: 'white' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5">{medAnalysis.overall_golden_hour_percent ?? 'N/A'}%</Typography>
                <Typography variant="caption">{t('tab5.results.golden_hour')}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ textAlign: 'center' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5" color="primary">{formatNumber(medReq.blood?.total_units || 0)}</Typography>
                <Typography variant="caption">{t('tab5.results.blood_units')}</Typography>
                {/* PHASE 1.2: Show WBB contribution if activated */}
                {projections.medical_requirements?.wbb && projections.medical_requirements.wbb.total_units_contributed > 0 && (
                  <Typography variant="caption" display="block" sx={{ fontSize: '0.65rem', color: 'error.main', mt: 0.5 }}>
                    (-{formatNumber(projections.medical_requirements.wbb.total_units_contributed)} WBB)
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* GROUP-1-C: WIA:KIA Ratio Validation Warning (Military Medicine 2022) */}
        {(() => {
          const totalWIA = corps.total_wia || 0;
          const totalKIA = corps.total_kia || 0;
          if (totalKIA === 0 || totalWIA === 0) return null;
          const ratio = totalWIA / totalKIA;
          const isHighWarning = ratio > WIA_KIA_RATIO_HIGH_WARNING;
          const isLowWarning = ratio < WIA_KIA_RATIO_LOW_WARNING;
          if (!isHighWarning && !isLowWarning) return null;
          return (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle2" gutterBottom>
                WIA:KIA Ratio Outside Peer-Combat Range
              </Typography>
              <Typography variant="body2">
                Calculated ratio: <strong>{ratio.toFixed(1)}:1</strong> WIA per KIA.
                Expected range for peer-combat / LSCO: <strong>{WIA_KIA_RATIO_PEER_MIN}:1 to {WIA_KIA_RATIO_PEER_MAX}:1</strong>.
              </Typography>
              {isHighWarning && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>High ratio ({ratio.toFixed(1)}:1)</strong> is closer to COIN/stabilization operations
                  ({WIA_KIA_RATIO_COIN}:1 Iraq/Afghanistan; {WIA_KIA_RATIO_PEACEKEEPING}:1 peacekeeping per Mil Med 2022).
                  For a peer-combat scenario, verify intensity factors, engagement levels, and medical
                  care effectiveness settings. A ratio this high may indicate the scenario has COIN-era
                  assumptions rather than peer-combat lethality.
                </Typography>
              )}
              {isLowWarning && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Low ratio ({ratio.toFixed(1)}:1)</strong> indicates unusually high battlefield lethality.
                  Peer-combat historical range is {WIA_KIA_RATIO_PEER_MIN}:1 to {WIA_KIA_RATIO_PEER_MAX}:1.
                  Verify MEDEVAC coverage, Role 1 medical manning, and TCCC implementation settings in Tabs 2 and 3.
                </Typography>
              )}
              <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                Source: Military Medicine 2022, Vol. 187(3-4):e313. This is a soft advisory — no action is blocked.
              </Typography>
            </Alert>
          );
        })()}

        {/* PHASE 5.2: Tactical Evacuation Capacity Card */}
        {(() => {
          // Pull representative values from last day of first brigade result
          const firstBrigade = projections.by_brigade?.[0];
          const lastDay = firstBrigade?.by_day?.[firstBrigade.by_day.length - 1];
          const surgeonPct = lastDay?.resources?.surgeon_performance_pct;
          const mtpApplied = projections.by_brigade?.some(b =>
            b.by_day?.some(d => d.resources?.mtp_protocol_applied)
          );
          const mtpProtocol = projections.by_brigade
            ?.flatMap(b => b.by_day || [])
            .find(d => d.resources?.mtp_protocol)?.resources?.mtp_protocol;
          const hasShelfLifeWarning = projections.by_brigade?.some(b =>
            b.by_day?.some(d => d.resources?.shelf_life?.shelf_life_warning)
          );
          const stratevacCap = projections.medical_requirements?.stratevac_capacity;
          if (surgeonPct == null && !mtpApplied && !hasShelfLifeWarning && !stratevacCap) return null;
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {/* Surgeon performance */}
              {surgeonPct != null && (
                <Chip
                  size="small"
                  label={`Surgeon performance: ${surgeonPct}%`}
                  color={surgeonPct >= 90 ? 'success' : surgeonPct >= 75 ? 'warning' : 'error'}
                  variant="outlined"
                />
              )}
              {/* MTP protocol */}
              {mtpApplied && (
                <Chip
                  size="small"
                  label={`MTP ${mtpProtocol || '1:1:1'} active`}
                  color="error"
                  variant="outlined"
                />
              )}
              {/* Shelf life warning */}
              {hasShelfLifeWarning && (
                <Chip
                  size="small"
                  label="PLT shelf life warning"
                  color="warning"
                  variant="outlined"
                />
              )}
              {/* STRATEVAC class */}
              {stratevacCap && (
                <Chip
                  size="small"
                  label={`STRATEVAC ${stratevacCap.class}${stratevacCap.farp_enabled ? ' + FARP' : ''}  -  ${stratevacCap.effective_hourly_throughput} litter/hr`}
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>
          );
        })()}
        {(() => {
          const tacEvacSummary = getTacticalEvacSummary(scenario);
          if (!tacEvacSummary) return null;
          const { ad_label, ad_modifier, weather_label, weather_modifier,
                  combined_pct, fleet, air, warnings } = tacEvacSummary;
          const chipColor = combined_pct >= 80 ? 'success' : combined_pct >= 50 ? 'warning' : 'error';
          return (
            <Paper elevation={2} sx={{ p: 2.5, mb: 3, border: '2px solid', borderColor: `${chipColor}.main` }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Tactical MEDEVAC  -  Evacuation Capacity
                <Chip label={`${combined_pct}% air availability`} size="small" color={chipColor} />
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* AD Threat */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">AD Threat</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{ad_label}</Typography>
                      <Typography variant="caption">{Math.round(ad_modifier * 100)}% sortie rate</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                {/* Weather */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Weather</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{weather_label}</Typography>
                      <Typography variant="caption">{Math.round(weather_modifier * 100)}% sortie rate</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                {/* Air Sorties */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card variant="outlined" sx={{ bgcolor: `${chipColor}.50` }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">MEDEVAC Sorties/Day</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: `${chipColor}.dark` }}>
                        {air.effective_daily_sorties}
                      </Typography>
                      <Typography variant="caption">
                        {air.nominal_daily_sorties} nominal &rarr; {air.degradation_pct}% degraded
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                {/* Fleet Utilization */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Ground Fleet</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {fleet.available_vehicles} / {fleet.total_vehicles} vehicles
                      </Typography>
                      <Typography variant="caption">{fleet.utilization_pct}% available</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Air pool info */}
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Air Pool:</strong> {air.pool_label}
              </Typography>

              {/* Warnings */}
              {warnings.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {warnings.map((w, i) => (
                    <Alert key={i} severity="warning" sx={{ mb: 0.5, py: 0.5 }}>
                      <Typography variant="body2">{w}</Typography>
                    </Alert>
                  ))}
                </Box>
              )}
            </Paper>
          );
        })()}

        {/* OBS-S96-006: R1 Ground Evacuation Fleet */}
        {(() => {
          const fleet = projections?.by_corps?.r1_fleet;
          if (!fleet || fleet.total.all === 0) return null;
          const { total, by_nation } = fleet;
          const heloCapacity = projections?.by_corps?.r1_helo_capacity || 0;
          const groundCapacity = total.all * 4 * 2; // 4 trips/day × 2 litter (matches calculator)
          const totalCapacity  = groundCapacity + heloCapacity;
          // BUG-S102-017 FIX: duration_days may be undefined when projections are freshly
          // re-hydrated. Fallback chain: by_corps → scenario prop → 1.
          // Without this, Math.max(1, undefined || 1) = 1, making dailyDemand = total_wia
          // (cumulative over the whole operation shown as a /day figure).
          const _dur = projections?.by_corps?.duration_days ||
                       scenario?.operation_parameters?.duration_days || 1;
          const dailyDemand    = Math.round(
            (projections?.by_corps?.total_wia || 0) / Math.max(1, _dur)
          );
          const adequate = totalCapacity >= dailyDemand;
          const nations  = Object.entries(by_nation).filter(([, c]) => c.armored + c.wheeled > 0);
          return (
            <Paper elevation={2} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: adequate ? 'success.light' : 'warning.light' }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                Ground Evacuation Fleet (R1 organic + attached)
                <Chip
                  size="small"
                  label={adequate ? 'Adequate' : 'Shortfall'}
                  color={adequate ? 'success' : 'warning'}
                />
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: nations.length > 1 ? 1 : 0 }}>
                <Typography variant="body2">
                  <strong>{total.armored}</strong> armoured + <strong>{total.wheeled}</strong> wheeled = <strong>{total.all}</strong> ambulances
                  {total.helo > 0 && <span> · <strong>{total.helo}</strong> helo</span>}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Capacity: ~{groundCapacity}/day ground
                  {heloCapacity > 0 && ` + ~${heloCapacity}/day helo`}
                  {' '}· Demand: ~{dailyDemand}/day
                </Typography>
              </Box>
              {nations.length > 1 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {nations.map(([nation, c]) => (
                    <Chip
                      key={nation}
                      size="small"
                      variant="outlined"
                      label={`${nation}: ${c.armored}a + ${c.wheeled}w`}
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                  ))}
                </Box>
              )}
            </Paper>
          );
        })()}

        {/* V5-014: HLZ Capability Panel — displays HLZ infrastructure constraints on MEDEVAC */}
        {(() => {
          const hlz = projections.medical_system_analysis?.medevac?.helicopter?.hlz_analysis;
          if (!hlz) return null;
          return (
            <Paper elevation={2} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: hlz.configured ? 'primary.light' : 'warning.light' }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                HLZ Capability
                {hlz.configured
                  ? <Chip label={hlz.size || 'HLZ'} size="small" color="primary" variant="outlined" />
                  : <Chip label="No HLZ configured" size="small" color="warning" />}
              </Typography>

              {/* No HLZ warning */}
              {hlz.no_hlz_warning && (
                <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
                  <Typography variant="body2">
                    No HLZ configured in Tab 3. Helicopter MEDEVAC is calculated assuming direct POI pickup with no
                    designated landing zone — no HLZ infrastructure constraints or FARP modifiers are applied.
                    Configure an HLZ in Tab 3 to model realistic landing zone constraints.
                  </Typography>
                </Alert>
              )}

              {/* HLZ details when configured */}
              {hlz.configured && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: hlz.capacity_cap_applied || hlz.is_throughput_bottleneck ? 1.5 : 0 }}>
                  {/* Simultaneous capacity */}
                  <Chip
                    size="small"
                    label={`${hlz.capacity_helicopters} simultaneous${hlz.capacity_cap_applied ? ' — CAP ACTIVE' : ''}`}
                    color={hlz.capacity_cap_applied ? 'warning' : 'default'}
                    variant={hlz.capacity_cap_applied ? 'filled' : 'outlined'}
                  />
                  {/* FARP status */}
                  <Chip
                    size="small"
                    label={hlz.has_farp
                      ? `FARP: ${hlz.farp_mod > 1 ? '+' : ''}${Math.round((hlz.farp_mod - 1) * 100)}% sorties`
                      : `No FARP: -${Math.round((1 - hlz.farp_mod) * 100)}% sorties (RTB for fuel)`}
                    color={hlz.has_farp ? 'success' : 'warning'}
                    variant="outlined"
                  />
                  {/* Night lighting */}
                  <Chip
                    size="small"
                    label={hlz.has_nvg_lighting
                      ? 'NVG lighting: full night capability'
                      : hlz.night_lighting_mod < 1.0
                        ? `Basic lighting: -${Math.round((1 - hlz.night_lighting_mod) * 100)}% night availability`
                        : 'Basic lighting (day ops — no penalty)'}
                    color={hlz.has_nvg_lighting ? 'success' : hlz.night_lighting_mod < 1.0 ? 'warning' : 'default'}
                    variant="outlined"
                  />
                </Box>
              )}

              {/* Capacity cap warning */}
              {hlz.capacity_cap_applied && (
                <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                  <Typography variant="body2">
                    HLZ simultaneous capacity ({hlz.capacity_helicopters}) is less than configured aircraft — effective
                    available aircraft capped at {hlz.capacity_helicopters}. Excess aircraft must orbit or wait for a
                    landing pad. Upgrade to a larger HLZ or add a second HLZ to remove this constraint.
                  </Typography>
                </Alert>
              )}

              {/* Throughput bottleneck */}
              {hlz.is_throughput_bottleneck && (
                <Alert severity="error" sx={{ py: 0.5 }}>
                  <Typography variant="body2">
                    HLZ throughput bottleneck: HLZ maximum {hlz.throughput_patients_per_day} patients/day
                    (based on {hlz.size} HLZ hourly rate x 12 operating hours) is below calculated air
                    MEDEVAC demand. The HLZ is the limiting factor — additional aircraft will not help.
                    Upgrade to MEDIUM or LARGE HLZ to resolve.
                  </Typography>
                </Alert>
              )}
            </Paper>
          );
        })()}

        {/* PHASE 2: Monte Carlo Uncertainty Analysis Display */}
        {projections.monte_carlo && projections.monte_carlo.p50 && (
          <Paper elevation={3} sx={{ p: 2.5, mb: 3, bgcolor: 'info.50', border: '2px solid', borderColor: 'info.main' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'info.dark' }}>
              [MC] Casualty Uncertainty Analysis (Monte Carlo)
              <Chip 
                label={`${projections.monte_carlo.iterations || 1000} iterations`} 
                size="small" 
                color="info"
                variant="outlined"
              />
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Monte Carlo Simulation:</strong> {projections.monte_carlo.iterations || 1000} independent 
                iterations, each with randomly sampled input parameters (normal distribution centered on 1.0). 
                p5 = worst case (5% chance higher), p50 = median outcome, p95 = best case (95% chance lower).
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {t('tab5.mc.params_text')}
              </Typography>
            </Alert>
            
            {/* Percentile Cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Worst Case (p5) */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Card sx={{ bgcolor: 'error.50', border: '1px solid', borderColor: 'error.main' }}>
                  <CardContent>
                    <Typography variant="overline" color="error.dark" sx={{ fontWeight: 'bold' }}>
                      {t('tab5.mc.worst_case')}
                    </Typography>
                    <Typography variant="h4" color="error.main" sx={{ my: 1 }}>
                      {formatNumber(Math.round(projections.monte_carlo.p5.total_casualties))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      5% chance higher
                    </Typography>
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'error.light' }}>
                      <Typography variant="caption" display="block">
                        KIA: {formatNumber(Math.round(projections.monte_carlo.p5.total_kia))}
                      </Typography>
                      <Typography variant="caption" display="block">
                        WIA: {formatNumber(Math.round(projections.monte_carlo.p5.total_wia))}
                      </Typography>
                      <Typography variant="caption" display="block">
                        DNBI: {formatNumber(Math.round(projections.monte_carlo.p5.total_dnbi))}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Expected (p50 - Median) */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Card sx={{ bgcolor: 'warning.50', border: '2px solid', borderColor: 'warning.main' }}>
                  <CardContent>
                    <Typography variant="overline" color="warning.dark" sx={{ fontWeight: 'bold' }}>
                      {t('tab5.mc.expected')}
                    </Typography>
                    <Typography variant="h4" color="warning.dark" sx={{ my: 1 }}>
                      {formatNumber(Math.round(projections.monte_carlo.p50.total_casualties))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Most likely outcome
                    </Typography>
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'warning.light' }}>
                      <Typography variant="caption" display="block">
                        KIA: {formatNumber(Math.round(projections.monte_carlo.p50.total_kia))}
                      </Typography>
                      <Typography variant="caption" display="block">
                        WIA: {formatNumber(Math.round(projections.monte_carlo.p50.total_wia))}
                      </Typography>
                      <Typography variant="caption" display="block">
                        DNBI: {formatNumber(Math.round(projections.monte_carlo.p50.total_dnbi))}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Best Case (p95) */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Card sx={{ bgcolor: 'success.50', border: '1px solid', borderColor: 'success.main' }}>
                  <CardContent>
                    <Typography variant="overline" color="success.dark" sx={{ fontWeight: 'bold' }}>
                      {t('tab5.mc.best_case')}
                    </Typography>
                    <Typography variant="h4" color="success.main" sx={{ my: 1 }}>
                      {formatNumber(Math.round(projections.monte_carlo.p95.total_casualties))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      95% chance lower
                    </Typography>
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'success.light' }}>
                      <Typography variant="caption" display="block">
                        KIA: {formatNumber(Math.round(projections.monte_carlo.p95.total_kia))}
                      </Typography>
                      <Typography variant="caption" display="block">
                        WIA: {formatNumber(Math.round(projections.monte_carlo.p95.total_wia))}
                      </Typography>
                      <Typography variant="caption" display="block">
                        DNBI: {formatNumber(Math.round(projections.monte_carlo.p95.total_dnbi))}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Mean */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Card sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.400' }}>
                  <CardContent>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      {t('tab5.mc.mean')}
                    </Typography>
                    <Typography variant="h4" color="text.primary" sx={{ my: 1 }}>
                      {formatNumber(Math.round(projections.monte_carlo.mean.total_casualties))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Statistical average
                    </Typography>
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'grey.300' }}>
                      <Typography variant="caption" display="block">
                        KIA: {formatNumber(Math.round(projections.monte_carlo.mean.total_kia))}
                      </Typography>
                      <Typography variant="caption" display="block">
                        WIA: {formatNumber(Math.round(projections.monte_carlo.mean.total_wia))}
                      </Typography>
                      <Typography variant="caption" display="block">
                        DNBI: {formatNumber(Math.round(projections.monte_carlo.mean.total_dnbi))}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            {/* R2-24d: range of the deaths before surgery, with the assumptions that have no data */}
            {projections.monte_carlo.deaths_before_surgery && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'info.dark' }}>
                  {t('tab5.mc.dbs_title')}
                </Typography>
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  {[['dbs_best', 'p95', 'success.main'], ['dbs_median', 'p50', 'text.primary'], ['dbs_worst', 'p5', 'error.main']].map(([label, key, color]) => (
                    <Grid key={key} size={{ xs: 12, md: 4 }}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="overline" sx={{ fontWeight: 'bold' }}>{t(`tab5.mc.${label}`)}</Typography>
                          <Typography variant="h4" sx={{ color }}>
                            {formatNumber(Math.round(projections.monte_carlo.deaths_before_surgery[key]))}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <Alert severity="warning" sx={{ mb: 1 }}>{t('tab5.mc.dbs_note')}</Alert>
                {projections.monte_carlo.deaths_before_surgery.sensitivity && projections.monte_carlo.deaths_before_surgery.sensitivity.length > 0 && (
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{t('tab5.mc.dbs_drivers')}</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableBody>
                          {projections.monte_carlo.deaths_before_surgery.sensitivity.slice(0, 5).map((factor, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{factor.factor_name}</TableCell>
                              <TableCell align="right">
                                <Chip label={`+/-${formatPercent(factor.impact_percent)}`} size="small" color={factor.impact_percent > 15 ? 'error' : factor.impact_percent > 10 ? 'warning' : 'default'} />
                              </TableCell>
                              <TableCell>{factor.explanation}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            )}

            {/* Sensitivity Analysis */}
            {projections.monte_carlo.sensitivity && projections.monte_carlo.sensitivity.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'info.dark' }}>
                  [Chart] Sensitivity Analysis - Top Uncertainty Drivers
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('tab5.mc.sa_factor')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('tab5.mc.sa_impact')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('tab5.mc.sa_explanation')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {projections.monte_carlo.sensitivity.slice(0, 5).map((factor, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{factor.factor_name}</TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={`+/-${formatPercent(factor.impact_percent)}`}
                              size="small"
                              color={factor.impact_percent > 15 ? 'error' : factor.impact_percent > 10 ? 'warning' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {factor.explanation || 'Primary driver of outcome variance'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            
            {/* Threshold Probabilities */}
            {projections.monte_carlo.thresholds && projections.monte_carlo.thresholds.length > 0 && (
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'info.dark' }}>
                  [Trend] Threshold Probabilities - Planning Guidance
                </Typography>
                <Grid container spacing={2}>
                  {projections.monte_carlo.thresholds.map((threshold, idx) => {
                    const probability = threshold.probability_percent;
                    const color = probability > 50 ? 'error' : probability > 25 ? 'warning' : 'success';
                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
                        <Card variant="outlined" sx={{ bgcolor: `${color}.50` }}>
                          <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Probability of exceeding
                            </Typography>
                            <Typography variant="h6" color={`${color}.dark`}>
                              {formatNumber(threshold.threshold_value)} casualties
                            </Typography>
                            <Typography variant="h4" color={`${color}.main`} sx={{ mt: 0.5 }}>
                              {formatPercent(probability)}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    <strong>NATO Planning Guidance:</strong> Medical planners should resource for p5 (worst case) 
                    to ensure adequate capacity. Logistics planning should target p50 (expected) with surge capability to p5.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Paper>
        )}
        
        {/* Key Metrics - v2.6.0: Using accessor functions for metadata */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {/* BUG-052 FIX: Duration from accessor function */}
          <Chip label={`Duration: ${scenarioMetadata.duration} days`} size="small" />
          
          {/* BUG-053 FIX: Brigades from accessor function */}
          <Chip label={`Brigades: ${scenarioMetadata.brigades}`} size="small" />
          
          {/* BUG-054 FIX: Standard from accessor function */}
          <Chip label={`Standard: ${humanizeNatStd(scenarioMetadata.standard)}`} size="small" />
          
          {/* T5-011: DOW Chip with Tooltip and Info Button */}
          {/* ISSUE-05: Tooltip updated  -  clarifies (?) is a help button, not a warning flag */}
          <Tooltip 
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Died of Wounds (DOW)
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Casualties wounded in action who died before reaching definitive surgical care.
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'info.light' }}>
                  Click the (?) icon to open the DOW methodology and educational explanation.
                </Typography>
              </Box>
            }
            arrow
          >
            <Chip 
              label={`DOW: ${formatNumber(corps.total_dow)}`} 
              size="small" 
              color="error" 
              variant="outlined"
              onClick={() => setDowDialogOpen(true)}
              onDelete={() => setDowDialogOpen(true)}
              deleteIcon={<HelpOutlineIcon sx={{ fontSize: '16px !important' }} />}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
          
          <Chip label={`RTD: ${formatNumber(corps.total_rtd)}`} size="small" color="success" variant="outlined" />
          {/* BUG-058 FIX: Calculator returns .hours not .total_hours */}
          <Chip label={`Surgical: ${formatNumber(medReq.surgical?.hours || 0)}h`} size="small" variant="outlined" />

          {/* BED-OCC: Bed occupancy chip  -  v3.3.0
               BED-CHIP-FIX: Reads from scenario.logistics.bed_occupancy (written via onUpdate).
               psp_logistics_projection (localStorage) does NOT carry bed_occupancy  -  that is
               calculateLogisticsProjection() output only. scenario.logistics is the correct path.
               Tab4 Section 4D shows per-role breakdown. Chip here = quick status only. */}
          {scenario?.logistics?.bed_occupancy && (
            <Tooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Bed Occupancy</Typography>
                  <Typography variant="caption" display="block">
                    {scenario?.logistics?.bed_occupancy.steady_state_beds_required ?? '?'} of {scenario?.logistics?.bed_occupancy.total_beds ?? '?'} beds required (steady state)
                  </Typography>
                  <Typography variant="caption" display="block">
                    Avg LOS: {scenario?.logistics?.bed_occupancy.avg_length_of_stay ?? '?'} days  -  Status: {scenario?.logistics?.bed_occupancy.status}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'info.light' }}>
                    Per-echelon breakdown available in Tab 4  -  Section 4D.
                  </Typography>
                </Box>
              }
              arrow
            >
              <Chip
                icon={<LocalHospitalIcon sx={{ fontSize: '14px !important' }} />}
                label={`Beds: ${scenario?.logistics?.bed_occupancy.occupancy_rate ?? '?'}%`}
                size="small"
                color={
                  scenario?.logistics?.bed_occupancy.status === 'CRITICAL' ? 'error' :
                  scenario?.logistics?.bed_occupancy.status === 'HIGH' ? 'error' :
                  scenario?.logistics?.bed_occupancy.status === 'ELEVATED' ? 'warning' : 'success'
                }
                variant="outlined"
              />
            </Tooltip>
          )}

          {/* CASEVAC tracking  -  ARCH-010: informational only per AJMedP-2 */}
          {logisticsImpact?.casevacUsed > 0 && (
            <Tooltip title="CASEVAC tracking (informational only  -  AJMedP-2). CASEVAC is unplanned/opportunistic and is not a factor in the medical estimate. DOW modelled using unevacuated penalty for all overflow.">
              <Chip 
                icon={<DirectionsCarIcon />}
                label={`CASEVAC: ${formatNumber(logisticsImpact.casevacUsed)} (${logisticsImpact.casevacPercent}%)`} 
                size="small" 
                color="info" 
                variant="outlined" 
              />
            </Tooltip>
          )}
        </Box>
        
        {/* BUG-055 FIX: Calculated timestamp */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Calculated: {formatCalculatedAt(scenarioMetadata.timestamp)}
        </Typography>
        
        {/* Warnings - BUG-059/063 FIX: Handle both string and object formats */}
        {projections.warnings?.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>{projections.warnings.length} Warning(s)</AlertTitle>
            {projections.warnings.slice(0, 3).map((w, idx) => {
              // BUG-063: Calculator returns strings like "CRITICAL: message" or objects {type, message}
              if (typeof w === 'string') {
                return <Typography key={idx} variant="body2"> {w}</Typography>;
              }
              return <Typography key={idx} variant="body2"> [{w.type || 'INFO'}] {w.message || 'No message'}</Typography>;
            })}
            {projections.warnings.length > 3 && (
              <Typography variant="body2"><em>... and {projections.warnings.length - 3} more</em></Typography>
            )}
          </Alert>
        )}
        
        {/* CALC-010: inputs the calculation had to assume (results.assumptions from assumptionLedger) */}
        <AssumptionsPanel assumptions={projections.assumptions} />

        {/* INC-010: FORWARD FIELD HOSPITAL — R2E POSITIONING IMPACT
             Reads projections.by_brigade[*].survival_analysis.forward_fh (already computed).
             Shows per-brigade outcome of R2E Forward FH positioning: applied vs standard,
             detection mode, expert override indicator. Null-guarded — absent for old saves. */}
        {(() => {
          const brigades = projections?.by_brigade || [];
          const fhBrigades = brigades.filter(b => b.survival_analysis?.forward_fh);
          if (!fhBrigades.length) return null;

          // Determine if any brigade has an expert override active
          const anyOverride = fhBrigades.some(b => b.survival_analysis.forward_fh.detection_mode === 'EXPERT_OVERRIDE');

          return (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, mt: 1, borderColor: 'divider' }}>
              <Typography variant="caption" sx={{
                fontWeight: 700, display: 'block', mb: 1,
                textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary'
              }}>
                Forward Field Hospital — R2E Positioning Impact
              </Typography>
              {anyOverride && (
                <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                  <Typography variant="caption">
                    🔬 Expert Override active — auto-detection has been bypassed for one or more brigades.
                    Override applies to the entire exercise duration.
                  </Typography>
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {fhBrigades.map((bde, idx) => {
                  const fh = bde.survival_analysis.forward_fh;
                  const isForward = fh.applied === true;
                  const isOverride = fh.detection_mode === 'EXPERT_OVERRIDE';
                  const posKm = fh.position_km != null ? `${fh.position_km}km` : 'N/A';
                  const threshKm = fh.threshold_km ?? 40;
                  const ghBonus = fh.golden_hour_bonus_applied != null
                    ? `+${Math.round(fh.golden_hour_bonus_applied * 100)}%`
                    : null;
                  const detectionLabel = isOverride
                    ? (isForward ? 'Expert Override: FORCE FORWARD' : 'Expert Override: FORCE STANDARD')
                    : (fh.position_km != null
                        ? `Auto (${fh.position_km}km ${isForward ? '<' : '≥'} ${threshKm}km)`
                        : 'Auto (no position data)');

                  return (
                    <Box key={idx} sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap',
                      py: 0.75, borderTop: idx > 0 ? '1px solid' : 'none', borderColor: 'divider'
                    }}>
                      <Box sx={{ minWidth: 170 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                          {bde.brigade_name || bde.name || `Brigade ${idx + 1}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Evacuation chain → R2E @ {posKm}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip
                          size="small"
                          label={isForward ? '● FORWARD ACTIVE' : '○ Standard'}
                          color={isForward ? 'success' : 'default'}
                          variant={isForward ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600 }}
                        />
                        {isOverride && (
                          <Chip
                            size="small"
                            label={`🔬 ${detectionLabel}`}
                            color="warning"
                            variant="outlined"
                            sx={{ fontSize: '0.66rem', height: 20 }}
                          />
                        )}
                        {isForward && (
                          <Typography variant="caption" color="text.secondary">
                            −19% DOW{ghBonus ? ` | Golden Hour: ${ghBonus} compliance` : ''}
                          </Typography>
                        )}
                        {!isOverride && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            {detectionLabel}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          );
        })()}

        {/* Brigade Breakdown - BUG-056 FIX: Using accessor functions */}
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 600 }}>
          {t('tab5.results.brigade_breakdown')}
        </Typography>
        {/* BUG-T41: footnote clarifying HQ/Support are calculated separately below */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
          Combat brigade casualties only. HQ (division, brigade, battalion HQ staff) and support unit
          (signal, logistics, medical, transport) casualties are calculated separately — see the
          HQ &amp; Support section below.
        </Typography>
        {(projections.by_brigade || []).map((bde) => {
          const bdeType = bde.unit_type || bde.type || '';
          const isCSSBrigade = ['LOGISTICS','SIGNAL','MEDICAL','AVIATION','HQ'].includes(bdeType.toUpperCase());
          const isCombatSupport = ['ARTILLERY','ENGINEERS'].includes(bdeType.toUpperCase());
          return (
          <Accordion
            key={bde.brigade_id || bde.id || `bde-${Math.random()}`}
            expanded={expandedBrigade === (bde.brigade_id || bde.id)}
            onChange={() => setExpandedBrigade(expandedBrigade === (bde.brigade_id || bde.id) ? null : (bde.brigade_id || bde.id))}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600, minWidth: 180 }}>{bde.brigade_name || bde.name || 'Unknown Brigade'}</Typography>
                <Chip label={bdeType || 'Unknown'} size="small" variant="outlined" />
                {isCSSBrigade && <Chip label="CSS" size="small" color="warning" variant="outlined" title="Combat Service Support — modelled at reduced casualty rate (rear-area)" />}
                {isCombatSupport && <Chip label="CS" size="small" color="info" variant="outlined" title="Combat Support — modelled at reduced casualty rate" />}
                <Chip label={`${formatNumber(getBrigadeTotalCasualties(bde))} cas`} size="small" />
                <Chip label={formatPercent(getBrigadeCasualtyRate(bde))} size="small" variant="outlined" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {isCSSBrigade && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                  {t('tab5.brigade.css_note')}
                </Typography>
              )}
              {/* Cumulative totals */}
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>{t('tab5.brigade.strength')}</TableCell>
                      <TableCell align="right">{formatNumber(bde.initial_strength || bde.strength)}</TableCell>
                      <TableCell>{t('tab5.brigade.kia')}</TableCell>
                      <TableCell align="right">{formatNumber(getBrigadeTotal(bde, 'kia'))}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t('tab5.brigade.dow')}</TableCell>
                      <TableCell align="right">{formatNumber(getBrigadeTotal(bde, 'dow'))}</TableCell>
                      <TableCell>{t('tab5.brigade.wia_survivors')}</TableCell>
                      <TableCell align="right">{formatNumber(getBrigadeTotal(bde, 'wia'))}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t('tab5.brigade.dnbi')}</TableCell>
                      <TableCell align="right">{formatNumber(getBrigadeTotal(bde, 'dnbi'))}</TableCell>
                      <TableCell>{t('tab5.brigade.rtd')}</TableCell>
                      <TableCell align="right">{formatNumber(getBrigadeTotal(bde, 'rtd'))}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* OBS-S102-024: Per-day casualty timeline (D1–Dn) */}
              {Array.isArray(bde.by_day) && bde.by_day.length > 0 && (() => {
                const days = bde.by_day.slice(0, 14); // cap at 14 days for display
                // Find peak-total day for highlighting
                const peakTotal = Math.max(...days.map(d =>
                  (d.battle?.kia ?? 0) + (d.battle?.dow ?? 0) + (d.battle?.wia ?? 0) + (d.dnbi?.total ?? 0)
                ));
                return (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Daily Casualty Timeline — {bde.brigade_name || 'Brigade'}
                      {bde.by_day.length > 14 && (
                        <span style={{ fontWeight: 400, marginLeft: 8 }}>
                          (first 14 of {bde.by_day.length} days shown)
                        </span>
                      )}
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280, overflowY: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {['Day', 'KIA', 'DOW', 'WIA', 'DNBI', 'Total', 'RTD', 'T1 Surg'].map(h => (
                              <TableCell key={h} sx={{ fontWeight: 700, py: 0.5, fontSize: '0.7rem',
                                bgcolor: 'grey.100', whiteSpace: 'nowrap' }}>
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {days.map((d, i) => {
                            const kia  = Math.round(d.battle?.kia  ?? 0);
                            const dow  = Math.round(d.battle?.dow  ?? 0);
                            const wia  = Math.round(d.battle?.wia  ?? 0);
                            const dnbi = Math.round(d.dnbi?.total  ?? 0);
                            const rtd  = Math.round((d.battle?.rtd ?? 0) + (d.dnbi?.rtd ?? 0));
                            const t1   = Math.round(d.triage?.T1 ?? d.triage?.t1_immediate ?? 0);
                            const tot  = kia + dow + wia + dnbi;
                            const isPeak = tot > 0 && tot === peakTotal;
                            return (
                              <TableRow key={i} sx={{
                                bgcolor: isPeak ? 'error.50' : 'inherit',
                                '&:hover': { bgcolor: isPeak ? 'error.100' : 'action.hover' }
                              }}>
                                <TableCell sx={{ fontSize: '0.72rem', fontWeight: isPeak ? 700 : 400 }}>
                                  {dayLabel(d.day ?? i + 1)} {isPeak && '▲'}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.72rem', color: kia > 0 ? 'error.main' : 'text.secondary' }}>{kia}</TableCell>
                                <TableCell sx={{ fontSize: '0.72rem', color: dow > 0 ? 'error.main' : 'text.secondary' }}>{dow}</TableCell>
                                <TableCell sx={{ fontSize: '0.72rem' }}>{wia}</TableCell>
                                <TableCell sx={{ fontSize: '0.72rem' }}>{dnbi}</TableCell>
                                <TableCell sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{tot}</TableCell>
                                <TableCell sx={{ fontSize: '0.72rem', color: 'success.main' }}>{rtd}</TableCell>
                                <TableCell sx={{ fontSize: '0.72rem', color: t1 > 0 ? 'warning.main' : 'text.secondary' }}>{t1}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      ▲ = peak-intensity day. D+0 = first operational day. T1 = immediate surgical priority.
                    </Typography>
                  </Box>
                );
              })()}
            </AccordionDetails>
          </Accordion>
          );
        })}
      </Paper>
    );
  };

  // ============================================================================
  // T5-002: RENDER: Logistics Impact Section
  // ============================================================================

  const renderLogisticsImpact = () => {
    if (!projections || !logisticsImpact) return null;

    // L5-06: Compute national thresholds from projection's national standard.
    // Falls back to NATO defaults (minimum=5, adequate=10, optimal=15) when projection absent.
    const nationalStandard = logisticsProjection?.national_standard ?? 'NATO';
    const natConfig = getNationalLogistics(nationalStandard);
    const dosThresholds = {
      minimum:  natConfig?.mdos_lines?.MINIMUM?.days  ?? 5,
      adequate: natConfig?.mdos_lines?.ADEQUATE?.days ?? 10,
      optimal:  natConfig?.mdos_lines?.OPTIMAL?.days  ?? 15
    };

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingIcon color="primary" />
          {t('tab5.logistics_extra.title')}
        </Typography>
        
        <Grid container spacing={2}>
          {/* MDOS Status Cards */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" color="text.secondary">{t('tab5.logistics_extra.blood_products')}</Typography>
                  {/* L5-06: Pass dosThresholds for national-standard-aware color coding */}
                  <BloodtypeIcon color={logisticsImpact.bloodDOS === null ? 'disabled' : getMDOSColor(logisticsImpact.bloodDOS, dosThresholds)} />
                </Box>
                <Typography variant="h4" color={logisticsImpact.bloodDOS === null ? 'text.secondary' : `${getMDOSColor(logisticsImpact.bloodDOS, dosThresholds)}.main`}>
                  {logisticsImpact.bloodDOS !== null ? `${logisticsImpact.bloodDOS} days` : 'N/A'}
                </Typography>
                {logisticsImpact.bloodDOS !== null ? (
                  <Chip
                    label={getMDOSLabel(logisticsImpact.bloodDOS, dosThresholds)}
                    size="small"
                    color={getMDOSColor(logisticsImpact.bloodDOS, dosThresholds)}
                    sx={{ mt: 1 }}
                  />
                ) : (
                  <Chip
                    label={t('tab5.logistics_extra.data_missing')}
                    size="small"
                    color="default"
                    sx={{ mt: 1 }}
                  />
                )}
                {/* L5-05: Null guidance  -  directs user to Tab 3, not console */}
                {logisticsImpact.bloodDOS === null && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    N/A  -  Complete Tab 3 calculation first
                  </Typography>
                )}
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Required: {formatNumber(logisticsImpact.bloodRequired)} units
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  For {formatNumber(logisticsImpact.casualtyContext?.t1t2Total || 0)} T1/T2 casualties ({logisticsImpact.casualtyContext?.unitsPerCasualty || 0} units/cas)
                </Typography>
                {/* V3.1-BLOOD-STREAM2: Civilian T1 demand annotation */}
                {(() => {
                  const civStream = projections?.mascal_analysis?.mroe_load?.civilian_stream;
                  if (!civStream || !civStream.competes_with_military || !(civStream.t1_surgical_daily > 0)) return null;
                  const civPrbc = Math.round(civStream.t1_surgical_daily * 6);
                  const civFfp  = Math.round(civStream.t1_surgical_daily * 5);
                  return (
                    <Typography variant="caption" display="block" color="warning.main" sx={{ mt: 0.5 }}>
                      +{civStream.t1_surgical_daily} civilian T1/day (+~{civPrbc} PRBC, {civFfp} FFP) — MRoE Stream 2 included
                    </Typography>
                  );
                })()}
                {/* IMP-009: Per-product blood breakdown (PRBC / FFP / PLT / Cryo) */}
                {(() => {
                  const blood = projections?.medical_requirements?.blood;
                  if (!blood?.prbc) return null;
                  const dur = projections?.by_corps?.duration_days || 1;
                  const rows = [
                    { label: 'PRBC', val: blood.prbc, color: 'error'   },
                    { label: 'FFP',  val: blood.ffp,  color: 'warning' },
                    { label: 'PLT',  val: blood.plt,  color: 'primary' },
                    { label: 'Cryo', val: blood.cryo, color: 'default' },
                  ];
                  return (
                    <Box sx={{ mt: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                      <Typography variant="caption" color="text.secondary"
                        display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
                        {t('tab5.logistics_extra.by_product')}
                      </Typography>
                      {rows.map(r => (
                        <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                          <Chip label={r.label} size="small" color={r.color} variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 18, minWidth: 40 }} />
                          <Typography variant="caption">
                            {formatNumber(r.val)} u ({formatNumber(Math.round(r.val / dur))}/day)
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
                {/* IMP-005: Planned MTF displacement gaps */}
                {(() => {
                  const gaps = projections?.by_corps?.planned_displacement_gaps;
                  if (!gaps?.length) return null;
                  const CAUSE_LBL = { INDIRECT_FIRE: 'Indirect fire', ENEMY_ADVANCE: 'Enemy advance', OPERATIONAL: 'Operational' };
                  const mascalBrigades = projections?.mascal_analysis?.brigades || [];
                  return (
                    <Box sx={{ mt: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Planned MTF displacements:
                      </Typography>
                      {gaps.map((g, i) => {
                        const totalOutage = g.gap_hrs + (g.setup_ramp_hrs || 0);
                        const brigadeData = mascalBrigades.find(b => b.brigade_id === g.brigade_id);
                        const gapDayEntry = brigadeData?.timeline?.find(t => t.day === g.day);
                        const backlogPts  = Math.round(gapDayEntry?.movement_backlog || 0);
                        const drainEntry  = brigadeData?.timeline?.find(t => t.day > g.day && (t.movement_backlog || 0) < 0.5);
                        const drainDay    = drainEntry?.day ?? null;
                        return (
                          <Box key={i} sx={{ mb: 0.75 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                {g.facility_type === 'ROLE2_BASIC' ? 'R2B' : 'R2E'} · {g.phase_label} · {CAUSE_LBL[g.cause] || g.cause}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Chip size="small" label={`${g.gap_hrs}h move`} variant="outlined"
                                  color={g.gap_hrs > 4 ? 'error' : g.gap_hrs > 2 ? 'warning' : 'default'}
                                  sx={{ fontSize: '0.65rem', height: 18 }} />
                                {g.gap_hrs > 2 && <Chip size="small" color="warning" label="Blood" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />}
                                {g.gap_hrs > 4 && <Chip size="small" color="error"   label="Surg."  variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />}
                              </Box>
                            </Box>
                            {/* Total outage = move gap + AJMedP-1 setup ramp */}
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5, display: 'block', mt: 0.25 }}>
                              Total outage: {g.gap_hrs}h (displacement) + {g.setup_ramp_hrs || 0}h ({g.facility_type === 'ROLE2_BASIC' ? 'R2B' : 'R2E'} setup ramp) = <strong>{totalOutage}h</strong>
                              {backlogPts > 0 && (
                                <> · Est. backlog: <strong>~{backlogPts} pts</strong>{drainDay ? ` · drain by D+${drainDay}` : ''}</>
                              )}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()}
                {/* Phase 6.2: MTP protocol indicator */}
                {(() => {
                  const mtpDay = projections?.by_brigade
                    ?.flatMap(b => b.by_day || [])
                    .find(d => d.resources?.mtp_protocol_applied);
                  if (!mtpDay) return null;
                  return (
                    <Chip
                      size="small"
                      label={`MTP ${mtpDay.resources.mtp_protocol} active`}
                      color="error"
                      sx={{ mt: 1 }}
                    />
                  );
                })()}
                {/* Phase 6.2: PLT shelf life warning */}
                {(() => {
                  const warnDay = projections?.by_brigade
                    ?.flatMap(b => b.by_day || [])
                    .find(d => d.resources?.shelf_life?.shelf_life_warning);
                  if (!warnDay) return null;
                  return (
                    <Alert severity="warning" sx={{ mt: 1, py: 0.25, px: 1 }}>
                      <Typography variant="caption">
                        PLT expiry: ~{warnDay.resources.shelf_life.plt_daily_wastage_estimate} units/day wastage (5-day shelf life)
                      </Typography>
                    </Alert>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>
          
          {/* IMP-015: Blood Adequacy Check — USCENTCOM 6+2 for US standard; AJMedP-2 for NATO/others */}
          {nationalStandard === 'US' ? (() => {
            // GROUP-1-B: USCENTCOM "6 and 2" Blood Floor Check (US standard only)
            const is = scenario?.logistics?.medical_supplies?.initial_stock || {};
            const surgTeamsCfg = scenario?.medical_configuration?.surgical_config?.teams;
            const fstCount = surgTeamsCfg?.FST?.count || 0;
            const surgCoCount = surgTeamsCfg?.SURG_CO?.count || 0;
            const orBeds = (fstCount * BLOOD_FLOOR_FST_OR_BEDS) + (surgCoCount * BLOOD_FLOOR_SURGCO_OR_BEDS);
            const brigadeCount = (scenario?.force?.brigades?.length || scenario?.force?.forceBuilderResult?.brigades?.length || 1);
            const poiTeams = brigadeCount;
            const minimumRequired = (orBeds * BLOOD_FLOOR_PER_OR_BED) + (poiTeams * BLOOD_FLOOR_PER_POI_TEAM);
            const currentStock =
              (is.prbc_units || 0) + (is.ffp_units || 0) +
              (is.platelets_units || 0) + (is.cryo_units || 0) +
              (is.whole_blood_units || 0);
            if (minimumRequired === 0 || currentStock === 0) return null;
            const coverFraction = minimumRequired > 0 ? currentStock / minimumRequired : 1;
            const floorStatus = coverFraction >= BLOOD_FLOOR_AMBER_THRESHOLD ? 'green'
              : coverFraction >= BLOOD_FLOOR_RED_THRESHOLD ? 'warning' : 'error';
            const floorLabel = floorStatus === 'green' ? 'ADEQUATE' : floorStatus === 'warning' ? 'MARGINAL' : 'DEFICIENT';
            return (
              <Grid size={{ xs: 12 }}>
                <Card variant="outlined" sx={{ borderColor: floorStatus === 'error' ? 'error.main' : floorStatus === 'warning' ? 'warning.main' : 'success.main', borderWidth: floorStatus !== 'green' ? 2 : 1 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2">
                        USCENTCOM Blood Floor Check (6+2 Rule)
                      </Typography>
                      <Chip
                        label={floorLabel}
                        size="small"
                        color={floorStatus === 'green' ? 'success' : floorStatus === 'warning' ? 'warning' : 'error'}
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="body2" color="text.secondary">OR Beds (OR tables)</Typography>
                        <Typography variant="h6">{orBeds}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fstCount > 0 ? `${fstCount} FST (${fstCount * BLOOD_FLOOR_FST_OR_BEDS} tables)` : ''}
                          {fstCount > 0 && surgCoCount > 0 ? ' + ' : ''}
                          {surgCoCount > 0 ? `${surgCoCount} SURG_CO (${surgCoCount * BLOOD_FLOOR_SURGCO_OR_BEDS} tables)` : ''}
                          {orBeds === 0 ? 'No surgical teams configured (Tab 3)' : ''}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="body2" color="text.secondary">Minimum Required (LTOWB eq.)</Typography>
                        <Typography variant="h6" color={floorStatus === 'error' ? 'error.main' : floorStatus === 'warning' ? 'warning.main' : 'success.main'}>
                          {minimumRequired} units
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {orBeds} beds x {BLOOD_FLOOR_PER_OR_BED} + {poiTeams} POI teams x {BLOOD_FLOOR_PER_POI_TEAM}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="body2" color="text.secondary">Initial Blood Stock</Typography>
                        <Typography variant="h6">{currentStock} units</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Math.round(coverFraction * 100)}% of minimum ({(currentStock - minimumRequired) >= 0 ? '+' : ''}{currentStock - minimumRequired} units)
                        </Typography>
                      </Grid>
                    </Grid>
                    <Alert severity={floorStatus === 'green' ? 'success' : floorStatus === 'warning' ? 'warning' : 'error'} sx={{ mt: 1.5, py: 0.5 }}>
                      <Typography variant="caption">
                        <strong>USCENTCOM doctrinal minimum:</strong> {BLOOD_FLOOR_PER_OR_BED} LTOWB per OR bed + {BLOOD_FLOOR_PER_POI_TEAM} LTOWB per POI team.
                        {floorStatus !== 'green' && (
                          <> <strong>Action required:</strong> increase initial blood stock in Tab 4 (Medical Supplies) or add surgical teams in Tab 3 to adjust OR bed count.</>
                        )}
                        {' '}
                        <em>Source: Military Medicine 2024, Vol. 189(9-10):249. POI teams approximated as 1 per brigade.</em>
                      </Typography>
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>
            );
          })() : (() => {
            // IMP-015: AJMedP-2 Blood Adequacy Check (NATO / ROMANIAN / non-US standards)
            const is = scenario?.logistics?.medical_supplies?.initial_stock || {};
            const bDOS = logisticsImpact.bloodDOS;
            const prbc = is.prbc_units || 0;
            const ffp  = is.ffp_units  || 0;
            const plt  = is.platelets_units || 0;
            if (bDOS === null && prbc === 0) return null;

            // 1. Blood DOS adequacy vs national standard thresholds
            const dosStatus = bDOS === null ? 'unknown'
              : bDOS >= dosThresholds.adequate ? 'green'
              : bDOS >= dosThresholds.minimum  ? 'warning'
              : 'error';
            const dosLabel = dosStatus === 'green' ? 'ADEQUATE'
              : dosStatus === 'warning' ? 'MARGINAL'
              : dosStatus === 'error'   ? 'DEFICIENT' : 'UNKNOWN';

            // 2. DCR ratio: pRBC:FFP — AJMedP-2 target 1:1 (Damage Control Resuscitation)
            const ffpRatio = prbc > 0 ? ffp / prbc : null;
            const mtpStatus = ffpRatio === null ? 'unknown'
              : ffpRatio >= 0.75 ? 'green'
              : ffpRatio >= 0.50 ? 'warning'
              : 'error';
            const mtpLabel = mtpStatus === 'green' ? '1:1 BALANCED'
              : mtpStatus === 'warning' ? 'LOW FFP'
              : mtpStatus === 'error'   ? 'FFP DEFICIENT' : 'UNKNOWN';

            // 3. WBB status
            const wbbCfg    = scenario?.medical_configuration?.blood_config || {};
            const wbbOn     = !!wbbCfg.wbb_enabled;
            const wbbDonors = wbbCfg.wbb_donors || 0;
            const wbbThresh = wbbCfg.wbb_activation_threshold || 30;
            const wbbUniv   = wbbCfg.wbb_low_titer_donors || 0;

            // Overall status
            const _statuses = [dosStatus, mtpStatus].filter(s => s !== 'unknown');
            const overallStatus = _statuses.includes('error') ? 'error'
              : _statuses.includes('warning') ? 'warning' : 'green';
            const overallLabel = overallStatus === 'error' ? 'DEFICIENT'
              : overallStatus === 'warning' ? 'MARGINAL' : 'ADEQUATE';

            return (
              <Grid size={{ xs: 12 }}>
                <Card variant="outlined" sx={{
                  borderColor: overallStatus === 'error' ? 'error.main' : overallStatus === 'warning' ? 'warning.main' : 'success.main',
                  borderWidth: overallStatus !== 'green' ? 2 : 1
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2">
                        AJMedP-2 Blood Adequacy Check ({nationalStandard} standard)
                      </Typography>
                      <Chip label={overallLabel} size="small"
                        color={overallStatus === 'green' ? 'success' : overallStatus === 'warning' ? 'warning' : 'error'}
                        sx={{ fontWeight: 700 }} />
                    </Box>
                    <Grid container spacing={2}>
                      {/* Column 1: Blood DOS vs national thresholds */}
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="body2" color="text.secondary">Blood DOS (commanded)</Typography>
                        <Typography variant="h6" color={
                          dosStatus === 'error' ? 'error.main' : dosStatus === 'warning' ? 'warning.main' : dosStatus === 'green' ? 'success.main' : 'text.secondary'
                        }>
                          {bDOS !== null ? `${bDOS}d` : '?'}
                        </Typography>
                        <Chip size="small" label={dosLabel} variant="outlined"
                          color={dosStatus === 'green' ? 'success' : dosStatus === 'warning' ? 'warning' : dosStatus === 'error' ? 'error' : 'default'}
                          sx={{ fontSize: '0.65rem', height: 20, mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          Min {dosThresholds.minimum}d / Adequate {dosThresholds.adequate}d ({nationalStandard})
                        </Typography>
                      </Grid>
                      {/* Column 2: DCR ratio pRBC:FFP */}
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="body2" color="text.secondary">DCR Ratio (pRBC:FFP)</Typography>
                        <Typography variant="h6" color={
                          mtpStatus === 'error' ? 'error.main' : mtpStatus === 'warning' ? 'warning.main' : mtpStatus === 'green' ? 'success.main' : 'text.secondary'
                        }>
                          {ffpRatio !== null ? `1 : ${ffpRatio.toFixed(2)}` : '—'}
                        </Typography>
                        <Chip size="small" label={mtpLabel} variant="outlined"
                          color={mtpStatus === 'green' ? 'success' : mtpStatus === 'warning' ? 'warning' : mtpStatus === 'error' ? 'error' : 'default'}
                          sx={{ fontSize: '0.65rem', height: 20, mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          Target ≥ 0.75 FFP per PRBC. PLT on hand: {plt} units.
                        </Typography>
                      </Grid>
                      {/* Column 3: WBB backup */}
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="body2" color="text.secondary">WBB Backup</Typography>
                        <Typography variant="h6" color={wbbOn ? 'success.main' : 'text.secondary'}>
                          {wbbOn ? `${wbbDonors} donors` : 'Not enabled'}
                        </Typography>
                        <Chip size="small"
                          label={wbbOn ? 'WBB ACTIVE' : 'NO WBB'}
                          color={wbbOn ? 'success' : 'default'} variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 20, mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          {wbbOn
                            ? `Activates at ${wbbThresh}% stock. ${wbbUniv} universal (Low-Titer O+) donors.`
                            : 'Configure WBB in Tab 3 for emergency whole-blood backup.'}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Alert severity={overallStatus === 'green' ? 'success' : overallStatus === 'warning' ? 'warning' : 'error'} sx={{ mt: 1.5, py: 0.5 }}>
                      <Typography variant="caption">
                        <strong>AJMedP-2 DCR standard:</strong> pRBC:FFP ratio ≥ 1:1 (Damage Control Resuscitation);
                        blood DOS ≥ {dosThresholds.minimum}d minimum / ≥ {dosThresholds.adequate}d adequate
                        ({nationalStandard} planning factors, STANAG 2228).
                        {!wbbOn && ' WBB not configured — no emergency whole-blood backup available.'}
                        {overallStatus !== 'green' && ' Adjust blood stock via Tab 4 (Medical Supplies).'}
                        {' '}<em>Source: NATO AJMedP-2 ed.B; STANAG 2228; DCR protocol.</em>
                      </Typography>
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>
            );
          })()}

          {/* PHASE 1.2: WBB Status Card */}
          {projections.medical_requirements?.wbb && projections.medical_requirements.wbb.total_activations > 0 && (
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card variant="outlined" sx={{ borderColor: 'error.main', borderWidth: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" color="text.secondary">Walking Blood Bank</Typography>
                    <BloodtypeIcon color="error" />
                  </Box>
                  <Alert severity="info" sx={{ mt: 1, mb: 0.5, py: 0, px: 1, fontSize: '0.65rem' }}>
                    <Typography variant="caption">
                      WBB is not a standalone NATO STANAG. Referenced in AMedP-1.1 operational appendices and national blood programme annexes.
                    </Typography>
                  </Alert>
                  <Chip 
                    label="ACTIVATED"
                    size="small" 
                    color="error"
                    sx={{ mt: 1 }}
                  />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Activations: {projections.medical_requirements.wbb.total_activations}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Units Contributed: {formatNumber(projections.medical_requirements.wbb.total_units_contributed)}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Universal (O+): {formatNumber(projections.medical_requirements.wbb.total_universal_units)}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Days: {projections.medical_requirements.wbb.days_activated.join(', ')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Tooltip
                    title="NATO medical materiel is classified under AMedP-1.12 (STANAG 2128). US forces use 'Class VIII' under ATP 4-02  -  not a NATO STANAG term."
                    arrow
                  >
                    <Typography variant="subtitle2" color="text.secondary" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                      {t('tab5.logistics_extra.medical_materiel')}
                    </Typography>
                  </Tooltip>
                  <InventoryIcon color={logisticsImpact.supplyDOS === null ? 'disabled' : getMDOSColor(logisticsImpact.supplyDOS, dosThresholds)} />
                </Box>
                <Typography variant="h4"
                  color={logisticsImpact.supplyDOS === null ? 'text.secondary' : `${getMDOSColor(logisticsImpact.supplyDOS, dosThresholds)}.main`}>
                  {logisticsImpact.supplyDOS !== null ? `${logisticsImpact.supplyDOS} days` : 'N/A'}
                </Typography>
                <Chip
                  label={getMDOSLabel(logisticsImpact.supplyDOS, dosThresholds)}
                  size="small"
                  color={getMDOSColor(logisticsImpact.supplyDOS, dosThresholds)}
                  sx={{ mt: 1 }}
                />
                {/* L5-05: Null guidance */}
                {logisticsImpact.supplyDOS === null && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    N/A  -  Complete Tab 3 calculation first
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* SURGICAL-DOS-001 FIX: Card relabelled to clarify this is FST operating-hours
              capacity, NOT surgical kit stock. Adding kits in Tab 4 has zero effect.
              Fix requires: more FST teams or dual-shift mode in Tab 3 Section 3D. */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Tooltip
                    title="FST operating hours available vs surgical demand. This is a staffing metric  -  adding surgical kits in Tab 4 does not affect it. To improve: add FST teams or enable dual-shift mode in Tab 3 -> Section 3D."
                    arrow
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}
                    >
                      {t('tab5.logistics_extra.fst_throughput')}
                    </Typography>
                  </Tooltip>
                  <HealingIcon color={logisticsImpact.surgicalDOS === null ? 'disabled' : getMDOSColor(logisticsImpact.surgicalDOS, dosThresholds)} />
                </Box>
                <Typography variant="h4"
                  color={logisticsImpact.surgicalDOS === null ? 'text.secondary' : `${getMDOSColor(logisticsImpact.surgicalDOS, dosThresholds)}.main`}>
                  {logisticsImpact.surgicalDOS === null ? 'N/A' : logisticsImpact.surgicalDOS === 0 ? '0 days' : `${logisticsImpact.surgicalDOS} days`}
                </Typography>
                {/* SURGICAL-DOS-001: coverage fraction  -  "3 days" reads as "3 of 10-day op
                    covered", not as days-of-supply stock */}
                {logisticsImpact.surgicalDOS !== null && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {'of '}
                    {scenario?.operation_parameters?.duration_days ??
                     projections?.scenario_summary?.duration_days ??
                     projections?.duration_days ?? '?'}
                    {'-day op covered'}
                  </Typography>
                )}
                <Chip
                  label={
                    logisticsImpact.surgicalDOS === null
                      ? 'N/A - Data Missing'
                      : logisticsImpact.surgicalDOS < (dosThresholds.minimum ?? 5)
                        ? 'RED - Add FST teams (Tab 3)'
                        : getMDOSLabel(logisticsImpact.surgicalDOS, dosThresholds)
                  }
                  size="small"
                  color={getMDOSColor(logisticsImpact.surgicalDOS, dosThresholds)}
                  sx={{ mt: 1 }}
                />
                {/* L5-05: Null guidance */}
                {logisticsImpact.surgicalDOS === null && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    N/A  -  Complete Tab 3 calculation first
                  </Typography>
                )}
                {logisticsImpact.surgicalDOS !== null && (
                  <>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Required: {formatNumber(logisticsImpact.surgicalHours)} FST hrs
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      For {formatNumber(logisticsImpact.casualtyContext?.surgicalCases || 0)} cases ({logisticsImpact.casualtyContext?.hoursPerCase || 0} hrs/case)
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* WTD-01: Anatomical Wound Distribution  -  derived from operation type */}
        {(() => {
          const brigades = projections?.by_brigade || [];
          if (brigades.length === 0) return null;

          const totals = { extremity: 0, thorax: 0, abdomen: 0, head_neck: 0, burns: 0, multi: 0 };
          let opType = null;
          let hasDist = false;

          brigades.forEach(b => {
            if (!b.wound_distribution) return;
            hasDist = true;
            opType = opType || b.wound_distribution.operation_type;
            const t1 = b.wound_distribution.t1_by_wound || {};
            const t2 = b.wound_distribution.t2_by_wound || {};
            Object.keys(totals).forEach(r => {
              totals[r] += (t1[r] || 0) + (t2[r] || 0);
            });
          });

          if (!hasDist) return null;
          const totalWIA = Object.values(totals).reduce((s, v) => s + v, 0);
          if (totalWIA === 0) return null;

          const regions = [
            { key: 'extremity',  label: 'Extremity',    color: 'primary', note: 'Tourniquet / vascular / ortho' },
            { key: 'thorax',     label: 'Thorax',       color: 'warning', note: 'Haemothorax / tension pneumo' },
            { key: 'abdomen',    label: 'Abdomen',      color: 'warning', note: 'Solid organ / bowel' },
            { key: 'head_neck',  label: 'Head / Neck',  color: 'error',   note: 'TBI / vascular  -  highest DOW' },
            { key: 'burns',      label: 'Burns',        color: 'error',   note: 'Inhalation / vehicle fires' },
            { key: 'multi',      label: 'Multi-cavity', color: 'inherit', note: 'Blast / penetrating multi-region' },
          ];

          return (
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Anatomical Wound Distribution  -  WIA
                <Chip size="small" label={opType || 'STANDARD'} variant="outlined" sx={{ ml: 'auto', fontSize: '0.65rem' }} />
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Derived from operation type mechanism mix (JTS JTTR). Guides dressing procurement and surgical team specialisation.
              </Typography>
              <Grid container spacing={1}>
                {regions.map(({ key, label, color, note }) => {
                  const count = totals[key] || 0;
                  const pct = totalWIA > 0 ? Math.round((count / totalWIA) * 100) : 0;
                  return (
                    <Grid size={{ xs: 6, sm: 4 }} key={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" fontWeight="bold">{label}</Typography>
                            <Typography variant="caption">{formatNumber(count)} ({pct}%)</Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            color={color}
                            sx={{
                              height: 6, borderRadius: 3, my: 0.3,
                              // 'inherit' used for multi-cavity (no valid MUI color)  -  force grey bar
                              ...(color === 'inherit' && {
                                backgroundColor: 'grey.200',
                                '& .MuiLinearProgress-bar': { backgroundColor: 'grey.500' }
                              })
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
                            {note}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          );
        })()}

      </Paper>
    );
  };

  // ============================================================================
  // PHASE 6.2: STRATEVAC CAPACITY CARD
  // Reads projections.medical_requirements.stratevac_capacity (v5.11.0).
  // Only renders when airfield class data is present (air evac configured in Tab 3).

  // ============================================================================
  // V4-TAB6-004: Pre-combat build-up phase DNBI panel.
  // Data source: projections.mascal_analysis.buildup_phase (calculator v5.56.0).
  // Null when buildup_config not set or enabled=false — panel is hidden gracefully.
  // ============================================================================
  const renderBuildupPhasePanel = () => {
    const bp = projections?.mascal_analysis?.buildup_phase;
    if (!bp) return null;

    const strengthRedHigh = bp.strength_reduction_pct > 10;

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="info" />
          {t('tab5.buildup.title')}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Estimated DNBI accumulated during the {bp.duration_days}-day build-up phase
          (staging, deployment, acclimatization) before H-Hour. Informational only —
          main calculation uses full brigade strength.
        </Typography>

        <Alert severity={strengthRedHigh ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {strengthRedHigh
            ? <><strong>Significant pre-combat attrition:</strong> {bp.strength_reduction_pct}% of
                force unavailable at D-Day. Review DNBI prevention program and pre-deployment
                medical screening.</>
            : <>Pre-combat attrition within expected range ({bp.strength_reduction_pct}% of force).
                Day-0 medical load carried into operations: {bp.day0_medical_load} patients.</>
          }
        </Alert>

        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('tab5.buildup_extra.duration')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {bp.duration_days} days
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('tab5.buildup_extra.total_dnbi')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {bp.buildup_dnbi_total}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              avg {bp.avg_daily_dnbi}/day
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('tab5.buildup_extra.day0_load')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>
              {bp.day0_medical_load}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              patients in system at H-Hour
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('tab5.buildup_extra.eff_strength')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {bp.effective_starting_strength?.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              of {bp.total_strength_reference?.toLocaleString()} total
              ({bp.strength_reduction_pct}% held)
            </Typography>
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          Methodology: SUPPORT_ACTIVE DNBI baseline x1.2 deployment stress. Day-0 load =
          last 3 build-up days x 15% non-RTD evacuation fraction. Does not include pre-deployment
          medical screening exclusions.
        </Typography>
      </Paper>
    );
  };

  // ============================================================================
  // ENH-047: Coalition CASEVAC Overflow Routing panel (Category 2)
  // Data source: projections.by_brigade[*].by_day[*].casevac_requirements.cross_division_support
  // ============================================================================
  const renderCoalitionOverflow = () => {
    if (!projections?.by_brigade?.length) return null;

    let crossDivEnabled = false;
    let totalRouted = 0;
    let totalEvacuated = 0;
    let overflowThreshold = null;
    let mascalOnly = false;
    let firstBlockReason = null;

    projections.by_brigade.forEach(brigade => {
      (brigade.by_day || []).forEach(day => {
        const cds = day?.casevac_requirements?.cross_division_support;
        if (!cds) return;
        if (cds.enabled) crossDivEnabled = true;
        totalRouted += (cds.casualties_routed || 0);
        totalEvacuated += (day?.casevac_requirements?.cross_division_evacuated || 0);
        if (overflowThreshold == null && cds.config?.routine_overflow_threshold != null)
          overflowThreshold = cds.config.routine_overflow_threshold;
        if (cds.config?.mascal_only) mascalOnly = true;
        if (!firstBlockReason && cds.blocked && cds.block_reason)
          firstBlockReason = cds.block_reason;
      });
    });

    if (!crossDivEnabled) {
      return (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={t('tab5.coalition_overflow.disabled_chip')}
            color="default"
            size="small"
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary">
            Enable in Tab 3 Medical — cross-division config.
          </Typography>
        </Box>
      );
    }

    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2, borderColor: 'info.main' }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('tab5.coalition_overflow.title')}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('tab5.coalition_extra.total_evacuated')}
            </Typography>
            <Typography variant="h6">{totalEvacuated.toLocaleString()}</Typography>
            <Typography variant="caption" color="text.secondary">
              {totalRouted > 0 ? `${totalRouted} routed for cross-division` : 'no overflow routed'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('tab5.coalition_extra.overflow_threshold')}
            </Typography>
            <Typography variant="h6">
              {overflowThreshold != null ? `${overflowThreshold}%` : 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary">utilisation trigger</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('tab5.coalition_extra.mascal_routing')}
            </Typography>
            <Chip
              size="small"
              label={mascalOnly ? 'MASCAL-only' : 'routine + MASCAL'}
              color={mascalOnly ? 'warning' : 'success'}
              variant="outlined"
            />
          </Grid>
        </Grid>
        <Divider sx={{ my: 1 }} />
        {firstBlockReason && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Cross-division routing blocked: {firstBlockReason}
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary">
          AJP-4.10C: Cross-division support activates at &ge;{overflowThreshold ?? '?'}% utilisation
          when routine_overflow_enabled. Routing is non-reciprocal and commander-authorised.
        </Typography>
      </Paper>
    );
  };

  // ============================================================================
  // MASCAL-WHEN-WHERE (v3.5.0  -  March 4, 2026)
  // Per-brigade R2B bed occupancy timeline.
  // v3.6.0 (March 5, 2026): Extended with R2E per-division and R3 corps timelines.
  // Data source: projections.mascal_analysis (casualtyCalculator.js v5.10.0).
  // ============================================================================
  const renderMASCALTimeline = () => {
    const ma = projections?.mascal_analysis;
    if (!ma || !Array.isArray(ma.brigades) || ma.brigades.length === 0) return null;

    const { brigades, system_summary, r2b_beds_per_brigade, avg_los_days, thp_key,
            r2e_divisions, r3_corps, r2e_system_summary, r3_system_summary,
            r3_absent_delta } = ma;
    const { first_critical_day, first_critical_brigade, brigades_ever_critical, total_brigades } = system_summary;

    // Status -> MUI colour mapping (mirrors Tab4 Section 4D)
    const statusColor = (s) => {
      if (s === 'CRITICAL') return 'error';
      if (s === 'HIGH')     return 'error';
      if (s === 'ELEVATED') return 'warning';
      return 'success';
    };
    const statusBg = (s) => {
      if (s === 'CRITICAL') return '#FFEBEE';
      if (s === 'HIGH')     return '#FFF3E0';
      if (s === 'ELEVATED') return '#FFFDE7';
      return '#F1F8E9';
    };

    // Duration = length of longest timeline in data
    const duration = brigades.reduce((max, b) => Math.max(max, b.timeline?.length || 0), 0);
    const days = Array.from({ length: duration }, (_, i) => i + 1);

    // ENH-022b: calendar date alongside D+N when operation_start_date is set in Tab 2
    const opStartDate = scenario?.operation_parameters?.operation_start_date || '';
    const opStartTimeZ = scenario?.operation_parameters?.operation_start_time_z || DEFAULT_START_TIME_Z;
    // R2-09: the date of the day and its Zulu hour (same start as Tab 2, Tab 7 and Tab 8)
    const fmtDay = (d) => {
      if (!opStartDate) return `D${d}`;
      try {
        const z = formatZulu(instantOfDay(opStartDate, opStartTimeZ, d));   // '22 Oct 2026 06:00Z'
        const [dd, mon, , hhmm] = z.split(' ');
        return `D${d}\n${dd} ${mon}\n${hhmm}`;
      } catch { return `D${d}`; }
    };

    // -- Helper: shared day-cell table for any echelon ----------------------
    const renderEchelonTable = (rows, rowKeyFn, rowLabelFn, rowBedsFn) => (
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.75rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '2px solid #E0E0E0', whiteSpace: 'nowrap', minWidth: 140 }}>
                {t('tab5.mascal_extra.col_unit')}
              </th>
              <th style={{ textAlign: 'center', padding: '4px 6px', borderBottom: '2px solid #E0E0E0', whiteSpace: 'nowrap' }}>
                {t('tab5.mascal_extra.col_beds')}
              </th>
              <th style={{ textAlign: 'center', padding: '4px 6px', borderBottom: '2px solid #E0E0E0', whiteSpace: 'nowrap' }}>
                {t('tab5.mascal_extra.col_first_mascal')}
              </th>
              <th style={{ textAlign: 'center', padding: '4px 6px', borderBottom: '2px solid #E0E0E0', whiteSpace: 'nowrap' }}>
                {t('tab5.mascal_extra.col_peak')}
              </th>
              {days.map(d => (
                <th key={d} style={{ textAlign: 'center', padding: '4px 4px', borderBottom: '2px solid #E0E0E0', minWidth: opStartDate ? 62 : 36, whiteSpace: 'pre-line', lineHeight: 1.2 }}>
                  {fmtDay(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={rowKeyFn(row, idx)} style={{ borderBottom: '1px solid #F5F5F5' }}>
                <td style={{ padding: '3px 8px', whiteSpace: 'nowrap', fontWeight: row.first_critical_day !== null ? 'bold' : 'normal' }}>
                  {rowLabelFn(row)}
                </td>
                <td style={{ textAlign: 'center', padding: '3px 6px', color: '#666' }}>
                  {rowBedsFn(row)}
                </td>
                <td style={{ textAlign: 'center', padding: '3px 6px' }}>
                  {row.first_critical_day !== null ? (
                    <Chip size="small" label={`Day ${row.first_critical_day}`} color="error" sx={{ fontSize: '0.65rem', height: 20 }} />
                  ) : (
                    <Typography variant="caption" color="text.secondary"> - </Typography>
                  )}
                </td>
                <td style={{ textAlign: 'center', padding: '3px 6px' }}>
                  <Chip
                    size="small"
                    label={`${row.peak_pct}% D${row.peak_day}`}
                    color={statusColor(row.peak_pct >= 100 ? 'CRITICAL' : row.peak_pct >= 85 ? 'HIGH' : row.peak_pct >= 70 ? 'ELEVATED' : 'GREEN')}
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 20 }}
                  />
                </td>
                {days.map(d => {
                  const cell = row.timeline?.find(t => t.day === d);
                  if (!cell) return <td key={d} style={{ textAlign: 'center', padding: '3px 4px', color: '#CCC' }}> - </td>;
                  return (
                    <td
                      key={d}
                      title={`${rowLabelFn(row)} Day ${d}: ${cell.occupancy_pct}% (census ${cell.census}, admissions ${cell.admissions})`}
                      style={{
                        textAlign: 'center',
                        padding: '3px 4px',
                        backgroundColor: statusBg(cell.status),
                        fontWeight: cell.status === 'CRITICAL' ? 'bold' : 'normal',
                        color: cell.status === 'CRITICAL' ? '#C62828' : cell.status === 'HIGH' ? '#E65100' : 'inherit'
                      }}
                    >
                      {cell.occupancy_pct}%
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    );

    // -- Determine first MASCAL across all three echelons for top summary --
    const allFirstCriticalDays = [
      first_critical_day !== null ? { day: first_critical_day, label: `R2B: ${first_critical_brigade}` } : null,
      r2e_system_summary?.first_critical_day !== null && r2e_system_summary?.first_critical_day != null
        ? { day: r2e_system_summary.first_critical_day, label: `R2E: ${r2e_system_summary.first_critical_division}` }
        : null,
      r3_system_summary?.first_critical_day !== null && r3_system_summary?.first_critical_day != null
        ? { day: r3_system_summary.first_critical_day, label: 'R3: Corps MTF' }
        : null,
    ].filter(Boolean);

    const overallFirstCritical = allFirstCriticalDays.length > 0
      ? allFirstCriticalDays.reduce((earliest, e) => e.day < earliest.day ? e : earliest, allFirstCriticalDays[0])
      : null;

    const anyMASCAL = overallFirstCritical !== null;

    // BUG-5C-001 FIX: Detect WARNING-level occupancy even when no CRITICAL event.
    // Configured warning threshold (default 80%). Any brigade/division with peak >= warn
    // should show AMBER banner, not GREEN "No MASCAL Events".
    const _warnThr = scenario?.medical_configuration?.mascal_config?.thresholds?.mtf_warning ?? 80;
    const anyWarning = !anyMASCAL && (
      brigades.some(b => (b.peak_pct ?? 0) >= _warnThr) ||
      (Array.isArray(r2e_divisions) && r2e_divisions.some(d => (d.peak_pct ?? 0) >= _warnThr)) ||
      (r3_corps && (r3_corps.peak_pct ?? 0) >= _warnThr)
    );

    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalHospitalIcon color="error" />
          {t('tab5.mascal.title')}
        </Typography>

        {/* ECHELON-DUAL: R3-absent system-state banner */}
        {!(projections?.tprp?.r3_accessible ?? true) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>
              {t('tab5.mascal.echelon_dual_banner')}
            </AlertTitle>
            DOW rate +{Math.round(((projections.tprp.dow_multiplier_applied ?? 1.0) - 1.0) * 100)}% applied.
            STRATEVAC drain disabled. Blood DOS consumption +15%.
            Required additional R2E surgical teams: {projections.tprp.additional_r2e_teams_required ?? 0}.
            Configure in Tab 3 Medical.
          </Alert>
        )}

        {/* ECHELON-DUAL: TPRP requirements card */}
        {!(projections?.tprp?.r3_accessible ?? true) && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: 'error.main' }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('tab5.mascal.tprp_title')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Additional R2E surgical teams required
                </Typography>
                <Typography variant="h6" color="error.main">
                  {projections.tprp.additional_r2e_teams_required ?? 0}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Additional blood PRBC required
                </Typography>
                <Typography variant="h6" color="error.main">
                  {projections.tprp.additional_blood_prbc_required ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">units</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  MASCAL threshold effect
                </Typography>
                <Chip size="small" label="accelerated" color="error" variant="outlined" />
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              AJMedP-2: When R3 is absent, R2E must provide all definitive surgery.
              TPRP increases proportionally to R3 drain loss. 1 surgical team handles approx. 14 T3 patients/day.
            </Typography>
          </Paper>
        )}

        {/* Top summary alert  -  first MASCAL across all echelons */}
        {anyMASCAL ? (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#FFEBEE', borderRadius: 1, border: '1px solid #EF9A9A' }}>
            <Typography variant="body2" fontWeight="bold" color="error.main">
              First MASCAL Event: {overallFirstCritical.label}  -  {dayLabel(overallFirstCritical.day)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {brigades_ever_critical} of {total_brigades} brigade{total_brigades !== 1 ? 's' : ''} exceed R2B capacity
              {r2e_system_summary?.divisions_ever_critical > 0 && `, ${r2e_system_summary.divisions_ever_critical} of ${r2e_system_summary.total_divisions} division${r2e_system_summary.total_divisions !== 1 ? 's' : ''} exceed R2E capacity`}
              {r3_system_summary?.ever_critical && ', Corps R3 MTF capacity exceeded'}
              {' '}at some point during the operation.
              {' '}Doctrinal responses: expedite STRATEVAC, activate surge beds, shorten TPRP, request additional assets.
            </Typography>
          </Box>
        ) : anyWarning ? (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#FFF3E0', borderRadius: 1, border: '1px solid #FFCC80' }}>
            <Typography variant="body2" fontWeight="bold" color="warning.dark">
              {t('tab5.mascal_extra.warning_banner', { threshold: _warnThr })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Peak occupancy approaches MASCAL range. Monitor resupply schedule and evacuation chain.
              Capacity has not been exceeded — but peak days require attention.
              Review the timeline below for affected brigades/divisions.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#F1F8E9', borderRadius: 1, border: '1px solid #A5D6A7' }}>
            <Typography variant="body2" fontWeight="bold" color="success.main">
              {t('tab5.mascal_extra.no_mascal')}
            </Typography>
          </Box>
        )}

        {/* Phase 6: Configured MASCAL threshold reference row */}
        {(() => {
          const savedThr = scenario?.medical_configuration?.mascal_config?.thresholds;
          const warn = savedThr?.mtf_warning             ?? 80;
          const crit = savedThr?.mtf_critical            ?? 95;
          const sqw  = savedThr?.surgical_queue_warning  ?? 4;
          const sqc  = savedThr?.surgical_queue_critical ?? 8;
          const isCustom = savedThr &&
            (warn !== 80 || crit !== 95 || sqw !== 4 || sqc !== 8);
          return (
            <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                MASCAL thresholds{isCustom ? ' (scenario-configured):' : ' (NATO defaults):'}
              </Typography>
              <Chip size="small" label={`MTF warn ${warn}%`}         color={isCustom ? 'warning' : 'default'} variant="outlined" />
              <Chip size="small" label={`MTF crit ${crit}%`}         color={isCustom ? 'error'   : 'default'} variant="outlined" />
              <Chip size="small" label={`Surg queue warn ${sqw}h`}   color={isCustom ? 'warning' : 'default'} variant="outlined" />
              <Chip size="small" label={`Surg queue crit ${sqc}h`}   color={isCustom ? 'error'   : 'default'} variant="outlined" />
            </Box>
          );
        })()}

        {/* V3.1-MASCAL-THRESH: Civilian patient footnote when MRoE Stream 2 is active.
            MASCAL thresholds are military-only per AJMedP-1 §3.21 (doctrinally correct).
            This block surfaces the additive civilian load so planners know effective
            combined occupancy is higher than the military-only percentages in the timeline. */}
        {(() => {
          const civLoad = ma.mroe_load?.civilian_stream;
          if (!civLoad || !civLoad.competes_with_military || !(civLoad.daily_patients > 0)) return null;
          return (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              <Typography variant="body2" gutterBottom>
                <strong>
                  MRoE Stream 2 active — {civLoad.daily_patients} civilian patient{civLoad.daily_patients !== 1 ? 's' : ''}/day
                  competing for MTF capacity
                </strong>
                {civLoad.triage_profile && (
                  <span style={{ fontWeight: 'normal' }}> ({civLoad.triage_profile} triage profile)</span>
                )}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
                {civLoad.t1_surgical_daily > 0 && (
                  <Chip size="small" label={`T1 surgical: ${civLoad.t1_surgical_daily}/day`} color="error" variant="outlined" />
                )}
                {civLoad.t2_ward_daily > 0 && (
                  <Chip size="small" label={`T2 ward: ${civLoad.t2_ward_daily}/day`} color="warning" variant="outlined" />
                )}
                {civLoad.t3_hns_daily > 0 && (
                  <Chip size="small" label={`T3 HNS: ${civLoad.t3_hns_daily}/day`} color="default" variant="outlined" />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {t('tab5.mascal.civilian_footnote')}
              </Typography>
            </Alert>
          );
        })()}

        {/* -- SECTION 1: R2B  -  Per-Brigade ------------------------------- */}
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1, mb: 0.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          {t('tab5.mascal_extra.r2b_label')}
        </Typography>

        {/* Planning parameter chips */}
        <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip size="small" label={`${r2b_beds_per_brigade} beds / brigade`} variant="outlined" />
          <Chip size="small" label={`Avg LOS ${avg_los_days} days (${thp_key})`} variant="outlined" />
          <Chip size="small" label={`Hosp. rate ${Math.round(HOSPITALIZATION_RATE * 100)}% x R2B fraction ${Math.round(ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_BASIC * 100)}%`} variant="outlined" color="info" />
          {ma.csu_beds_added > 0 && (
            <Chip size="small" label={`+${ma.csu_beds_added} CSU beds included`} variant="outlined" color="success" />
          )}
        </Box>

        {renderEchelonTable(
          brigades,
          (row, idx) => row.brigade_id || idx,
          (row) => row.brigade_name,
          (row) => row.r2b_beds
        )}

        {/* -- SECTION 2: R2E  -  Per-Division ------------------------------ */}
        {r2e_divisions && r2e_divisions.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2.5, mb: 0.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
              {t('tab5.mascal_extra.r2e_label')}
            </Typography>
            <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" label={`${ma.total_r2e_beds > 0 ? Math.round(ma.total_r2e_beds / r2e_divisions.length) : '?'} beds / division`} variant="outlined" />
              <Chip size="small" label={`Hosp. rate ${Math.round(HOSPITALIZATION_RATE * 100)}% x R2E fraction ${Math.round(ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_ENHANCED * 100)}%`} variant="outlined" color="info" />
              {r2e_system_summary?.divisions_ever_critical > 0 && (
                <Chip size="small" label={`${r2e_system_summary.divisions_ever_critical}/${r2e_system_summary.total_divisions} divisions MASCAL`} color="error" />
              )}
            </Box>
            {renderEchelonTable(
              r2e_divisions,
              (row) => row.division_id,
              (row) => row.division_name,
              (row) => row.r2e_beds
            )}

            {/* R2E Planning Gap — BUG-NEXT-015: differentiate true gap (toggle OFF) from
                doctrinal defaults (toggle ON, no ORBAT entry — brigade echelon pattern). */}
            {ma.total_r2e_beds === 0 && (
              <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                <strong>{t('tab5.mascal_whatif.r2e_gap_title')} </strong>{t('tab5.mascal_whatif.r2e_gap_body')}
              </Alert>
            )}
            {ma.r2e_using_defaults === true && (
              <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                <strong>{t('tab5.mascal_whatif.r2e_doctrinal_title')} </strong>{t('tab5.mascal_whatif.r2e_doctrinal_body')}
              </Alert>
            )}

            {/* BUG-R2E-MARCH-001: March phase caveat — shown when any division had march phases */}
            {r2e_divisions.some(d => d.had_march_phases) && (
              <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                <Typography variant="caption">
                  <strong>{t('tab5.mascal_whatif.r2e_march_title')} </strong>
                  {t('tab5.mascal_whatif.r2e_march_pre')}{' '}
                  {Math.max(...r2e_divisions.map(d => d.peak_movement_backlog || 0))}{' '}
                  {t('tab5.mascal_whatif.r2e_march_post')}
                </Typography>
              </Alert>
            )}

            {/* V4-ECH-002: per-division R2E-absent delta cards */}
            {r2e_divisions.some(d => d.r2e_absent_delta) && (
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* OBS-S102-020: What-If header — amber border + bold to match clinical severity */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.5, py: 0.75,
                  bgcolor: 'warning.50', borderLeft: '4px solid', borderColor: 'warning.main', borderRadius: '0 4px 4px 0' }}>
                  <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                    {t('tab5.mascal_whatif.r2e_whatif_header')}
                  </Typography>
                  <Chip label={t('tab5.mascal_whatif.planning_sensitivity')} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                </Box>
                {r2e_divisions.map(div => {
                  const d = div.r2e_absent_delta;
                  if (!d) return null;
                  return (
                    <Alert key={div.division_id} severity="warning" sx={{ py: 0.75 }}>
                      <AlertTitle sx={{ mb: 0.5 }}>
                        {div.division_name} {t('tab5.mascal_whatif.r2e_overrun_suffix')}
                      </AlertTitle>
                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('tab5.mascal_whatif.first_critical_r2e')}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {d.first_critical_day_without_r2e !== null
                              ? `Day ${d.first_critical_day_without_r2e}`
                              : t('tab5.mascal_whatif.none')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {div.first_critical_day
                              ? t('tab5.mascal_whatif.vs_day_with_r2e', { day: div.first_critical_day })
                              : t('tab5.mascal_whatif.with_r2e_none')}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('tab5.mascal_whatif.excess_t1_deaths')}
                          </Typography>
                          <Typography variant="subtitle1" color="error.main" sx={{ fontWeight: 600 }}>
                            ~{d.excess_t1_deaths_estimate}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({d.avg_t1_fraction}% T1 at R2E)
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('tab5.mascal_whatif.overflow_r3_stratevac')}
                          </Typography>
                          <Typography variant="subtitle1" color="warning.main" sx={{ fontWeight: 600 }}>
                            {d.r3_overflow_increase}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('tab5.mascal_whatif.unmet_routed_up')}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('tab5.mascal_whatif.critical_days_r2e')}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {d.critical_days_without_r2e}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('tab5.mascal_whatif.vs_days_with_r2e', { days: d.baseline_critical_days })}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Alert>
                  );
                })}
                <Typography variant="caption" color="text.secondary">
                  {t('tab5.mascal_whatif.r2e_estimates_note')}
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* -- SECTION 3: R3  -  Corps -------------------------------------- */}
        {/* BUG-159 FIX: Show planning gap warning when no R3 MTF configured.
            BUG-NEXT-014: Differentiate between R3 toggle OFF (true gap) and
            R3 toggle ON with no ORBAT entry (doctrinal defaults applied). */}
        {ma.r3_configured === false && (
          <Alert severity="warning" sx={{ mt: 2, mb: 1 }} icon={false}>
            <strong>{t('tab5.mascal_whatif.r3_gap_title')} </strong>{t('tab5.mascal_whatif.r3_gap_body')}
          </Alert>
        )}
        {ma.r3_configured === true && ma.r3_using_defaults === true && (
          <Alert severity="info" sx={{ mt: 2, mb: 1 }} icon={false}>
            <strong>{t('tab5.mascal_whatif.r3_doctrinal_title')} </strong>{t('tab5.mascal_whatif.r3_doctrinal_body')}
          </Alert>
        )}
        {r3_corps && (
          <>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2.5, mb: 0.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
              {t('tab5.mascal_extra.r3_label')}
            </Typography>
            <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" label={t('tab5.mascal_whatif.r3_total_beds', { beds: r3_corps.r3_beds })} variant="outlined" />
              <Chip size="small" label={t('tab5.mascal_whatif.r3_hosp_rate', { hosp: Math.round(HOSPITALIZATION_RATE * 100), frac: Math.round(ECHELON_ADMISSION_DISTRIBUTION.ROLE_3 * 100) })} variant="outlined" color="info" />
              {r3_system_summary?.ever_critical && (
                <Chip size="small" label={t('tab5.mascal_whatif.r3_corps_mascal')} color="error" />
              )}
            </Box>
            {renderEchelonTable(
              [{ ...r3_corps, unit_name: 'Corps MTF (Role 3)' }],
              () => 'r3_corps',
              () => 'Corps MTF (Role 3)',
              (row) => row.r3_beds
            )}
          </>
        )}

        {/* ENH-046: Phase Transition Surge Events (Category 2) */}
        {(() => {
          const pt = projections?.phase_timeline;
          if (!pt || (pt.phase_count ?? 1) <= 1) return null;

          // Scan all brigade by_phase arrays for phase_transition entries
          const surgeEvents = [];
          (projections?.by_brigade || []).forEach(brigade => {
            (brigade.by_phase || []).forEach(phaseEntry => {
              if (!phaseEntry?.phase_transition) return;
              const tr = phaseEntry.phase_transition;
              surgeEvents.push({
                day: phaseEntry.start_day ?? phaseEntry.day ?? '?',
                from: tr.from_posture || '?',
                to: tr.to_posture || '?',
                factor: tr.surge_factor,
                surgeKia: tr.surge_kia ?? 0,
                surgeWia: tr.surge_wia ?? 0,
                surgeTotal: tr.surge_total ?? 0,
                brigade: brigade.brigade_name || brigade.name || '?',
              });
            });
          });
          surgeEvents.sort((a, b) => (Number(a.day) || 0) - (Number(b.day) || 0));

          const factorColor = (f) => {
            if (!f) return 'text.secondary';
            if (f >= 1.50) return 'error.main';
            if (f >= 1.40) return 'warning.main';
            if (f >= 1.25) return 'warning.light';
            return 'text.secondary';
          };

          return (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
                {t('tab5.mascal.surge_table_title')}
              </Typography>
              <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                AJMedP-1 §5.4: Phase transition demands -- surge casualty load at posture change.
              </Alert>
              {surgeEvents.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('tab5.mascal.no_surge')}
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('common.day')}</TableCell>
                          <TableCell>{t('common.from')}</TableCell>
                          <TableCell>{t('common.to')}</TableCell>
                          <TableCell align="right">{t('common.factor')}</TableCell>
                          <TableCell align="right">{t('tab5.mascal.surge_kia')}</TableCell>
                          <TableCell align="right">{t('tab5.mascal.surge_wia')}</TableCell>
                          <TableCell align="right">{t('tab5.mascal.surge_total')}</TableCell>
                          <TableCell>{t('common.brigade')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {surgeEvents.map((ev, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{dayLabel(ev.day)}</TableCell>
                            <TableCell>{ev.from}</TableCell>
                            <TableCell>{ev.to}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ color: factorColor(ev.factor), fontWeight: 600 }}>
                                x{ev.factor != null ? ev.factor.toFixed(2) : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{ev.surgeKia}</TableCell>
                            <TableCell align="right">{ev.surgeWia}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{ev.surgeTotal}</TableCell>
                            <TableCell>{ev.brigade}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Surge applied on day 1 of posture change only.
                    Factors: ATTACK-&gt;DEFENSE x1.25, DEFENSE-&gt;ATTACK x1.50, others x1.15-x1.40.
                  </Typography>
                </>
              )}
            </Box>
          );
        })()}

        {/* V4-ECH-001: R3-absent delta card */}
        {r3_absent_delta && (
          <Alert severity={r3_absent_delta.is_active_mode ? 'error' : 'warning'} sx={{ mt: 2 }}>
            <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {r3_absent_delta.is_active_mode
                ? t('tab5.mascal.r3_active_title')
                : t('tab5.mascal.r3_lost_title')}
              <Chip
                label={r3_absent_delta.is_active_mode ? t('tab5.mascal_whatif.r3_echelon_dual') : t('tab5.mascal_whatif.planning_sensitivity')}
                size="small"
                color={r3_absent_delta.is_active_mode ? 'error' : 'warning'}
                variant="outlined"
              />
            </AlertTitle>
            <Grid container spacing={2} sx={{ mt: 0.25 }}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('tab5.mascal_whatif.first_critical_r3')}
                </Typography>
                <Typography variant="h6">
                  {r3_absent_delta.first_critical_day_without_r3 !== null
                    ? `Day ${r3_absent_delta.first_critical_day_without_r3}`
                    : t('tab5.mascal_whatif.none')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r3_corps?.first_critical_day
                    ? t('tab5.mascal_whatif.vs_day_with_r3', { day: r3_corps.first_critical_day })
                    : t('tab5.mascal_whatif.with_r3_none')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('tab5.mascal_whatif.excess_t1_deaths')}
                </Typography>
                <Typography variant="h6" color="error.main">
                  ~{r3_absent_delta.excess_t1_deaths_estimate}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('tab5.mascal_whatif.t1_fraction_r3', { frac: r3_absent_delta.avg_t1_fraction })}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('tab5.mascal_whatif.unmet_routed_down')}
                </Typography>
                <Typography variant="h6" color="warning.main">
                  {r3_absent_delta.r2e_overflow_increase}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('tab5.mascal_whatif.overflow_r2e_stratevac')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('tab5.mascal_whatif.critical_days_r3')}
                </Typography>
                <Typography variant="h6">
                  {r3_absent_delta.critical_days_without_r3}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('tab5.mascal_whatif.vs_days_with_r3', { days: r3_absent_delta.baseline_critical_days })}
                </Typography>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {r3_absent_delta.is_active_mode
                ? t('tab5.mascal_whatif.r3_echelon_dual_note')
                : t('tab5.mascal_whatif.r3_planning_sens_note')
              }
            </Typography>
          </Alert>
        )}

        {/* Legend */}
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>{t('tab5.mascal_extra.legend')}</Typography>
          {[
            { key: 'legend_green',    bg: '#F1F8E9', color: 'inherit' },
            { key: 'legend_elevated', bg: '#FFFDE7', color: 'inherit' },
            { key: 'legend_high',     bg: '#FFF3E0', color: '#E65100' },
            { key: 'legend_critical', bg: '#FFEBEE', color: '#C62828' },
          ].map(({ key, bg, color }) => (
            <Box key={key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 14, bgcolor: bg, border: '1px solid #E0E0E0', borderRadius: 0.5 }} />
              <Typography variant="caption" style={{ color }}>{t(`tab5.mascal_extra.${key}`)}</Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {t('tab5.mascal_extra.source_footer', {
            brigades: total_brigades,
            brigadeWord: total_brigades !== 1
              ? (i18n.language === 'ro' ? 'brigăzi' : 'brigades')
              : (i18n.language === 'ro' ? 'brigadă' : 'brigade'),
            divisions: r2e_divisions?.length || 1,
            divisionWord: (r2e_divisions?.length || 1) !== 1
              ? (i18n.language === 'ro' ? 'divizii' : 'divisions')
              : (i18n.language === 'ro' ? 'divizie' : 'division'),
            los: Math.ceil(avg_los_days),
            avg_los: avg_los_days
          })}
        </Typography>
      </Paper>
    );
  };

  // ============================================================================
  // ENH-003 Phase 4: STRATEVAC SCHEDULE ANALYSIS  -  PHA QUEUE TIMELINE
  // Reads projections.mascal_analysis.stratevac_schedule_analysis (v5.16.0).
  // Shows per-airfield CSU/PHA queue day-by-day, departure flights, backflow to R3,
  // and wait-time DOW penalty. Positioned after renderStratevacCapacity().
  //
  // DOCTRINAL NOTE (RTO-MP-HFM-157 / AJMedP-2):
  //   The airfield itself has no organic beds. The CSU (Casualty Staging Unit)
  //   is deployed TO the airhead (APOD) to provide patient holding. "PHA beds"
  //   in this display = CSU holding capacity at the APOD. Source: "The CSU
  //   provides the holding capability at the airhead (APOD) to meet the STRATEVAC.
  //   This capability may be provided by the Role 3 [...] but ideally should be
  //   separate." (AJMedP-2 / RTO-MP-HFM-157)

  // ============================================================================
  // PHASE 4.5.5: HELPER: Aggregate Injury Mechanism Data
  // ============================================================================

  /**
   * Aggregate injury mechanism breakdown from all brigades/days
   * Shows actual injury patterns and blood consumption by mechanism
   */
  const aggregateInjuryMechanismData = () => {
    if (!projections?.by_brigade) return null;

    const aggregate = {
      total_t1_casualties: 0,
      total_prbc_units: 0,
      total_ffp_units: 0,
      total_mtp_patients: 0,
      by_mechanism: {},
      category: 'MIXED',
      surge_days: 0,
      surge_active: false
    };

    let daysWithData = 0;
    let categoryCount = {};

    // Aggregate from all brigades and all days
    for (const brigade of projections.by_brigade) {
      if (!brigade.by_day) continue;

      for (const dayData of brigade.by_day) {
        const bloodMech = dayData.resources?.blood_by_mechanism;
        if (!bloodMech) continue;

        daysWithData++;
        
        // Track T1 casualties
        aggregate.total_t1_casualties += bloodMech.totals?.t1_casualties || 0;
        aggregate.total_prbc_units += bloodMech.totals?.prbc_units || 0;
        aggregate.total_ffp_units += bloodMech.totals?.ffp_units || 0;
        aggregate.total_mtp_patients += bloodMech.totals?.mtp_patients || 0;

        // Track surge days
        if (bloodMech.surge?.active) {
          aggregate.surge_days++;
          aggregate.surge_active = true;
        }

        // Track category frequency
        if (bloodMech.category) {
          categoryCount[bloodMech.category] = (categoryCount[bloodMech.category] || 0) + 1;
        }

        // Aggregate by mechanism
        if (bloodMech.by_mechanism) {
          for (const [mechKey, mechData] of Object.entries(bloodMech.by_mechanism)) {
            if (!aggregate.by_mechanism[mechKey]) {
              aggregate.by_mechanism[mechKey] = {
                name: mechData.name,
                casualties: 0,
                mtp_patients: 0,
                prbc_units: 0,
                ffp_units: 0,
                platelets_units: 0,
                cryo_units: 0
              };
            }
            aggregate.by_mechanism[mechKey].casualties += mechData.casualties || 0;
            aggregate.by_mechanism[mechKey].mtp_patients += mechData.mtp_patients || 0;
            aggregate.by_mechanism[mechKey].prbc_units += mechData.prbc_units || 0;
            aggregate.by_mechanism[mechKey].ffp_units += mechData.ffp_units || 0;
            aggregate.by_mechanism[mechKey].platelets_units += mechData.platelets_units || 0;
            aggregate.by_mechanism[mechKey].cryo_units += mechData.cryo_units || 0;
          }
        }
      }
    }

    if (daysWithData === 0) return null;

    // Determine most common category
    let maxCount = 0;
    for (const [cat, count] of Object.entries(categoryCount)) {
      if (count > maxCount) {
        maxCount = count;
        aggregate.category = cat;
      }
    }

    // Calculate averages
    aggregate.average_prbc_per_t1 = aggregate.total_t1_casualties > 0 
      ? Math.round((aggregate.total_prbc_units / aggregate.total_t1_casualties) * 10) / 10 
      : 0;

    return aggregate;
  };

  // ============================================================================
  // PHASE 4.5.5: RENDER: Injury Mechanism Analysis
  // ============================================================================

  const renderInjuryMechanismAnalysis = () => {
    const mechanismData = aggregateInjuryMechanismData();
    if (!mechanismData) return null;

    const natoStandard = 2.0; // NATO standard PRBC per T1 casualty
    const percentDiff = ((mechanismData.average_prbc_per_t1 - natoStandard) / natoStandard) * 100;

    // Sort mechanisms by casualty count
    const sortedMechanisms = Object.entries(mechanismData.by_mechanism)
      .sort(([, a], [, b]) => b.casualties - a.casualties);

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon color="primary" />
          {t('tab5.injury.title')}
          {mechanismData.surge_active && (
            <Chip 
              label="SURGE DETECTED"
              size="small" 
              color="warning"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">{t('tab5.injury.op_env')}</Typography>
                <Typography variant="h6" color="primary.main">
                  {mechanismData.category}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {mechanismData.category === 'URBAN' && 'Urban combat pattern'}
                  {mechanismData.category === 'OPEN' && 'Open terrain pattern'}
                  {mechanismData.category === 'DEFENSIVE' && 'Defensive operations'}
                  {mechanismData.category === 'OFFENSIVE' && 'Offensive operations'}
                  {mechanismData.category === 'MIXED' && 'Mixed operations'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">{t('tab5.injury.avg_blood')}</Typography>
                <Typography variant="h6" color={percentDiff > 10 ? 'warning.main' : percentDiff < -10 ? 'success.main' : 'text.primary'}>
                  {mechanismData.average_prbc_per_t1} PRBC/casualty
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    NATO standard: {natoStandard} PRBC/casualty
                  </Typography>
                  <Chip 
                    label={`${percentDiff > 0 ? '+' : ''}${Math.round(percentDiff)}%`}
                    size="small"
                    color={percentDiff > 10 ? 'warning' : percentDiff < -10 ? 'success' : 'default'}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">{t('tab5.injury.total_t1')}</Typography>
                <Typography variant="h6">{formatNumber(mechanismData.total_t1_casualties)}</Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  MTP patients: {formatNumber(mechanismData.total_mtp_patients)} ({mechanismData.total_t1_casualties > 0 ? Math.round((mechanismData.total_mtp_patients / mechanismData.total_t1_casualties) * 100) : 0}%)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Surge Warning */}
        {mechanismData.surge_active && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>MSK Surge Detected</AlertTitle>
            Blood requirements increased on {mechanismData.surge_days} day(s) due to high casualty volume exceeding medical system capacity. 
            Surge multipliers: +50% PRBC, +30% FFP, +20% Platelets.
          </Alert>
        )}

        {/* Mechanism Breakdown Table */}
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          {t('tab5.injury.breakdown_title')}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('tab5.injury.col_mechanism')}</TableCell>
                <TableCell align="right">{t('tab5.injury.col_t1')}</TableCell>
                <TableCell align="right">{t('tab5.injury.col_pct')}</TableCell>
                <TableCell align="right">{t('tab5.injury.col_prbc')}</TableCell>
                <TableCell align="right">{t('tab5.injury.col_ffp')}</TableCell>
                <TableCell align="right">{t('tab5.injury.col_mtp')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedMechanisms.map(([key, mech]) => {
                const percentage = mechanismData.total_t1_casualties > 0 
                  ? Math.round((mech.casualties / mechanismData.total_t1_casualties) * 100) 
                  : 0;
                
                return (
                  <TableRow key={key}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{mech.name}</Typography>
                    </TableCell>
                    <TableCell align="right">{formatNumber(mech.casualties)}</TableCell>
                    <TableCell align="right">
                      <Chip label={`${percentage}%`} size="small" />
                    </TableCell>
                    <TableCell align="right">{formatNumber(mech.prbc_units)}</TableCell>
                    <TableCell align="right">{formatNumber(mech.ffp_units)}</TableCell>
                    <TableCell align="right">{formatNumber(mech.mtp_patients)}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>{formatNumber(mechanismData.total_t1_casualties)}</strong></TableCell>
                <TableCell align="right"><strong>100%</strong></TableCell>
                <TableCell align="right"><strong>{formatNumber(mechanismData.total_prbc_units)}</strong></TableCell>
                <TableCell align="right"><strong>{formatNumber(mechanismData.total_ffp_units)}</strong></TableCell>
                <TableCell align="right"><strong>{formatNumber(mechanismData.total_mtp_patients)}</strong></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Educational Note */}
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>{t('tab5.injury.edu_note_title')}</strong>{' '}
            {t('tab5.injury.edu_note_body', { category: mechanismData.category.toLowerCase() })}
          </Typography>
        </Alert>
      </Paper>
    );
  };

  // ============================================================================
  // PHASE 2.2: RENDER: Evacuation Routes Status

  // ============================================================================
  // PHASE 4.2: RENDER: Resupply Analysis
  // ============================================================================

  // BUG-197 FIX (March 24, 2026): Calculator writes 999 as sentinel for "infinite DOS"
  // when dailyPRBCConsumption = 0 (CSS/support force with no T1/T2 casualties).
  // Display as "N/A" (no blood consumption) rather than the raw sentinel value.
  // Threshold ≥ 100 captures both 999 (blood sentinel) and any similar high sentinel.
  const formatDOS = (val) => {
    if (val == null) return '--';
    if (val >= 100) return 'N/A';
    return val.toFixed(1);
  };

  const renderResupplyAnalysis = () => {
    const resupplySummary = projections?.medical_requirements?.resupply_summary;
    
    // Don't show if resupply not configured
    if (!resupplySummary?.configured) return null;
    
    const { total_events, total_units, events_by_day, dos_projection } = resupplySummary;
    
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingIcon color="primary" />
          {t('tab5.resupply.title')}
          {dos_projection?.stockout_day && (
            <Chip 
              label={`Stockout Day ${dos_projection.stockout_day}`}
              size="small" 
              color="error"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
        
        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('tab5.resupply.total_resupplies')}
                </Typography>
                <Typography variant="h4" color="primary">
                  {total_events}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 6, md: 3 }}>
            {/* BUG-T28 fix: add tooltip explaining RED threshold for PRBC Received */}
            <Tooltip
              title="RED = total PRBC units received via resupply. Blood is always shown in red (emergency item) regardless of quantity — it is never surplus. Compare to calculated demand to assess coverage."
              arrow
            >
              <Card variant="outlined" sx={{ cursor: 'help' }}>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('tab5.resupply.prbc_received')}
                  </Typography>
                  <Typography variant="h4" color="error">
                    {total_units.prbc}
                  </Typography>
                  <Typography variant="caption" color="error.light">
                    {t('tab5.resupply.units_via_resupply')}
                  </Typography>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
          
          <Grid size={{ xs: 6, md: 3 }}>
            {/* BUG-S81-002 FIX: dos_projection.minimum_dos = initial_blood_units / daily_rate = meaningless 39+.
                Correct minimum DOS = logisticsImpact.minDOS (min of commanded blood/supply/surgical DOS = 5 days). */}
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('tab5.resupply.min_dos')}
                </Typography>
                <Typography
                  variant="h4"
                  color={logisticsImpact?.minDOS == null ? 'text.secondary' : logisticsImpact.minDOS < 3 ? 'error' : logisticsImpact.minDOS < 5 ? 'warning' : 'success'}
                >
                  {logisticsImpact?.minDOS != null ? formatDOS(logisticsImpact.minDOS) : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            {/* BUG-S81-002 FIX: show commanded blood buffer (target MDOS) as Final DOS, not raw stock projection. */}
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('tab5.resupply.blood_dos')}
                </Typography>
                <Typography variant="h4" color="primary">
                  {logisticsImpact?.bloodDOS != null ? formatDOS(logisticsImpact.bloodDOS) : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* PHASE 4.6.5: Monte Carlo Probability Bands */}
        {dos_projection?.monte_carlo?.enabled && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>{t('tab5.mc_risk.title')}</AlertTitle>
            <Typography variant="body2" gutterBottom>
              Resupply delivery simulation accounting for route conditions, weather, and enemy threat.
              Environmental conditions: {dos_projection.monte_carlo.environmental_conditions?.weather || 'CLEAR'} weather, 
              {dos_projection.monte_carlo.environmental_conditions?.ad_threat || 'NONE'} AD threat.
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'error.light', color: 'error.contrastText' }}>
                  <Typography variant="caption">Worst Case (5th percentile)</Typography>
                  <Typography variant="h6">
                    {/* BUG-DOS-DISPLAY-001 FIX: formatDOS caps sentinel ≥100 as N/A */}
                    {formatDOS(dos_projection.monte_carlo.final_dos?.p5)} days DOS
                  </Typography>
                  <Typography variant="caption" display="block">
                    Min: {formatDOS(dos_projection.monte_carlo.minimum_dos?.p5)} days
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                  <Typography variant="caption">Expected (Median)</Typography>
                  <Typography variant="h6">
                    {formatDOS(dos_projection.monte_carlo.final_dos?.p50)} days DOS
                  </Typography>
                  <Typography variant="caption" display="block">
                    Min: {formatDOS(dos_projection.monte_carlo.minimum_dos?.p50)} days
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'success.light', color: 'success.contrastText' }}>
                  <Typography variant="caption">Best Case (95th percentile)</Typography>
                  <Typography variant="h6">
                    {formatDOS(dos_projection.monte_carlo.final_dos?.p95)} days DOS
                  </Typography>
                  <Typography variant="caption" display="block">
                    Min: {formatDOS(dos_projection.monte_carlo.minimum_dos?.p95)} days
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2">
                <strong>Stockout Probability:</strong> {Math.round(dos_projection.monte_carlo.stockout_probability * 100)}%
                {dos_projection.monte_carlo.average_stockout_day && (
                  <> (Average {dayLabel(Math.round(dos_projection.monte_carlo.average_stockout_day))})</>
                )}
              </Typography>
              <Chip 
                label={`${Math.round(dos_projection.monte_carlo.success_rate * 100)}% Success Rate`}
                color={dos_projection.monte_carlo.success_rate > 0.95 ? 'success' : dos_projection.monte_carlo.success_rate > 0.80 ? 'warning' : 'error'}
                size="small"
              />
            </Box>
          </Alert>
        )}
        
        {/* Stockout Warning */}
        {dos_projection?.stockout_day && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              onNavigate && (
                <Button 
                  color="inherit" 
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    onNavigate(3); // Navigate to Tab 4 (0-indexed)
                    // Scroll to resupply section after navigation
                    setTimeout(() => {
                      const resupplySection = document.getElementById('resupply-section');
                      if (resupplySection) {
                        resupplySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('tab5.resupply.fix_tab4')}
                </Button>
              )
            }
          >
            <AlertTitle>{t('tab5.resupply.stockout_title')}</AlertTitle>
            <Typography variant="body2" gutterBottom>
              CRITICAL: Blood supply exhausted on {dayLabel(dos_projection.stockout_day)}. 
              Immediate resupply or reduction in casualty generation required.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
              -> Click "Fix in Tab 4" to add resupply events or use Auto-Schedule.
            </Typography>
          </Alert>
        )}
        
        {/* Low DOS Warning */}
        {dos_projection?.minimum_dos != null && dos_projection.minimum_dos < 100 && dos_projection.minimum_dos < 5 && !dos_projection?.stockout_day && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            action={
              onNavigate && (
                <Button 
                  color="inherit" 
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    onNavigate(3); // Navigate to Tab 4
                    setTimeout(() => {
                      const resupplySection = document.getElementById('resupply-section');
                      if (resupplySection) {
                        resupplySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('tab5.resupply.add_resupply')}
                </Button>
              )
            }
          >
            <AlertTitle>{t('tab5.resupply.low_dos_title')}</AlertTitle>
            <Typography variant="body2">
              Blood supply drops to {formatDOS(dos_projection.minimum_dos)} days. 
              NATO minimum standard is 5 days. Additional resupply events recommended.
            </Typography>
          </Alert>
        )}
        
        {/* Resupply Events Timeline */}
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
          {t('tab5.resupply.events_timeline')}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Day</strong></TableCell>
                <TableCell><strong>Package</strong></TableCell>
                <TableCell><strong>Destination</strong></TableCell>
                <TableCell><strong>Method</strong></TableCell>
                {/* PHASE 4.6.5: Add Delivery Risk column if Monte Carlo enabled */}
                {dos_projection?.monte_carlo?.enabled && (
                  <TableCell><strong>Delivery Risk</strong></TableCell>
                )}
                <TableCell align="right"><strong>PRBC</strong></TableCell>
                <TableCell align="right"><strong>FFP</strong></TableCell>
                <TableCell align="right"><strong>PLT</strong></TableCell>
                {/* BUG-T29 fix: clarify this is blood DOS impact, not materiel DOS */}
                <TableCell align="right"><strong>Blood DOS Impact</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(events_by_day || {})
                .sort(([dayA], [dayB]) => parseInt(dayA) - parseInt(dayB))
                .flatMap(([day, dayEvents]) => 
                  dayEvents.map((event, index) => {
                    // Lookup package info from PUSH_PACKAGES
                    const packageInfo = PUSH_PACKAGES[event.push_package];
                    const packageLabel = packageInfo?.label || event.push_package;
                    const packageDescription = packageInfo?.description || '';
                    
                    return (
                      <TableRow key={`${day}-${index}`} hover>
                        <TableCell>{dayLabel(event.day)}</TableCell>
                        <TableCell>
                          <Tooltip title={packageDescription} arrow>
                            <span style={{ cursor: 'help' }}>
                              {packageLabel}
                            </span>
                          </Tooltip>
                        </TableCell>
                        {/* ENUM-SWEEP FIX: map raw MTF/delivery-method keys to human-readable labels. */}
                        <TableCell>{({ ROLE1: 'Role 1', ROLE2_BASIC: 'Role 2 Basic', ROLE2_ENHANCED: 'Role 2 Enhanced', ROLE3: 'Role 3' }[event.mtf_target] ?? event.mtf_target ?? 'All')}</TableCell>
                        <TableCell>
                          <Chip
                            label={event.delivery_method === 'DRONE'
                              ? `Drone${event.drone_type ? ' (' + event.drone_type.replace(/_DRONE$/,'').replace(/_/g,' ').toLowerCase() + ')' : ''}`
                              : ({ AIR: 'Air', GROUND: 'Ground', BLUE_LIGHT: 'Blue Light' }[event.delivery_method] ?? event.delivery_method)}
                            size="small"
                            color={event.delivery_method === 'BLUE_LIGHT' ? 'error' :
                                   event.delivery_method === 'AIR'        ? 'primary' :
                                   event.delivery_method === 'DRONE'      ? 'warning' : 'default'}
                            icon={event.delivery_method === 'AIR' || event.delivery_method === 'BLUE_LIGHT' || event.delivery_method === 'DRONE'
                              ? <AirplanemodeActiveIcon />
                              : <DirectionsCarIcon />}
                          />
                        </TableCell>
                        {/* PHASE 4.6.5: Show delivery risk if Monte Carlo enabled */}
                        {dos_projection?.monte_carlo?.enabled && (() => {
                          // Find matching event in Monte Carlo results
                          const riskEvent = dos_projection.monte_carlo.events_with_risk?.find(
                            e => e.day === event.day && e.push_package === event.push_package
                          );
                          const deliveryProb = riskEvent?.delivery_analysis?.probability;
                          const deliveryTime = riskEvent?.delivery_analysis?.time;
                          
                          if (deliveryProb) {
                            const riskColor = deliveryProb.risk_level === 'LOW' ? 'success' :
                                             deliveryProb.risk_level === 'MEDIUM' ? 'warning' :
                                             deliveryProb.risk_level === 'HIGH' ? 'error' : 'error';
                            
                            return (
                              <TableCell>
                                <Tooltip 
                                  title={
                                    <Box sx={{ p: 0.5 }}>
                                      <Typography variant="caption" display="block" fontWeight="bold">
                                        Delivery Analysis:
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                        Time Factors:
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        Route: {deliveryTime?.factors?.route || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        Weather: {deliveryTime?.factors?.weather || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        AD Threat: {deliveryTime?.factors?.ad_threat || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                        Total Time: {deliveryTime?.total_time_hours?.toFixed(1)}h
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                        Success Probability: {deliveryProb?.percentage}%
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                >
                                  <Chip 
                                    label={`${deliveryProb.percentage}%`}
                                    size="small"
                                    color={riskColor}
                                  />
                                </Tooltip>
                              </TableCell>
                            );
                          }
                          return <TableCell>--</TableCell>;
                        })()}
                        <TableCell align="right">
                          <Chip label={`+${event.units?.prbc || 0}`} size="small" color="error" />
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={`+${event.units?.ffp || 0}`} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={`+${event.units?.plt || 0}`} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="success.main" fontWeight="bold">
                            {event.dos_impact == null
                              ? '--'
                              // BUG-5E-003 FIX: >= 0 catches exact zero (previously fell through to +0.0 days).
                              // Any impact that rounds to "0.0" (i.e. < 0.05) is shown as "+<0.1 days".
                              : event.dos_impact >= 0 && event.dos_impact < 0.05
                                ? '+<0.1 days'
                                : `+${event.dos_impact.toFixed(1)} days`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
            </TableBody>
          </Table>
        </TableContainer>
        

      </Paper>
    );
  };

  // ============================================================================
  // PHASE 5.2: TACTICAL EVACUATION CAPACITY VS DEMAND TABLE
  // Data source: projections.by_brigade[*].by_day[*].tactical_evac_analysis (v5.12.0)
  // Aggregates across all brigades per operation day.
  // ARCH-010: arch010_dow_risk_informational displayed as footnote only  -  not applied to DOW.

  // ============================================================================
  // T5-007: RENDER: Sustainability Indicators
  // ============================================================================

  const renderSustainabilityIndicators = () => {
    if (!projections || !sustainabilityIndicators) return null;
    
    const si = sustainabilityIndicators;
    
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon color="primary" />
          {t('tab5.sustainability.title')}
          <Chip 
            label={si.overallStatus} 
            size="small" 
            color={si.overallStatus === 'SUSTAINABLE' ? 'success' : si.overallStatus === 'MARGINAL' ? 'warning' : 'error'}
            sx={{ ml: 'auto' }}
          />
        </Typography>
        
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color={`${getStatusColor(si.effectivenessPercent, { green: 70, yellow: 50 })}.main`}>
                  {si.effectivenessPercent}%
                </Typography>
                <Typography variant="caption">{t('tab5.sustainability.combat_effectiveness')}</Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={si.effectivenessPercent} 
                  color={getStatusColor(si.effectivenessPercent, { green: 70, yellow: 50 })}
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color={`${getStatusColor(si.goldenHour)}.main`}>
                  {si.goldenHour}%
                </Typography>
                <Typography variant="caption">{t('tab5.sustainability.golden_hour_ach')}</Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={si.goldenHour} 
                  color={getStatusColor(si.goldenHour)}
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color={si.supplyAdequate ? 'success.main' : 'error.main'}>
                  {si.minDOS}d
                </Typography>
                <Typography variant="caption">{t('tab5.sustainability.min_supply_days')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {si.supplyAdequate ? '" Adequate' : '"" Insufficient'} for {si.durationDays}d op
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="error.main">
                  {DATA_ICONS.TRENDING_DOWN} {formatNumber(si.netLoss)}
                </Typography>
                <Typography variant="caption">{t('tab5.sustainability.net_personnel_loss')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  ({formatNumber(si.totalCasualties)} cas - {formatNumber(si.rtd)} RTD)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ENH-018-E: Personnel composition breakdown  -  shows echelon support share of total strength */}
        {projections.support_casualties && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('tab5.sustainability.initial_composition')}
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ py: 0.5 }}>{t('tab5.sustainability.total_strength')}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>
                    {formatNumber(si.totalStrength)}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }} />
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, pl: 3, color: 'text.secondary' }}>
                    {t('tab5.sustainability.combat_formations')}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, color: 'text.secondary' }}>
                    {/* BUG-158 FIX: subtract BOTH hq command personnel AND support personnel.
                        Previously only support_casualties.personnel was subtracted, leaving the
                        580 HQ command troops uncounted and inflating "Combat formations". */}
                    {formatNumber(
                      Math.max(0, si.totalStrength
                        - (projections.hq_casualties?.total_hq_personnel || 0)
                        - (projections.support_casualties.personnel || 0))
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {si.totalStrength > 0
                        ? Math.round(Math.max(0, si.totalStrength
                            - (projections.hq_casualties?.total_hq_personnel || 0)
                            - (projections.support_casualties.personnel || 0)) / si.totalStrength * 100)
                        : 0}%
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, pl: 3, color: 'text.secondary' }}>
                    {t('tab5.sustainability.hq_staff')}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, color: 'text.secondary' }}>
                    {/* BUG-158 FIX: was reading support_casualties.hq_support_personnel (small
                        internal HQ staff ~20-40 people). Correct source is
                        hq_casualties.total_hq_personnel (actual command personnel e.g. 580). */}
                    {formatNumber(projections.hq_casualties?.total_hq_personnel || 0)}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {si.totalStrength > 0
                        ? Math.round((projections.hq_casualties?.total_hq_personnel || 0) / si.totalStrength * 100)
                        : 0}%
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, pl: 3, color: 'text.secondary' }}>
                    {t('tab5.sustainability.echelon_support')}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, color: 'text.secondary' }}>
                    {formatNumber(projections.support_casualties.echelon_support_personnel || 0)}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {si.totalStrength > 0
                        ? Math.round((projections.support_casualties.echelon_support_personnel || 0) / si.totalStrength * 100)
                        : 0}%
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>
    );
  };

  // ============================================================================
  // T5-003: RENDER: Daily Projection Chart (Simplified - no Recharts)

  // ============================================================================
  // E-02 to E-11: RENDER: Logistics DOS Timeline Graph (ARCH-005)
  // ============================================================================

  /**
   * Renders the day-by-day logistics supply depletion timeline.
   * Reads logisticsProjection.daily_simulation[] from psp_logistics_projection (Tab4 owner).
   * Shows 3 categories: Blood, NATO Medical Materiel, Surgical Capacity.
   * Color coding: GREEN >= commandedMDOS, AMBER >= minimum (5 days), RED < minimum.
   * Resupply spikes from logisticsProjection.resupply_events[].
   * Stockout markers from logisticsProjection.stockout_days.
   */
  const renderLogisticsTimeline = () => {
    // E-11: Null-safe  -  show guidance if no projection exists
    if (!logisticsProjection) {
      return (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon color="primary" />
            {t('tab5.logistics_timeline.title')}
          </Typography>
          <Alert severity="info">
            <AlertTitle>{t('tab5.logistics_timeline.no_projection')}</AlertTitle>
            Complete Tab 3 (Medical Configuration) and Tab 4 (Logistics Planning) first.
            Tab 4 Save &amp; Continue generates the day-by-day supply depletion timeline.
          </Alert>
        </Paper>
      );
    }

    const sim = logisticsProjection.daily_simulation || [];
    // BUG-124 FIX: Clamp commandedMDOS to minimum 1. When user sets target MDOS to 0,
    // commandedMDOS = 0.0 and getDOSBgColor(0) evaluates 0 >= 0 = true → GREEN.
    // Any DOS value including 0.0 falsely satisfied the threshold. Min of 1 ensures
    // the green threshold is always > 0.
    const commandedMDOS = Math.max(1, logisticsProjection.commanded_mdos_days ?? logisticsProjection.target_mdos_days ?? 7);
    const minimum = 5; // NATO minimum MDOS
    const opDuration = logisticsProjection.op_duration_days || sim.length || 7;
    const stockoutDays = logisticsProjection.stockout_days || {};
    const resupplyEvents = logisticsProjection.resupply_events || [];

    // Build set of resupply days for annotation
    const resupplyDaySet = new Set(resupplyEvents.map(e => e.day));

    // ACTION-2: Surgical DOS column removed from logistics timeline.
    // Surgical DOS = FST team capacity (staffing), not a consumable stock.
    // G4/J4 cannot action a surgical DOS deficit  -  fix is Tab 3 (add FST teams).
    // FST Surgical Throughput card in Logistics Impact Analysis is the correct location.
    const effectiveStockoutDays = { ...stockoutDays };

    // Determine color for a DOS value against commanded and minimum thresholds
    const getDOSColor = (dos) => {
      if (dos === null || dos === undefined) return 'text.secondary';
      if (dos >= commandedMDOS) return 'success.dark'; // dark text on light green  -  readable
      if (dos >= minimum) return '#FFFFFF';             // white on yellow/orange  -  WCAG AA pass
      return '#FFFFFF';                                 // white on red  -  WCAG AA pass
    };
    const getDOSBgColor = (dos) => {
      if (dos === null || dos === undefined) return 'inherit';
      if (dos >= commandedMDOS) return 'success.light';
      if (dos >= minimum) return 'warning.light';
      return 'error.light';
    };

    // E-10: Build per-category summary text
    // BUG-5A-001 FIX: distinguish AMBER (< NATO 5-day minimum, no stockout) from GREEN (adequate).
    // Previously "Adequate for full operation" was returned for ALL non-stockout cases, including
    // days where DOS dipped below the 5-day NATO minimum — contradicting the AMBER color shown.
    const buildSummary = (categoryKey, label, stockoutDay) => {
      if (stockoutDay != null) {
        const resupplyFixes = resupplyEvents.some(e => e.day < stockoutDay);
        if (resupplyFixes) {
          return `${label}: Initial stockout prevented by resupply. STOCKOUT Day ${stockoutDay}  -  resupply by Day ${Math.max(1, stockoutDay - 2)} to maintain Commanded MDOS.`;
        }
        return `${label}: STOCKOUT Day ${stockoutDay}. Resupply required by Day ${Math.max(1, stockoutDay - 2)} to maintain Commanded MDOS.`;
      }
      const minVal = sim.length > 0
        ? Math.min(...sim.map(d => d[categoryKey] ?? Infinity).filter(v => isFinite(v)))
        : null;
      if (minVal !== null) {
        // BUG-5A-001: AMBER case — below NATO minimum (5 days) but no stockout
        if (minVal < minimum) {
          return `${label}: ⚠ Below NATO minimum — drops to ${minVal.toFixed(1)} days (NATO min: ${minimum} days). No stockout, but resupply required to restore minimum stock.`;
        }
        return `${label}: Adequate for full operation. Minimum: ${minVal.toFixed(1)} days.`;
      }
      return `${label}: N/A  -  No simulation data.`;
    };

    const bloodSummary    = buildSummary('blood_remaining', 'Blood', effectiveStockoutDays.blood);
    const materielSummary = buildSummary('nato_medical_materiel_remaining', 'NATO Medical Materiel', effectiveStockoutDays.nato_medical_materiel);

    // BUG-5A-001 FIX: Compute minimum DOS for AMBER tier (< NATO 5-day minimum, no stockout).
    const minBloodDOS    = sim.length > 0
      ? Math.min(...sim.map(d => d.blood_remaining             ?? Infinity).filter(isFinite))
      : null;
    const minMaterielDOS = sim.length > 0
      ? Math.min(...sim.map(d => d.nato_medical_materiel_remaining ?? Infinity).filter(isFinite))
      : null;

    // Three-tier adequacy: RED = stockout, AMBER = DOS < NATO min (5d), GREEN = adequate
    const getAdequacyTier = (stockout, minDOS) => {
      if (stockout) return 'RED';
      if (minDOS !== null && minDOS < minimum) return 'AMBER';
      return 'GREEN';
    };

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon color="primary" />
          {t('tab5.logistics_timeline.title')}
          {/* Commanded MDOS reference */}
          <Chip
            label={`Commanded MDOS: ${commandedMDOS} days`}
            size="small"
            color="primary"
            sx={{ ml: 1 }}
          />
          {(effectiveStockoutDays.blood != null || effectiveStockoutDays.nato_medical_materiel != null) && (
            <Chip
              label="STOCKOUT DETECTED"
              size="small"
              color="error"
              sx={{ ml: 1 }}
            />
          )}
          {/* ENH-014c: Cold chain integrity alert chip */}
          {logisticsProjection.cold_chain_viability?.any_non_viable && (
            <Chip
              label="COLD CHAIN COMPROMISED"
              size="small"
              color="error"
              icon={<BloodtypeIcon />}
              sx={{ ml: 1 }}
            />
          )}
        </Typography>

        {/* E-06/E-09: Legend */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Chip size="small" label={`>= ${commandedMDOS} days (Commanded MDOS)`} sx={{ bgcolor: 'success.light', color: 'success.dark', fontWeight: 'bold' }} />
          <Chip size="small" label={`${minimum}-${commandedMDOS - 1} days (Below Commanded)`} sx={{ bgcolor: 'warning.light', color: 'warning.dark', fontWeight: 'bold' }} />
          <Chip size="small" label={`< ${minimum} days (Critical / Stockout)`} sx={{ bgcolor: 'error.light', color: 'error.dark', fontWeight: 'bold' }} />
          {resupplyEvents.length > 0 && (
            <Chip size="small" icon={<LocalShippingIcon />} label="Resupply day" color="primary" variant="outlined" />
          )}
        </Box>

        {/* E-04/E-05/E-07/E-08: Day-by-day table */}
        {sim.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  {/* ISSUE-04: sub-label clarifies Day 1 = 6.x is correct end-of-day value */}
                  <TableCell>
                    <strong>Day</strong>
                    <Typography variant="caption" display="block" color="text.secondary">
                      (end of day)
                    </Typography>
                  </TableCell>
                  {/* BUG-LABEL-001 FIX: clarify DOS = Days of Supply, not blood units */}
                  <TableCell align="center">
                    <Tooltip title="Days of Supply (DOS) — at current consumption rate, how many days the blood stock lasts. This is NOT the number of blood units in stock. High DOS when consumption is near zero (e.g. no surgical cases) may appear as 100+ days." arrow>
                      <Box sx={{ cursor: 'help' }}>
                        <strong>Blood — Days of Supply (DOS)</strong>
                        <Typography variant="caption" display="block" color="text.secondary">not blood units</Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  {/* ENH-010: weight columns */}
                  <TableCell align="center">
                    <Tooltip title="Blood product weight consumed that day (PRBC + FFP + Platelets), from ITEM_WEIGHTS_KG constants. Used for cold-chain convoy planning." arrow>
                      <Box sx={{ cursor: 'help' }}>
                        <strong>Blood (kg)</strong>
                        <Typography variant="caption" display="block" color="text.secondary">per day</Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Days of Supply (DOS) for medical materiel (crystalloid, tourniquets, dressings, etc.). Not a unit count — indicates how many days current stock lasts at the computed consumption rate." arrow>
                      <Box sx={{ cursor: 'help' }}>
                        <strong>Materiel — Days of Supply (DOS)</strong>
                        <Typography variant="caption" display="block" color="text.secondary">not item count</Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Medical materiel weight consumed that day (crystalloid, tourniquets, dressings, etc.), from ITEM_WEIGHTS_KG constants." arrow>
                      <Box sx={{ cursor: 'help' }}>
                        <strong>Materiel (kg)</strong>
                        <Typography variant="caption" display="block" color="text.secondary">per day</Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={`Standard trucks needed to deliver that day's resupply. Based on ${CONVOY_CAPACITY.STANDARD_TRUCK.effective_payload_kg.toLocaleString()} kg effective payload per truck (80% planning factor).`} arrow>
                      <Box sx={{ cursor: 'help' }}>
                        <strong>Trucks</strong>
                        <Typography variant="caption" display="block" color="text.secondary">resupply</Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center"><strong>Events</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sim.map((row) => {
                  const day = row.day;
                  const isResupplyDay = resupplyDaySet.has(day);
                  const bloodStockout    = effectiveStockoutDays.blood != null && day >= effectiveStockoutDays.blood;
                  const materielStockout = effectiveStockoutDays.nato_medical_materiel != null && day >= effectiveStockoutDays.nato_medical_materiel;

                  const bloodDOS    = row.blood_remaining ?? null;
                  const materielDOS = row.nato_medical_materiel_remaining ?? null;
                  // ENH-010: weight/volume per day (null-safe  -  older projections may not have these fields)
                  const bloodKg    = row.blood_weight_kg    ?? null;
                  const materielKg = row.materiel_weight_kg ?? null;
                  const trucksReqd = row.trucks_resupply ?? null;  // BUG-RESUPPLY-003: resupply delivery trucks (0 on non-resupply days)

                  return (
                    <TableRow key={day} sx={{ bgcolor: isResupplyDay ? 'primary.light' : 'inherit' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={isResupplyDay ? 'bold' : 'normal'}>
                          {dayLabel(day)}
                        </Typography>
                      </TableCell>

                      {/* Blood DOS */}
                      <TableCell align="center" sx={{ bgcolor: bloodStockout ? 'error.main' : getDOSBgColor(bloodDOS) }}>
                        {bloodStockout ? (
                          <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>STOCKOUT</Typography>
                        ) : bloodDOS != null ? (
                          <Typography variant="body2" color={getDOSColor(bloodDOS)} fontWeight="bold">
                            {bloodDOS.toFixed(1)}
                          </Typography>
                        ) : <Typography variant="caption" color="text.secondary">N/A</Typography>}
                      </TableCell>

                      {/* ENH-010: Blood kg/day */}
                      <TableCell align="center">
                        {bloodKg != null
                          ? <Typography variant="body2">{bloodKg.toFixed(1)}</Typography>
                          : <Typography variant="caption" color="text.secondary"> - </Typography>}
                      </TableCell>

                      {/* NATO Medical Materiel DOS */}
                      <TableCell align="center" sx={{ bgcolor: materielStockout ? 'error.main' : getDOSBgColor(materielDOS) }}>
                        {materielStockout ? (
                          <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>STOCKOUT</Typography>
                        ) : materielDOS != null ? (
                          <Typography variant="body2" color={getDOSColor(materielDOS)} fontWeight="bold">
                            {materielDOS.toFixed(1)}
                          </Typography>
                        ) : <Typography variant="caption" color="text.secondary">N/A</Typography>}
                      </TableCell>

                      {/* ENH-010: Materiel kg/day */}
                      <TableCell align="center">
                        {materielKg != null
                          ? <Typography variant="body2">{materielKg.toFixed(1)}</Typography>
                          : <Typography variant="caption" color="text.secondary"> - </Typography>}
                      </TableCell>

                      {/* BUG-RESUPPLY-003: Trucks column shows delivery trucks for resupply events only. */}
                      <TableCell align="center">
                        {trucksReqd != null && trucksReqd > 0
                          ? <Typography variant="body2" fontWeight="bold"
                              color={isResupplyDay ? 'primary.contrastText' : (trucksReqd <= 1 ? 'success.main' : trucksReqd <= 2 ? 'warning.main' : 'error.main')}>
                              {trucksReqd}
                            </Typography>
                          : <Typography variant="caption" color="text.secondary"> - </Typography>}
                      </TableCell>

                      {/* Events column */}
                      <TableCell align="center">
                        {isResupplyDay ? (
                          <Chip icon={<LocalShippingIcon />} label="Resupply" size="small" color="primary" />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="warning">
            <Typography variant="body2">
              No daily simulation data available. Ensure Tab 4 is saved after Tab 3 completes.
              The simulation requires casualty projections from Tab 3.
            </Typography>
          </Alert>
        )}

        {/* E-10: Per-category summary text */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
          <Typography variant="subtitle2" gutterBottom>{t('tab5.logistics_timeline.adequacy_summary')}</Typography>
          {[
            { text: bloodSummary,    tier: getAdequacyTier(stockoutDays.blood != null,                       minBloodDOS) },
            { text: materielSummary, tier: getAdequacyTier(stockoutDays.nato_medical_materiel != null, minMaterielDOS) },
          ].map(({ text, tier }, i) => (
            <Typography
              key={i}
              variant="body2"
              color={tier === 'RED' ? 'error.main' : tier === 'AMBER' ? 'warning.dark' : 'success.main'}
              sx={{ mb: 0.5, fontWeight: tier !== 'GREEN' ? 'bold' : 'normal' }}
            >
              {tier === 'RED' ? '[RED] ' : tier === 'AMBER' ? '[AMBER] ' : '[GREEN] '}{text}
            </Typography>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
            Commanded MDOS: {commandedMDOS} days (set in Tab 4) | Operation: {opDuration} days | 
            Minimum NATO standard: {minimum} days (AMedP-9.1)
          </Typography>
        </Box>

        {/* ENH-014c: Cold Chain Losses & Viability */}
        {(() => {
          const ccViability = logisticsProjection.cold_chain_viability;
          const ccLosses = logisticsProjection.cold_chain_losses || [];
          if (!ccViability) return null;

          const { any_non_viable, factors, meta } = ccViability;

          // Friendly display labels matching classVIII.js product keys
          const PRODUCT_LABELS = {
            prbc: 'PRBC', ffp: 'FFP', platelets: 'PLT',
            cryo: 'CRYO', fdp: 'FDP', fwb: 'FWB', ltowb: 'LTOWB'
          };

          const nonViableProducts = Object.entries(factors || {})
            .filter(([, factor]) => factor < 1)
            .map(([product]) => PRODUCT_LABELS[product] || product.toUpperCase());

          // All viable  -  green confirmation box
          if (!any_non_viable && ccLosses.length === 0) {
            return (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
                <Typography variant="subtitle2" sx={{ color: 'success.dark', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon fontSize="small" />
                  Cold Chain Integrity: INTACT  -  All resupply days viable
                </Typography>
                <Typography variant="caption" color="success.dark">
                  All blood products will arrive viable. MSR transit times are within AMedP-7.1 limits for all products.
                </Typography>
              </Box>
            );
          }

          return (
            <Box sx={{ mt: 2 }}>
              {/* Alert banner */}
              <Alert severity="error" icon={<BloodtypeIcon />} sx={{ mb: 2 }}>
                <AlertTitle>Cold Chain Integrity: COMPROMISED</AlertTitle>
                <Typography variant="body2" gutterBottom>
                  Blood resupply degraded  -  MSR transit time exceeds AMedP-7.1 limits
                  for: <strong>{nonViableProducts.length > 0 ? nonViableProducts.join(', ') : 'one or more products'}</strong>.
                  Affected products arrive at 0 viable units on loss days.
                </Typography>
                {meta?.worst_leg_hours != null && (
                  <Typography variant="body2">
                    Worst MSR leg: <strong>{meta.worst_leg_hours.toFixed(1)} h</strong> one-way.
                    Platelet limit: 4 h (most restrictive per AMedP-7.1).
                    Fix: reduce leg transit time in Tab 4 Section 4F.
                  </Typography>
                )}
              </Alert>

              {/* Per-product viability chips */}
              {factors && Object.keys(factors).length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {Object.entries(factors).map(([product, factor]) => (
                    <Chip
                      key={product}
                      label={`${PRODUCT_LABELS[product] || product.toUpperCase()}: ${factor >= 1 ? 'VIABLE' : 'NON-VIABLE'}`}
                      size="small"
                      color={factor >= 1 ? 'success' : 'error'}
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}

              {/* Loss events table */}
              {ccLosses.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('tab5.logistics_timeline.cold_chain_losses')}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell><strong>Day</strong></TableCell>
                          <TableCell><strong>Product</strong></TableCell>
                          <TableCell align="right"><strong>Ordered</strong></TableCell>
                          <TableCell align="right"><strong>Arrived Viable</strong></TableCell>
                          <TableCell align="right"><strong>Lost</strong></TableCell>
                          <TableCell><strong>Reason</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ccLosses.map((loss, idx) => {
                          const lostUnits = (loss.ordered || 0) - (loss.applied || 0);
                          const productLabel = PRODUCT_LABELS[loss.item] || loss.item?.toUpperCase() || '?';
                          const worstHours = loss.meta?.worst_leg_hours;
                          const limitHours = loss.meta?.max_hours;
                          return (
                            <TableRow key={idx} sx={{ bgcolor: 'error.light' }}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">{dayLabel(loss.day)}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip label={productLabel} size="small" color="error" />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">{loss.ordered ?? 0}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="success.dark" fontWeight="bold">
                                  {loss.applied ?? 0}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="error.dark" fontWeight="bold">
                                  {lostUnits}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {worstHours != null && limitHours != null
                                    ? `Transit ${worstHours.toFixed(1)} h > ${limitHours} h limit`
                                    : 'Transit time exceeded limit'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                    Binary degradation model per NATO AMedP-7.1  -  a product exceeding its transit limit arrives at 0 viable units. No partial credit.
                  </Typography>
                </>
              )}
            </Box>
          );
        })()}

        {/* ENH-010: Convoy Planning Panel */}
        {(() => {
          const dwv = logisticsProjection.daily_weight_volume;
          if (!dwv) return null;

          // Peak truck day from daily_simulation
          const peakDay = sim.reduce((best, row) => {
            const t = row.trucks_required ?? 0;
            return t > (best?.trucks_required ?? 0) ? row : best;
          }, null);

          // Average trucks across all days with data
          const daysWithTrucks = sim.filter(r => r.trucks_required != null);
          const avgTrucks = daysWithTrucks.length > 0
            ? daysWithTrucks.reduce((sum, r) => sum + r.trucks_required, 0) / daysWithTrucks.length
            : dwv.trucks_required;

          const truckColor = (n) => {
            if (n <= 1) return 'success.main';
            if (n <= 2) return 'warning.main';
            return 'error.main';
          };

          const bindingConstraint = dwv.trucks_weight >= dwv.trucks_volume ? 'weight' : 'volume';

          return (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              {/* BUG-086 FIX: "Average Daily Resupply" framing implied daily delivery.
                  Tab5 has no schedule access  -  always shows consumption reference data. */}
              <Typography variant="subtitle2" gutterBottom>
                {t('tab5.logistics_timeline.convoy_planning')}
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Tooltip title="Average daily blood product weight (PRBC + FFP + Platelets). Requires dedicated cold-chain (isothermal) vehicle." arrow>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Blood weight</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {dwv.blood_weight_kg.toFixed(1)} kg/day
                      </Typography>
                      <Typography variant="caption" color="warning.main" sx={{ fontStyle: 'italic' }}>
                        Cold chain required
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>

                <Grid size={{ xs: 6, sm: 3 }}>
                  <Tooltip title="Average daily NATO Medical Materiel weight (consumables, IV fluids, antibiotics, analgesics, surgical kits). Standard truck transport." arrow>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Materiel weight</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {dwv.materiel_weight_kg.toFixed(1)} kg/day
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>

                <Grid size={{ xs: 6, sm: 3 }}>
                  <Tooltip title={`Total daily resupply: ${dwv.total_weight_kg.toFixed(1)} kg / ${dwv.total_volume_m3.toFixed(3)} m3. Binding constraint: ${bindingConstraint}. Truck specs: ${CONVOY_CAPACITY.STANDARD_TRUCK.effective_payload_kg.toLocaleString()} kg / ${CONVOY_CAPACITY.STANDARD_TRUCK.effective_volume_m3} m3 effective payload (80% planning factor).`} arrow>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Total / Volume</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {dwv.total_weight_kg.toFixed(1)} kg
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dwv.total_volume_m3.toFixed(3)} m3
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>

                <Grid size={{ xs: 6, sm: 3 }}>
                  <Tooltip title={`Average ${avgTrucks.toFixed(1)} trucks/day across operation. Peak: Day ${peakDay?.day ?? '?'} (${peakDay?.trucks_required ?? '?'} trucks). Binding constraint: ${bindingConstraint}-limited. Vehicle: IVECO M250 / DAC 665T class.`} arrow>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Trucks required (avg)</Typography>
                      <Typography variant="body2" fontWeight="bold" color={truckColor(Math.ceil(avgTrucks))}>
                        {avgTrucks.toFixed(1)} trucks/day
                      </Typography>
                      {peakDay && (
                        <Typography variant="caption" color={truckColor(peakDay.trucks_required)}>
                          Peak: {dayLabel(peakDay.day)} ({peakDay.trucks_required} trucks)
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </Grid>
              </Grid>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                Standard truck: {CONVOY_CAPACITY.STANDARD_TRUCK.effective_payload_kg.toLocaleString()} kg / {CONVOY_CAPACITY.STANDARD_TRUCK.effective_volume_m3} m3 effective (80% planning factor) |
                Blood products require isothermal body vehicle  -  plan separate cold-chain asset.
                Constraint: {bindingConstraint}-limited.
              </Typography>

              {/* ARCH-002: convoy_plan from logisticsProjection */}
              {logisticsProjection.convoy_plan && (() => {
                const cp = logisticsProjection.convoy_plan;
                const alertSev = cp.warning === 'EXTREME' ? 'error' : cp.warning ? 'warning' : 'success';
                return (
                  <Alert severity={alertSev} sx={{ mt: 1.5 }} icon={false}>
                    <Typography variant="caption" fontWeight="bold" display="block">
                      MSR Travel Time  -  {cp.road_type_label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cp.distance_km} km  |  {cp.effective_speed_kmh} km/h  |  One-way {cp.one_way_hours.toFixed(1)} h  |  Round-trip {cp.round_trip_hours.toFixed(1)} h
                      &nbsp;|&nbsp; <strong>Lead time: {cp.lead_time_days} day{cp.lead_time_days !== 1 ? 's' : ''} advance order</strong>
                      {' '}{cp.same_day_feasible ? ' |  Same-day delivery feasible.' : ' |  Overnight convoy required.'}
                    </Typography>
                    {cp.warning_label && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        {cp.warning_label}
                      </Typography>
                    )}
                  </Alert>
                );
              })()}
            </Box>
          );
        })()}
      </Paper>
    );
  };

  // ============================================================================
  // T5-009: RENDER: G1 Personnel Summary
  // ============================================================================

  const renderG1Summary = () => {
    if (!projections || !g1Summary) return null;
    
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon color="primary" />
          {t('tab5.g1.title')}
        </Typography>
        
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('tab5.g1.total_requirement')}
                </Typography>
                <Typography variant="h3" color="error.main">
                  {formatNumber(g1Summary.totalReplacement)}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>KIA (immediate)</TableCell>
                      <TableCell align="right">{formatNumber(g1Summary.breakdown.kia)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>DOW (immediate)</TableCell>
                      <TableCell align="right">{formatNumber(g1Summary.breakdown.dow)}</TableCell>
                    </TableRow>
                    <TableRow>
                      {/* QM-5G-001 FIX: was hardcoded "40%". Compute actual non-RTD rate from
                          g1Summary proportional RTD split and actual total WIA from projections. */}
                      <TableCell>
                        {`WIA (non-RTD, ${
                          (projections?.by_corps?.total_wia ?? 0) > 0
                            ? Math.round(g1Summary.breakdown.wia_permanent / projections.by_corps.total_wia * 100)
                            : '?'
                        }%)`}
                      </TableCell>
                      <TableCell align="right">{formatNumber(g1Summary.breakdown.wia_permanent)}</TableCell>
                    </TableRow>
                    <TableRow>
                      {/* QM-5G-001 FIX: was hardcoded "15%". Compute actual non-RTD rate from
                          g1Summary proportional RTD split and actual total DNBI from projections. */}
                      <TableCell>
                        {`DNBI (non-RTD, ${
                          (projections?.by_corps?.total_dnbi ?? 0) > 0
                            ? Math.round(g1Summary.breakdown.dnbi_permanent / projections.by_corps.total_dnbi * 100)
                            : '?'
                        }%)`}
                      </TableCell>
                      <TableCell align="right">{formatNumber(g1Summary.breakdown.dnbi_permanent)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('tab5.g1.replacement_timeline')}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">{t('tab5.g1.immediate')}</Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={g1Summary.totalReplacement > 0 ? (g1Summary.priority.immediate / g1Summary.totalReplacement) * 100 : 0}
                    color="error"
                    sx={{ height: 10, borderRadius: 1, mb: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary">{formatNumber(g1Summary.priority.immediate)} personnel</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">{t('tab5.g1.short_term')}</Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={g1Summary.totalReplacement > 0 ? (g1Summary.priority.shortTerm / g1Summary.totalReplacement) * 100 : 0}
                    color="warning"
                    sx={{ height: 10, borderRadius: 1, mb: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary">{formatNumber(g1Summary.priority.shortTerm)} personnel</Typography>
                </Box>
                <Box>
                  <Typography variant="body2">{t('tab5.g1.long_term')}</Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={g1Summary.totalReplacement > 0 ? (g1Summary.priority.longTerm / g1Summary.totalReplacement) * 100 : 0}
                    color="info"
                    sx={{ height: 10, borderRadius: 1, mb: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary">{formatNumber(g1Summary.priority.longTerm)} personnel</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // ============================================================================
  // HNS-001: RENDER: Host Nation Support Panel
  // Displays HNS configuration and its effect on this operation's medical system.
  // Data sources:
  //   scenario.operation_parameters.hns_config  -  level, enabled, modifiers
  //   HNS_SUPPORT_LEVELS[level]  -  nominal (pre-reliability) values for comparison
  //   projections.by_brigade[*].by_day[*].dnbi.hns_absorbed  -  total absorbed DNBI
  // Only renders when HNS is enabled and support_level is not NONE.
  // ============================================================================

  const renderHNSPanel = () => {
    const hnsConfig = scenario?.operation_parameters?.hns_config;
    if (!hnsConfig?.enabled || hnsConfig?.support_level === 'NONE') return null;

    const level    = hnsConfig.support_level;
    const modifiers = hnsConfig.modifiers;
    const levelDef  = HNS_SUPPORT_LEVELS?.[level];
    if (!levelDef || !modifiers) return null;

    // Aggregate total DNBI absorbed across all brigades and all days
    let totalDNBIAbsorbed = 0;
    if (projections?.by_brigade) {
      for (const brigade of projections.by_brigade) {
        const days = brigade.by_day || brigade.daily_casualties || [];
        for (const d of days) {
          totalDNBIAbsorbed += d.dnbi?.hns_absorbed || 0;
        }
      }
    }
    // BUG-5H-001: If calculator did not populate hns_absorbed fields (older output format),
    // fall back to an estimate: totalDNBI × absorption rate. This keeps the count consistent
    // with the rate shown above it (rate is always present via modifiers).
    if (totalDNBIAbsorbed === 0 && (modifiers.dnbi_absorption || 0) > 0) {
      const corpsDNBI = projections?.by_corps?.total_dnbi || 0;
      if (corpsDNBI > 0) {
        totalDNBIAbsorbed = Math.round(corpsDNBI * modifiers.dnbi_absorption);
      }
    }

    const reliabilityPct = Math.round((modifiers.reliability ?? 1) * 100);

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalHospitalIcon color="primary" />
          {t('tab5.hns.title', { level: levelDef.label })}
          <Chip
            label={level}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ ml: 'auto' }}
          />
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          {levelDef.description}
        </Alert>

        <Grid container spacing={2}>
          {/* Bed Augmentation */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('tab5.hns.nominal_beds')}
                </Typography>
                <Typography variant="h4">
                  {levelDef.civilian_hospital_beds}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Reliability-adjusted
                </Typography>
                <Typography variant="h5" color="success.main">
                  +{modifiers.bed_augmentation_absolute}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  @ {reliabilityPct}% reliability
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Civilian Ambulances */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('tab5.hns.nominal_ambulances')}
                </Typography>
                <Typography variant="h4">
                  {levelDef.civilian_ambulances}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Reliability-adjusted
                </Typography>
                <Typography variant="h5" color="success.main">
                  +{modifiers.civilian_ambulances}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  @ {reliabilityPct}% reliability
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* DNBI Absorption */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('tab5.hns.dnbi_absorption')}
                </Typography>
                <Typography variant="h4">
                  {Math.round(modifiers.dnbi_absorption * 100)}%
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total DNBI absorbed
                </Typography>
                <Typography variant="h5" color="success.main">
                  {totalDNBIAbsorbed > 0 ? totalDNBIAbsorbed : '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  casualties diverted to HN system
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Reliability */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('tab5.hns.reliability')}
                </Typography>
                <Typography
                  variant="h4"
                  color={
                    reliabilityPct >= 90 ? 'success.main' :
                    reliabilityPct >= 80 ? 'warning.main' : 'error.main'
                  }
                >
                  {reliabilityPct}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={reliabilityPct}
                  color={
                    reliabilityPct >= 90 ? 'success' :
                    reliabilityPct >= 80 ? 'warning' : 'error'
                  }
                  sx={{ mt: 1, height: 8, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  probability HNS delivers as agreed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <AlertTitle>Planning Note (AJP-4.10C Section 5.4)</AlertTitle>
          HNS delivery is not guaranteed. Civilian hospital staff may not report under fire,
          facilities may be damaged, and ambulance crews may be diverted to civilian casualties.
          The reliability factor ({reliabilityPct}%) is already applied to all values above.
          In contested environments, actual HNS may be lower still.
        </Alert>
      </Paper>
    );
  };

  // ============================================================================
  // ENH-019: RENDER: MROE / EPW & Civilian Casualty Panel
  // Data sources:
  //   scenario.operation_parameters.mroe_config   -  epw_level, civilian_level, enabled
  //   projections.mascal_analysis.mroe_load       -  calculator output (v5.38.0+)
  // Renders when MROE is enabled. Shows EPW bed load, MP wing flag, civilian impact.
  // ============================================================================

  const renderMROEPanel = () => {
    const mroeConfig = scenario?.operation_parameters?.mroe_config;
    if (!mroeConfig?.enabled && !mroeConfig?.state) return null;

    const mroeLoad = projections?.mascal_analysis?.mroe_load;
    // Support both new (epw_volume/civilian_density) and legacy (epw_level/civilian_level) shapes
    const epwLevel = mroeConfig.epw_volume    || mroeConfig.epw_level    || 'NONE';
    const civLevel = mroeConfig.civilian_density || mroeConfig.civilian_level || 'NONE';
    const epwDef   = EPW_THREAT_LEVELS?.[epwLevel];
    const civDef   = CIVILIAN_CASUALTY_LEVELS?.[civLevel];
    // hasCalcData: calculator has run and returned a non-sentinel mroe_load
    const hasCalcData = mroeLoad && !mroeLoad.not_configured;

    // V3.1: matrix state and modified cell count
    const mroeStateDef = mroeConfig.state ? MROE_STATES[mroeConfig.state] : null;
    let modifiedCellCount = 0;
    if (mroeConfig.state && mroeConfig.matrix) {
      const defaultMx = MROE_DEFAULT_MATRICES[mroeConfig.state] || {};
      for (const cat of PATIENT_CATEGORIES) {
        if (cat.lockTier === 'A') continue;
        const cell    = mroeConfig.matrix[cat.key] || {};
        const defCell = defaultMx[cat.key] || {};
        if ((cell.lle    || 'Y') !== (defCell.lle    || 'Y')) modifiedCellCount++;
        if ((cell.nonLle || 'Y') !== (defCell.nonLle || 'Y')) modifiedCellCount++;
      }
    }

    const cellColor = (val) => {
      if (val === 'Y')  return 'success';
      if (val === 'Y*') return 'warning';
      if (val === 'N')  return 'error';
      return 'default';
    };

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MilitaryTechIcon color="warning" />
          {t('tab5.mroe_extra.title')}
          {mroeStateDef && (
            <Chip
              label={`MRoE State: ${mroeStateDef.label}`}
              size="small"
              color={mroeStateDef.color}
              sx={{ ml: 1 }}
            />
          )}
          {modifiedCellCount > 0 && (
            <Chip
              label={`${modifiedCellCount} cell${modifiedCellCount !== 1 ? 's' : ''} modified`}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          )}
          <Chip
            label={`EPW: ${epwLevel}`}
            size="small"
            color={epwLevel === 'NONE' ? 'default' : 'warning'}
            variant="outlined"
            sx={{ ml: 'auto', mr: 1 }}
          />
          <Chip
            label={`CIV: ${civLevel}`}
            size="small"
            color={civLevel === 'NONE' ? 'default' : 'info'}
            variant="outlined"
          />
        </Typography>

        {/* V3.1: Treatment Authorization Matrix (read-only mini-table) */}
        {mroeConfig.state && mroeConfig.matrix && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('tab5.mroe_extra.treatment_matrix')}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '40%' }}>Patient Category</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>LLE</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Non-LLE</TableCell>
                    {mroeConfig.state && <TableCell align="right" sx={{ fontWeight: 600 }}>Ref</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {PATIENT_CATEGORIES.map((cat) => {
                    const cell    = mroeConfig.matrix[cat.key] || {};
                    const defCell = (MROE_DEFAULT_MATRICES[mroeConfig.state] || {})[cat.key] || {};
                    const lleVal    = cell.lle    || 'Y';
                    const nonLleVal = cell.nonLle || 'Y';
                    const lleModified    = cat.lockTier !== 'A' && lleVal    !== (defCell.lle    || 'Y');
                    const nonLleModified = cat.lockTier !== 'A' && nonLleVal !== (defCell.nonLle || 'Y');
                    return (
                      <TableRow key={cat.key} sx={{ opacity: cat.lockTier === 'A' ? 0.85 : 1 }}>
                        <TableCell>
                          <Typography variant="body2">{cat.label}</Typography>
                          {cat.gcRef && (
                            <Typography variant="caption" color="text.secondary">
                              {cat.gcRef}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={lleModified ? `Modified from default (${defCell.lle || 'Y'})` : (cell.lleConds?.length > 0 ? cell.lleConds.join(', ') : '')}
                          >
                            <Chip
                              label={lleVal}
                              size="small"
                              color={cellColor(lleVal)}
                              variant={lleModified ? 'filled' : 'outlined'}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={nonLleModified ? `Modified from default (${defCell.nonLle || 'Y'})` : (cell.nonLleConds?.length > 0 ? cell.nonLleConds.join(', ') : '')}
                          >
                            <Chip
                              label={nonLleVal}
                              size="small"
                              color={cellColor(nonLleVal)}
                              variant={nonLleModified ? 'filled' : 'outlined'}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          {cat.lockTier === 'A' && (
                            <Typography variant="caption" color="text.secondary">locked</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {modifiedCellCount > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {modifiedCellCount} cell{modifiedCellCount !== 1 ? 's' : ''} modified from MRoE {mroeConfig.state} standard.
                All modifications require OPORD authorization and LEGAD verification.
              </Alert>
            )}
          </Box>
        )}

        {/* Planning gap warning  -  MROE enabled but calculator hasn't run yet */}
        {mroeLoad?.not_configured && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>EPW stream not planned</AlertTitle>
            MROE is enabled but no EPW/civilian load has been computed. Recalculate after
            configuring MROE in Tab 2. (AJMedP-1 Section 3.21)
          </Alert>
        )}

        {/* V4-CAL-001: Two-stream patient load display */}
        <Grid container spacing={2}>

          {/* EPW STREAM — additive, does NOT compete with military surgical */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card variant="outlined" sx={{ borderColor: 'warning.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" color="warning.main">
                    {t('tab5.mroe_extra.epw_stream')}
                  </Typography>
                  <Chip label="ADDITIVE" size="small" color="warning" variant="outlined" />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {epwDef?.label || epwLevel}  -  {epwDef?.description || ''}
                </Typography>

                {hasCalcData && mroeLoad.epw_stream ? (
                  <Box sx={{ mt: 1.5 }}>
                    {/* Chain visualization */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                      <Chip
                        label={`${Math.round(mroeLoad.epw_stream.daily_patients * 10) / 10} EPW/day`}
                        size="small" color="warning" variant="filled"
                      />
                      <Typography variant="caption" color="text.secondary">→ collection</Typography>
                      <Chip
                        label={`${mroeLoad.epw_stream.collection_hold_days}d hold`}
                        size="small" variant="outlined"
                      />
                      <Typography variant="caption" color="text.secondary">→ EPW section</Typography>
                      <Chip
                        label={`+${mroeLoad.epw_stream.section_beds_daily} beds/day`}
                        size="small" color="warning" variant="filled"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                      x1.4 LOS multiplier  -  no RTD (GC III Art.12 repatriation)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Does NOT compete with military T1 surgical slots
                    </Typography>
                    {mroeLoad.epw_stream.mp_escort_required && (
                      <Chip
                        label="MP escort required" size="small" color="warning"
                        variant="outlined" sx={{ mt: 0.75 }}
                      />
                    )}
                  </Box>
                ) : (
                  !hasCalcData && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Run calculation to see EPW load figures.
                    </Typography>
                  )
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* CIVILIAN STREAM — enters main chain at R2B/R2E, competes with military */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card variant="outlined" sx={{ borderColor: 'info.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" color="info.main">
                    {t('tab5.mroe_extra.civilian_stream')}
                  </Typography>
                  <Chip label="COMPETES at R2B/R2E" size="small" color="info" variant="outlined" />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {civDef?.label || civLevel}  -  {civDef?.description || ''}
                </Typography>

                {hasCalcData && mroeLoad.civilian_stream ? (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {mroeLoad.civilian_stream.daily_patients} civilian patients / day
                      {mroeLoad.civilian_stream.triage_profile && (
                        <> ({mroeLoad.civilian_stream.triage_profile} triage profile)</>
                      )}
                    </Typography>
                    {/* Triage breakdown */}
                    {mroeLoad.civilian_stream.triage_profile && (
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={`T1: ${mroeLoad.civilian_stream.t1_surgical_daily}/day`}
                            size="small" color="error" variant="outlined"
                          />
                          <Chip
                            label={`T2: ${mroeLoad.civilian_stream.t2_ward_daily}/day`}
                            size="small" color="warning" variant="outlined"
                          />
                          <Chip
                            label={`T3 HNS: ${mroeLoad.civilian_stream.t3_hns_daily}/day`}
                            size="small" color="success" variant="outlined"
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                          T1 competes for R2B/R2E surgical slots.
                          T2 competes for R2B/R2E ward beds.
                          T3 transferred to HNS  -  no military bed demand.
                        </Typography>
                      </Box>
                    )}
                    {!mroeLoad.civilian_stream.triage_profile && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        Capacity impact: {mroeLoad.capacity_impact_pct}%
                      </Typography>
                    )}
                  </Box>
                ) : (
                  !hasCalcData && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Run calculation to see civilian load figures.
                    </Typography>
                  )
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Operational planning flags */}
        {hasCalcData && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {mroeLoad.mp_wing_required && (
              <Alert severity="warning" icon={<MilitaryTechIcon />}>
                <AlertTitle>MP Security Wing Required (GC III Art.12)</AlertTitle>
                EPW stream requires a dedicated MP-guarded ward at Role 2/3 facilities.
                Coordinate with G2 and Provost Marshal. Ground evacuation only  -  EPW are
                not eligible for MEDEVAC.
              </Alert>
            )}
            {mroeLoad.humanitarian_crisis && (
              <Alert severity="error" icon={<ReportProblemIcon />}>
                <AlertTitle>Humanitarian Crisis Conditions (AJP-4.10)</AlertTitle>
                Civilian casualty load may overwhelm military medical capacity. Request
                CMO coordination and NGO/ICRC integration.
              </Alert>
            )}
            {mroeLoad.civilian_cmo_required && !mroeLoad.humanitarian_crisis && (
              <Alert severity="info" icon={<GroupIcon />}>
                Civil-Military Operations (CMO) coordination required for civilian patient
                handover to host-nation system.
              </Alert>
            )}
            {mroeLoad.epw_dedicated_medical && (
              <Alert severity="info" icon={<LocalHospitalIcon />}>
                EPW volume requires a dedicated EPW medical section at Role 2/3. Request
                EPW medical augmentation through medical channels.
              </Alert>
            )}
          </Box>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>Planning Note (AJMedP-1 Section 3.21  |  MC 0326/4)</AlertTitle>
          During declared MASCAL, all MROE tiers are suspended  -  triage by clinical need
          only (AMedP-8.19 Section 4.5). Acute emergency treatment cannot be denied within
          available capacity for any person.
        </Alert>
      </Paper>
    );
  };

  // ============================================================================
  // T5-006: RENDER: RTD Timeline
  // ============================================================================

  const renderRTDTimeline = () => {
    if (!projections) return null;
    
    const corps = projections.by_corps || {};
    const totalRTD = corps.total_rtd || 0;
    const totalWIA = corps.total_wia || 0;
    const totalDNBI = corps.total_dnbi || 0;
    
    // P1-05 FIX: Derive THP timeframe from scenario config and read RTD rates from constants.
    // Old code used hardcoded rates (T3=0.60, T2=0.15, T1=0.05) and hardcoded triage proportions.
    const thpConfig = scenario?.logistics?.theatre_holding_policy;
    const thpDaysRaw = thpConfig?.policy_days ||
      (thpConfig?.policy?.match(/THP_(\d+)_DAY/)?.[1]
        ? parseInt(thpConfig.policy.match(/THP_(\d+)_DAY/)[1], 10)
        : 7);
    let rtdTimeframe;
    if (thpDaysRaw <= 3) rtdTimeframe = 'within_72h';
    else if (thpDaysRaw <= 7) rtdTimeframe = 'within_7d';
    else if (thpDaysRaw <= 15) rtdTimeframe = 'within_15d';
    else rtdTimeframe = 'within_30d';

    // Read RTD rates from theatreHoldingPolicy.js constants for the active THP timeframe.
    // Triage proportions (T3=30%, T2=25%, T1=15% of WIA) are NATO planning estimates;
    // they remain as estimates since by_corps does not carry per-triage WIA counts.
    // APP_RULES 6.1: No ?? fallbacks  -  missing constants must surface visibly, not silently.
    // React render cannot throw; return error UI so constants failure is visible to user.
    const t3RtdRate = RTD_RATES?.BY_TRIAGE_CATEGORY?.T3_MINIMAL?.rtd_rates?.[rtdTimeframe];
    const t2RtdRate = RTD_RATES?.BY_TRIAGE_CATEGORY?.T2_DELAYED?.rtd_rates?.[rtdTimeframe];
    const t1RtdRate = RTD_RATES?.BY_TRIAGE_CATEGORY?.T1_IMMEDIATE?.rtd_rates?.[rtdTimeframe];
    if (t3RtdRate === undefined || t2RtdRate === undefined || t1RtdRate === undefined) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          RTD rates could not be read from theatreHoldingPolicy.js constants
          for THP timeframe &apos;{rtdTimeframe}&apos;.
          Check RTD_RATES.BY_TRIAGE_CATEGORY structure.
          (T1={String(t1RtdRate)} T2={String(t2RtdRate)} T3={String(t3RtdRate)})
        </Alert>
      );
    }

    // RTD breakdown by category.
    // BUG-5G-001 FIX: Raw breakdown uses fixed NATO triage proportion estimates
    // (T3=30%, T2=25%, T1=15% of WIA). These DO NOT match the calculator's actual
    // per-day triage distribution → row sum diverged from corps.total_rtd headline.
    // Fix: compute raw proportional breakdown, then scale all rows so they sum exactly
    // to totalRTD. Rows remain estimates of category split; total is authoritative.
    const rawRtd = {
      wia_t3:       (totalWIA * 0.30) * t3RtdRate,
      wia_t2:       (totalWIA * 0.25) * t2RtdRate,
      wia_t1:       (totalWIA * 0.15) * t1RtdRate,
      dnbi_minor:   (totalDNBI * 0.50) * 0.95,
      dnbi_moderate:(totalDNBI * 0.30) * 0.70
    };
    const rawSum = Object.values(rawRtd).reduce((s, v) => s + v, 0);
    const scale  = rawSum > 0 ? totalRTD / rawSum : 1;
    const rtdBreakdown = {
      wia_t3:       Math.round(rawRtd.wia_t3       * scale),
      wia_t2:       Math.round(rawRtd.wia_t2       * scale),
      wia_t1:       Math.round(rawRtd.wia_t1       * scale),
      dnbi_minor:   Math.round(rawRtd.dnbi_minor   * scale),
      dnbi_moderate:Math.round(rawRtd.dnbi_moderate* scale)
    };
    // BUG-5G-001 ROUNDING FIX: Math.round on 5 independent cells can leave a residual
    // vs totalRTD (up to ±5). Absorb it into the largest cell so rows sum exactly.
    const roundedSum = Object.values(rtdBreakdown).reduce((s, v) => s + v, 0);
    const residual = totalRTD - roundedSum;
    if (residual !== 0) {
      const largestKey = Object.entries(rawRtd).sort((a, b) => b[1] - a[1])[0][0];
      rtdBreakdown[largestKey] = (rtdBreakdown[largestKey] || 0) + residual;
    }
    
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HealingIcon color="success" />
          {t('tab5.rtd_extra.title')}
        </Typography>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>{t('tab5.rtd_extra.edu_note')}</AlertTitle>
          RTD rates are based on historical data and NATO medical doctrine. Actual rates vary significantly 
          based on injury type, medical care quality, and unit morale. DNBI typically has higher RTD rates 
          than battle casualties.
        </Alert>
        
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined" sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3">{formatNumber(totalRTD)}</Typography>
                <Typography variant="subtitle1">{t('tab5.rtd_extra.total_rtd')}</Typography>
                <Typography variant="body2">
                  ({totalWIA + totalDNBI > 0 ? Math.round((totalRTD / (totalWIA + totalDNBI)) * 100) : 0}% of WIA + DNBI)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, md: 8 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Est. RTD</TableCell>
                    <TableCell align="right">Timeline</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>WIA T3 (Minimal)</TableCell>
                    <TableCell align="right">{formatNumber(rtdBreakdown.wia_t3)}</TableCell>
                    <TableCell align="right">1-7 days</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>WIA T2 (Delayed)</TableCell>
                    <TableCell align="right">{formatNumber(rtdBreakdown.wia_t2)}</TableCell>
                    <TableCell align="right">7-30 days</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>WIA T1 (Urgent)</TableCell>
                    <TableCell align="right">{formatNumber(rtdBreakdown.wia_t1)}</TableCell>
                    <TableCell align="right">30-90 days</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>DNBI Minor</TableCell>
                    <TableCell align="right">{formatNumber(rtdBreakdown.dnbi_minor)}</TableCell>
                    <TableCell align="right">1-3 days</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>DNBI Moderate</TableCell>
                    <TableCell align="right">{formatNumber(rtdBreakdown.dnbi_moderate)}</TableCell>
                    <TableCell align="right">3-14 days</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // ============================================================================
  // RENDER: Reports Section
  // ============================================================================

  // BUG-TAB6-001: Convert internal unit ID (e.g. role1_div_bde1_battalion_177) to
  // a readable label for display. Internal constants are unchanged.
  const humanizeUnitId = (id) => {
    if (!id || typeof id !== 'string') return '—';
    return id
      .replace(/^role(\d+)_/, 'Role $1 › ')
      .replace(/div_/, 'Div › ')
      .replace(/bde(\d+)_/, 'Bde $1 › ')
      .replace(/battalion_(\d+)/, 'Bn $1')
      .replace(/_(\d+)/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
  };

  // ENH-002: Attachment / Detachment Timeline panel
  // Reads per_facility_evacuation_assets from medical_configuration and
  // renders a compact table showing which assets are attached or detached
  // per facility across the operation duration.
  const renderAttachmentTimeline = () => {
    const perFacility = scenario?.medical_configuration?.per_facility_evacuation_assets;
    if (!Array.isArray(perFacility) || perFacility.length === 0) return null;

    // Collect only facilities that have at least one attachment or detachment
    const activeFacilities = perFacility.filter(f =>
      (Array.isArray(f.attached_assets)  && f.attached_assets.length  > 0) ||
      (Array.isArray(f.detached_assets)  && f.detached_assets.length  > 0)
    );
    if (activeFacilities.length === 0) return null;

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          {t('tab5.attachments.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Assets augmenting (+) or temporarily absent from (-) organic facility capacity.
          Effective capacity on each day = organic + attached - detached (AJP-4.10C §5.4).
        </Typography>

        {activeFacilities.map((f, fi) => {
          // BUG-FACILITY-NAME-001 FIX: `.replace('_', ' ')` only replaces first underscore
          // (ROLE_2_BASIC → ROLE 2_BASIC). Use label map for human-readable names.
          const FACILITY_TYPE_LABELS = {
            ROLE1: 'Role 1', ROLE_1: 'Role 1',
            ROLE2_BASIC: 'Role 2 Basic', ROLE_2_BASIC: 'Role 2 Basic',
            ROLE2_ENHANCED: 'Role 2 Enhanced', ROLE_2_ENHANCED: 'Role 2 Enhanced',
            ROLE3: 'Role 3', ROLE_3: 'Role 3',
            ROLE4: 'Role 4', ROLE_4: 'Role 4',
          };
          const facilityLabel = f.facility_type
            ? `${FACILITY_TYPE_LABELS[f.facility_type] ?? f.facility_type.replaceAll('_', ' ')} — ${f.facility_id}`
            : f.facility_id;
          const attached = Array.isArray(f.attached_assets) ? f.attached_assets : [];
          const detached = Array.isArray(f.detached_assets) ? f.detached_assets : [];

          return (
            <Box key={fi} sx={{ mb: fi < activeFacilities.length - 1 ? 3 : 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {facilityLabel}
              </Typography>

              {attached.length > 0 && (
                <>
                  <Typography variant="caption" color="success.dark" sx={{ fontWeight: 600 }}>
                    Attached assets ({attached.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell>Model</TableCell>
                          <TableCell align="center">Qty</TableCell>
                          <TableCell align="center">Day range</TableCell>
                          <TableCell>Source unit</TableCell>
                          <TableCell>MOU ref</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {attached.map((a, ai) => (
                          <TableRow key={ai}>
                            <TableCell>{a.asset_type}</TableCell>
                            <TableCell>{a.model}</TableCell>
                            <TableCell align="center">+{a.quantity}</TableCell>
                            <TableCell align="center">
                              D{a.attach_day} - {a.detach_day != null ? `D${a.detach_day}` : 'end'}
                            </TableCell>
                            <TableCell>{humanizeUnitId(a.source_unit)}</TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {a.mou_reference || '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {detached.length > 0 && (
                <>
                  <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 600 }}>
                    Detached assets ({detached.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell align="center">Qty</TableCell>
                          <TableCell align="center">Day range</TableCell>
                          <TableCell>Destination unit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detached.map((d, di) => (
                          <TableRow key={di}>
                            <TableCell>{d.asset_type}</TableCell>
                            <TableCell align="center">-{d.quantity}</TableCell>
                            <TableCell align="center">
                              D{d.detach_day} - {d.return_day != null ? `D${d.return_day}` : 'permanent'}
                            </TableCell>
                            <TableCell>{d.destination_unit || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          );
        })}
      </Paper>
    );
  };

  // (ENH-030: renderReportsSection moved to Tab 6)

  // ============================================================================
  // T5-011: RENDER: DOW Explanation Dialog
  // ============================================================================

  const renderDOWDialog = () => (
    <Dialog 
      open={dowDialogOpen} 
      onClose={() => setDowDialogOpen(false)} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HelpOutlineIcon color="primary" />
        {t('tab5.dow_dialog.title')}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" paragraph>
          <strong>Definition:</strong> {DOW_EXPLANATION.definition}
        </Typography>
        
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
          {t('tab5.dow_dialog.factors_title')}
        </Typography>
        
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Factor</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Impact on DOW</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DOW_EXPLANATION.factors.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={{ fontWeight: 500 }}>{item.factor}</TableCell>
                  <TableCell>{item.impact}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
          {t('tab5.dow_dialog.formula_title')}
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', fontFamily: 'monospace' }}>
          {DOW_EXPLANATION.formula}
        </Paper>
        
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
          {t('tab5.dow_dialog.references_title')}
        </Typography>
        <Box component="ul" sx={{ pl: 2, mt: 1 }}>
          {DOW_EXPLANATION.references.map((ref, idx) => (
            <li key={idx}>
              <Typography variant="body2">{ref}</Typography>
            </li>
          ))}
        </Box>
        
        {/* Current scenario DOW breakdown if available */}
        {projections && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
              {t('tab5.dow_dialog.scenario_analysis')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color="error.main">
                      {formatNumber(projections.by_corps?.total_dow || 0)}
                    </Typography>
                    <Typography variant="caption">Total DOW</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h5">
                      {projections.by_corps?.total_wia > 0 
                        ? formatPercent(((projections.by_corps?.total_dow || 0) / projections.by_corps.total_wia) * 100)
                        : '0%'}
                    </Typography>
                    <Typography variant="caption">DOW Rate</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color={projections.medical_system_analysis?.overall_golden_hour_percent >= 80 ? 'success.main' : 'warning.main'}>
                      {projections.medical_system_analysis?.overall_golden_hour_percent || 'N/A'}%
                    </Typography>
                    <Typography variant="caption">Golden Hour</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    {/* ARCH-010 / G-07e: CASEVAC is informational only per AJMedP-2.
                        Color was warning.main if > 0  -  implied a DOW impact that no longer
                        applies after calculateOverflowDOW() fix in v5.6.0. Now info.main.
                        G-07b / C-012: MEDEVAC coverage shows four-tier adequacy via
                        getAssetStatusColor()  -  no hardcoded thresholds per APP_RULES 2.1. */}
                    <Typography variant="h5" color="info.main">
                      {logisticsImpact?.casevacPercent || 0}%
                    </Typography>
                    <Typography variant="caption">CASEVAC Used</Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.6rem', mt: 0.5 }}>
                      (Informational  -  AJMedP-2)
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color={`${getAssetStatusColor(100 - (logisticsImpact?.casevacPercent || 0))}.main`}
                      sx={{ fontSize: '0.65rem', mt: 0.5, fontWeight: 600 }}
                    >
                      MEDEVAC: {100 - (logisticsImpact?.casevacPercent || 0)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDowDialogOpen(false)}>{t('tab5.dow_dialog.close')}</Button>
      </DialogActions>
    </Dialog>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssessmentIcon fontSize="large" color="primary" />
        {t('tab5.title')}
      </Typography>
      {scenario?.operation_parameters?.operation_start_date && Number(scenario?.operation_parameters?.duration_days) >= 1 && (
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          {t('tab7.operation_period')}: {operationPeriodLabel(
            scenario.operation_parameters.operation_start_date,
            scenario.operation_parameters.operation_start_time_z || DEFAULT_START_TIME_Z,
            Number(scenario.operation_parameters.duration_days)
          )}
        </Typography>
      )}

      {/* v2.6.0 FIX: Dynamic version in description */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Run the casualty calculator{calculatorVersion !== 'N/A' ? ` (v${calculatorVersion})` : ''} and generate the Medical Estimate (AJMedP-1 Steps 4-6). Formal NATO reports (MEDSITREP, Annex Q, CASREP, etc.) are generated in Tab 6 after variant analysis.
      </Typography>
      
      {/* ENH-WORKFLOW-003: Direct navigation notice — shown when user jumped here via toolbar
          rather than completing Tab 4 via Save & Continue. */}
      {(() => {
        const navMethod = (() => {
          try { return sessionStorage.getItem('psp_tab5_nav_method'); } catch (_e) { return null; }
        })();
        if (navMethod !== 'direct') return null;
        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            You navigated directly to Tab 5. Unsaved changes from Tabs 2–4 were auto-flushed but
            not sequentially validated. For a fully validated data flow, use{' '}
            <strong>Save &amp; Continue</strong> from each preceding tab.
          </Alert>
        );
      })()}

      {/* V4-014: CoA B reminder banner — shown when CoA A is saved but CoA B is not yet */}
      {(() => {
        try {
          const coaA = lsRead('psp_cre_coa_1');
          const coaB = lsRead('psp_cre_coa_2');
          if (!coaA || coaB) return null;
          return (
            <Alert
              severity="warning"
              sx={{ mb: 2, border: '1px solid', borderColor: 'warning.main' }}
              icon={false}
            >
              <strong>CoA B Mode</strong> — CoA A is saved. Recalculate with the new operational parameters, then use{' '}
              <strong>&ldquo;Save as CoA B&rdquo;</strong> in the CoA section below to store this result.
              Tab 6 will then show the CoA A vs CoA B comparison.
            </Alert>
          );
        } catch (_e) { return null; }
      })()}

      {renderPrerequisitesCheck()}
      {renderCalculateSection()}

      {/* BUG-84-014: Persistent CoA context chip above results so user always knows
          which CoA the current panels represent. Only shown once a CoA has been saved. */}
      {(coaSlots[1] || coaSlots[2]) && projections && (() => {
        const hasA = !!coaSlots[1];
        const hasB = !!coaSlots[2];
        let label, color;
        if (hasA && !hasB) {
          label = 'Unsaved calculation — save as CoA B to enable comparison';
          color = 'warning';
        } else if (hasA && hasB) {
          label = 'CoA A & B saved — displaying current (recalculate to update)';
          color = 'success';
        } else {
          label = 'Displaying: CoA A (baseline)';
          color = 'primary';
        }
        return (
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip size="small" label={label} color={color} variant="outlined"
              sx={{ fontWeight: 600 }} />
          </Box>
        );
      })()}

      {renderResultsDashboard()}
      
      {/* New sections after results */}
       {projections && (
        <>
          {renderMedicalEstimateSteps()}
          {renderSustainabilityIndicators()}
          {/* Phase 5.2: Tactical evacuation capacity vs demand per-day table */}
          {renderLogisticsImpact()}
          {/* V4-TAB6-004: Pre-combat build-up DNBI baseline (before MASCAL timeline) */}
          {renderBuildupPhasePanel()}
          {/* ENH-047: Coalition CASEVAC overflow routing (Category 2) */}
          {renderCoalitionOverflow()}
          {/* MASCAL-WHEN-WHERE (v3.5.0): Per-brigade R2B bed occupancy day-by-day timeline */}
          {renderMASCALTimeline()}
          {/* Phase 6.2: STRATEVAC capacity by airfield class + FARP */}
          {/* ENH-003 Phase 4: STRATEVAC schedule analysis  -  CSU/PHA queue per airfield */}
          {/* E-02: Logistics DOS Timeline Graph  -  day-by-day supply depletion from Tab4 projection */}
          {renderLogisticsTimeline()}
          {/* PHASE 4.5.5: Injury Mechanism Analysis */}
          {renderInjuryMechanismAnalysis()}
          {renderResupplyAnalysis()}
          {renderG1Summary()}
          {renderRTDTimeline()}
          {renderHNSPanel()}
          {/* ENH-019: MROE / EPW & Civilian Casualty planning panel */}
          {renderMROEPanel()}
          {/* ENH-002: Attach/Detach evacuation asset timeline */}
          {renderAttachmentTimeline()}
          
          {/* HQ & Support Unit Casualties */}
          <NonCombatPersonnelSection 
            hqCasualties={projections.hq_casualties}
            supportCasualties={projections.support_casualties}
            duration={getScenarioMetadata(projections, scenario).duration}
            nationalStandard={getScenarioMetadata(projections, scenario).nationalStandard}
            echelon={getScenarioMetadata(projections, scenario).echelon}
          />

          {/* R1-08: losses of the medical personnel and what is left of the prolonged field care benefit */}
          <MedicalPersonnelSection brigades={projections.by_brigade} />
        </>
      )}
      
      {/* T5-011: DOW Explanation Dialog */}
      {renderDOWDialog()}

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => onNavigate && onNavigate(3)}
        >
          {t('tab5.nav.back_tab4')}
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={() => {
            if (onComplete) onComplete();
            if (onNavigate) onNavigate(5);
          }}
        >
          {t('tab5.nav.proceed_tab6')}
        </Button>
      </Box>

      {/* ENH-COA-GUARD-001: CoA overwrite confirmation dialog */}
      <Dialog open={!!coaOverwriteDialog} onClose={() => setCoaOverwriteDialog(null)}>
        <DialogTitle>Overwrite {coaOverwriteDialog?.label}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {coaOverwriteDialog?.label} already has a saved snapshot
            {coaSlots[coaOverwriteDialog?.slot]?.saved_at
              ? ` (saved ${new Date(coaSlots[coaOverwriteDialog?.slot].saved_at).toLocaleTimeString()})`
              : ''}.
            Overwriting it will permanently replace the previous snapshot.
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCoaOverwriteDialog(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => { doSaveAsCoa(coaOverwriteDialog.slot); setCoaOverwriteDialog(null); }}
          >
            Overwrite {coaOverwriteDialog?.label}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default Tab5_Reports;
